import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, User, createUserWithEmailAndPassword, signInWithEmailAndPassword, updatePassword, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, Firestore, doc, collection, collectionGroup, addDoc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, deleteField, query, where, orderBy, onSnapshot, runTransaction, Query, DocumentData } from 'firebase/firestore';
import { getFunctions, httpsCallable, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, ref as storageRef, uploadBytes, deleteObject, listAll, getMetadata } from 'firebase/storage';
import { buildDeliveryReportPdfBlob, deliveryReportFilename } from '../../services/deliveryReport';
import {
  AUDIOPATH,
  AUDIOMETAPATH,
  USERSPATH,
  GENRESPATH,
  ORGANIZATIONSPATH,
  CLIENTPATH,
  AGENCYPATH,
  BROADCASTERPATH,
  MEDIAOWNERPATH,
  GLOBAL_ARTISTPATH,
  MASTER_CATALOGUEPATH,
  PUBLIC_CATALOGUEPATH,
  getOrgCollectionPath,
  getOrgStoragePath,
} from '../../constants/paths';

// Type definitions
type OrgRole = 'ADMIN' | 'OPERATOR' | 'USER';

interface InviteData {
  id: string;
  code: string;
  orgId: string;
  orgName: string;
  role: OrgRole;
  createdBy: string;
  createdAt: string;
  expiresAt: string | null;
  used: boolean;
  usedBy: string | null;
  usedAt: string | null;
  invitedEmail: string;
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};


// Initialize Firebase
let app: FirebaseApp;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

class Firebase {
  auth: Auth;
  db: Firestore;
  storage: ReturnType<typeof getStorage>;
  functions: Functions;
  storageRef: ReturnType<typeof storageRef>;
  audioRef: ReturnType<typeof storageRef>;
  cache: any = {};

  constructor() {
    this.auth = getAuth(app);
    this.db = getFirestore(app);
    this.storage = getStorage(app);
    this.functions = getFunctions(app, 'europe-west3');
    this.storageRef = storageRef(this.storage);
    this.audioRef = storageRef(this.storage, AUDIOPATH);

    // Dev-only: route Cloud Function calls to the local emulator when flagged.
    // Leaves Firestore/Storage/Auth pointed at prod so existing data is reachable.
    if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true') {
      const host = process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || '127.0.0.1';
      const port = Number(process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || 5001);
      console.log(`[Firebase] Using functions emulator at ${host}:${port}`);
      connectFunctionsEmulator(this.functions, host, port);
    }
  }

  // *** Auth API *** //
  doCreateUserWithEmailAndPassword = (email: string, password: string) =>
    createUserWithEmailAndPassword(this.auth, email, password);

  doSignInWithEmailAndPassword = (email: string, password: string) =>
    signInWithEmailAndPassword(this.auth, email, password);

  doSignOut = () => this.auth.signOut();

  /**
   * Send a password reset email via the `sendPasswordResetLink` Cloud
   * Function (routed through Resend on our verified domain), not
   * Firebase Auth's built-in sender. The built-in sender ships from
   * noreply@<project>.firebaseapp.com which lots of mail providers
   * demote or drop for unsigned domains — the client would see
   * "Email sent!" but nothing arrives.
   *
   * The Admin SDK still generates the reset link, so the URL the
   * recipient clicks is a genuine Firebase Auth action link and the
   * flow after the click is identical to before.
   *
   * Silently succeeds when the email is unknown (anti-enumeration —
   * matches Firebase's own default behaviour).
   */
  doPasswordReset = (email: string) => {
    const callable = httpsCallable(this.functions, 'sendPasswordResetLink');
    return callable({ email });
  };

  doPasswordUpdate = (password: string) =>
    this.auth.currentUser ? updatePassword(this.auth.currentUser, password) : Promise.reject('No current user');

  onAuthUserListener = (next: any, fallback: any) =>
    onAuthStateChanged(this.auth, async (authUser: User | null) => {
      if (authUser) {
        try {
          // Force refresh the ID token to ensure Firestore has valid auth
          await authUser.getIdToken(true);

          const userDoc = doc(this.db, USERSPATH, authUser.uid);
          let docSnap = await getDoc(userDoc);
          let dbUser;
          if (!docSnap.exists()) {
            dbUser = {
              roles: {
                USER: 'USER',
              },
              email: authUser.email,
              createdAt: new Date().toISOString(),
            };
            await setDoc(userDoc, dbUser);
          } else {
            dbUser = docSnap.data();
            if (!dbUser.roles) {
              dbUser.roles = { USER: 'USER' };
            } else if (!dbUser.roles.USER) {
              dbUser.roles.USER = 'USER';
            }
          }
          const mergedUser = {
            uid: authUser.uid,
            email: authUser.email,
            ...dbUser,
          };
          next(mergedUser);
        } catch (error) {
          console.error('Error getting user document:', error);
          fallback();
        }
      } else {
        fallback();
      }
    });

  // *** User API *** //
  user = (uid: string) => doc(this.db, USERSPATH, uid);

  getUserDetails = async (uid: string) => {
    const userDoc = await getDoc(doc(this.db, USERSPATH, uid));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  };

  deleteUser = (uid: string) => {
    const callable = httpsCallable(this.functions, 'deleteUser');
    return callable({ uid })
      .then((result: any) => {
        return result;
      })
      .catch((error: any) => {
        console.error('Function execution failed:', error);
        throw error;
      });
  };

  // *** Storage API *** //
  doUpload = (fileData: any) =>
    uploadBytes(storageRef(this.storage, `${AUDIOPATH}/${fileData.name}`), fileData.file, fileData.metadata);

  deleteFile = (fileName: any) => deleteObject(storageRef(this.storage, `${AUDIOPATH}/${fileName}`));

  // *** Audio Meta Data *** //
  saveAudioMetaData = (metaData: any) =>
    setDoc(doc(this.db, AUDIOMETAPATH, metaData.audioMetaRef), metaData);

  deleteAudioMetaData = (docId: any) =>
    deleteDoc(doc(this.db, AUDIOMETAPATH, docId));

  getAudioMetaData = (docId: string) =>
    getDoc(doc(this.db, AUDIOMETAPATH, docId));

  getAudioMetaRef = () => collection(this.db, AUDIOMETAPATH);

  audioMetaList = (config: any): Query<DocumentData> => {
    let col = collection(this.db, AUDIOMETAPATH);
    const { searchFilter, sortBy } = config;
    const multiSearch = searchFilter?.multi;
    const multiActive = multiSearch?.active;
    const dateSearch = searchFilter?.date;
    const dateActive = dateSearch?.active;
    const subSearch = searchFilter?.subFilters;
    const subSearchActive = subSearch?.active;

    const useMulti =
      searchFilter &&
      multiSearch?.data &&
      Object.keys(multiSearch?.data).length > 0 &&
      multiActive;
    const useDate =
      searchFilter && Object.keys(searchFilter?.date).length > 0 && dateActive;
    const useSubSearch =
      searchFilter &&
      searchFilter?.subFilters?.filters?.length > 0 &&
      subSearchActive;
    const canFilter = useMulti || useDate || useSubSearch;

    let sortByCol = sortBy.column;

    let q: Query<DocumentData> = col;
    if (canFilter) {
      if (useMulti) {
        const useValue = !!multiSearch.useValue;
        q = query(
          q,
          where(
            useValue ? multiSearch?.useValue : multiSearch?.value,
            'array-contains-any',
            [...searchFilter.multi.data]
          )
        );
      }
      if (useDate) {
        const { from, to, type } = dateSearch;
        const oneDay = 24 * 60 * 60 * 1000;
        const useType = type === 'completed' ? 'dateCompleted' : 'dateExpires';
        sortByCol = useType;
        q = query(q, where(useType, '>=', from - oneDay), where(useType, '<=', to));
      }
      if (useSubSearch) {
        searchFilter.subFilters.filters.forEach((subFilter: any) => {
          if (subFilter.key === 'bpm') {
            const [minStr, maxStr] = subFilter.value.value.split('-');
            const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
            const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;
            
            if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
              // Complete range (min-max)
              q = query(q, where('bpm', '>=', min), where('bpm', '<=', max));
            } else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
              // Minimum-only range (min-)
              q = query(q, where('bpm', '>=', min));
            } else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
              // Maximum-only range (-max)
              q = query(q, where('bpm', '<=', max));
            }
          } else if (subFilter.key === 'tags') {
            q = query(q, where('tags', 'array-contains-any', subFilter.value));
          } else {
            q = query(q, where(subFilter.key + '.label', '==', subFilter.value.label));
          }
        });
      }
    }
    q = query(q, orderBy(sortByCol, sortBy.ascending ? 'asc' : 'desc'));
    return q;
  };

  // *** Chatbot Collections *** //
  getChatbotConfig = () => getDoc(doc(this.db, 'chatbotConfig', 'gemini'));

  updateChatbotConfig = (config: any) =>
    updateDoc(doc(this.db, 'chatbotConfig', 'gemini'), config);

  getChatbotMappings = () => getDoc(doc(this.db, 'chatbotMappings', 'default'));

  updateChatbotMappings = (mappings: any) =>
    updateDoc(doc(this.db, 'chatbotMappings', 'default'), mappings);

  // *** Genres *** //
  saveGenre = (genre: any, docId: any) =>
    setDoc(doc(this.db, GENRESPATH, docId), genre);
  getGenreRef = () => doc(this.db, GENRESPATH);

  genres = () => collection(this.db, GENRESPATH);

  // *** Generic *** //
  save = (data: any, docId: any, path: any) =>
    setDoc(doc(this.db, path, docId), data);

  getRef = (path: any) => doc(this.db, path);

  collection = (path: any) => {
    return collection(this.db, path);
  };

  // Helper for modular query usage
  collectionQuery = (path: string, ...queryConstraints: any[]) => {
    return query(collection(this.db, path), ...queryConstraints);
  };

  callFunction = (name: string, data?: any) => {
    const fn = httpsCallable(this.functions, name);
    return fn(data);
  };

  // ==========================================================================
  // *** ORGANIZATION API ***
  // ==========================================================================

  // Get organization document
  getOrganization = (orgId: string) =>
    getDoc(doc(this.db, ORGANIZATIONSPATH, orgId));

  // Get all organizations (for super admin)
  getAllOrganizations = () =>
    getDocs(collection(this.db, ORGANIZATIONSPATH));

  // Create a new organization
  createOrganization = async (orgData: {
    name: string;
    type: 'artist' | 'client';
    category?: 'artist' | 'publisher' | 'agency' | 'brand' | 'production';
    createdBy: string;
    settings?: any;
  }) => {
    const orgRef = doc(collection(this.db, ORGANIZATIONSPATH));
    const now = new Date().toISOString();
    const organization: any = {
      id: orgRef.id,
      name: orgData.name,
      type: orgData.type,
      createdBy: orgData.createdBy,
      createdAt: now,
      settings: orgData.settings || {
        allowUserInvites: false,
        defaultUserRole: 'USER',
      },
    };

    // Add category if provided
    if (orgData.category) {
      organization.category = orgData.category;
    }

    await setDoc(orgRef, organization);

    // Add the creator to the members subcollection as ADMIN
    await setDoc(doc(this.db, ORGANIZATIONSPATH, orgRef.id, 'members', orgData.createdBy), {
      joinedAt: now,
      role: 'ADMIN',
      invitedBy: null,
      inviteCode: null,
    });

    return organization;
  };

  // Update organization settings
  updateOrganization = (orgId: string, updates: any) =>
    updateDoc(doc(this.db, ORGANIZATIONSPATH, orgId), updates);

  // Update organization name (also updates denormalized copies in ALL member user documents)
  updateOrganizationName = async (orgId: string, newName: string) => {
    try {
      // 1. Update the organization document
      await updateDoc(doc(this.db, ORGANIZATIONSPATH, orgId), { name: newName });

      // 2. Get all members of this organization
      const membersRef = collection(this.db, `${ORGANIZATIONSPATH}/${orgId}/members`);
      const membersSnapshot = await getDocs(membersRef);

      // 3. Update each member's denormalized organization name in their user document
      const updatePromises = membersSnapshot.docs.map(async (memberDoc) => {
        try {
          const memberId = memberDoc.id;
          const userRef = doc(this.db, USERSPATH, memberId);
          const userDocSnap = await getDoc(userRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            const organizations = userData.organizations || [];
            const updatedOrgs = organizations.map((org: any) =>
              org.orgId === orgId ? { ...org, orgName: newName } : org
            );
            await updateDoc(userRef, { organizations: updatedOrgs });
          }
        } catch (memberError) {
          console.error(`Failed to update member ${memberDoc.id}:`, memberError);
        }
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('updateOrganizationName error:', error);
      throw error;
    }
  };

  // Delete organization (super admin only)
  deleteOrganization = (orgId: string) =>
    deleteDoc(doc(this.db, ORGANIZATIONSPATH, orgId));

  // Get user's organizations from their memberships
  getUserOrganizations = async (userId: string) => {
    const userDoc = await getDoc(doc(this.db, USERSPATH, userId));
    if (!userDoc.exists()) return [];
    const userData = userDoc.data();
    return userData.organizations || [];
  };

  // Add user to organization
  addUserToOrganization = async (
    userId: string,
    orgId: string,
    orgName: string,
    role: 'ADMIN' | 'OPERATOR' | 'USER',
    orgType?: 'artist' | 'client',
    orgCategory?: 'artist' | 'publisher' | 'agency' | 'brand' | 'production'
  ) => {
    const userRef = doc(this.db, USERSPATH, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const organizations = userData.organizations || [];

    // Check if user already in org
    const existingIndex = organizations.findIndex((o: any) => o.orgId === orgId);
    if (existingIndex >= 0) {
      // Update role, type, and category
      organizations[existingIndex].role = role;
      if (orgType) {
        organizations[existingIndex].orgType = orgType;
      }
      if (orgCategory) {
        organizations[existingIndex].orgCategory = orgCategory;
      }
    } else {
      // Add new membership
      const membership: any = {
        orgId,
        orgName,
        orgType: orgType || 'artist',  // Default to 'artist' for backwards compatibility
        role,
        joinedAt: new Date().toISOString(),
      };
      if (orgCategory) {
        membership.orgCategory = orgCategory;
      }
      organizations.push(membership);
    }

    await updateDoc(userRef, { organizations });
    return organizations;
  };

  // Remove user from organization
  removeUserFromOrganization = async (userId: string, orgId: string) => {
    const userRef = doc(this.db, USERSPATH, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const organizations = (userData.organizations || []).filter(
      (o: any) => o.orgId !== orgId
    );

    await updateDoc(userRef, { organizations });
    return organizations;
  };

  // Update user's current organization selection
  setUserCurrentOrg = (userId: string, orgId: string) =>
    updateDoc(doc(this.db, USERSPATH, userId), { currentOrgId: orgId });

  // ==========================================================================
  // *** ORGANIZATION INVITES API ***
  // ==========================================================================

  // Generate a unique invite code
  generateInviteCode = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed ambiguous chars (0,O,1,I)
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Create an invite for an organization.
  // Pass `expiresInDays = 0` to create a never-expiring invite (expiresAt = null).
  createOrgInvite = async (
    orgId: string,
    orgName: string,
    createdBy: string,
    expiresInDays: number = 7,
    role: 'USER' | 'OPERATOR' | 'ADMIN' = 'USER',
    invitedEmail: string
  ) => {
    const code = this.generateInviteCode();
    const now = new Date();
    const expiresAt = expiresInDays > 0
      ? new Date(now.getTime() + expiresInDays * 24 * 60 * 60 * 1000).toISOString()
      : null;

    const inviteData = {
      code,
      orgId,
      orgName,
      createdBy,
      createdAt: now.toISOString(),
      expiresAt,
      used: false,
      usedBy: null,
      usedAt: null,
      role,
      invitedEmail: invitedEmail.toLowerCase(),
    };

    // Store in global invites collection (for lookup by code)
    await setDoc(doc(this.db, 'invites', code), inviteData);

    // Also store in org's invites subcollection (for org admin to manage)
    await setDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'invites', code), inviteData);

    return inviteData;
  };

  // Get invite by code (for validation)
  getInviteByCode = async (code: string): Promise<InviteData | null> => {
    const inviteDoc = await getDoc(doc(this.db, 'invites', code));
    if (!inviteDoc.exists()) {
      return null;
    }
    return { id: inviteDoc.id, ...inviteDoc.data() } as InviteData;
  };

  // Validate an invite code (optionally check email match)
  validateInvite = async (code: string, userEmail?: string) => {
    const invite = await this.getInviteByCode(code);

    if (!invite) {
      return { valid: false, error: 'Invite not found' };
    }

    if (invite.used) {
      return { valid: false, error: 'Invite has already been used' };
    }

    // Null expiresAt means the invite never expires.
    if (invite.expiresAt) {
      const now = new Date();
      const expiresAt = new Date(invite.expiresAt);
      if (now > expiresAt) {
        return { valid: false, error: 'Invite has expired' };
      }
    }

    // If user email provided, check if it matches the invited email
    if (userEmail && invite.invitedEmail) {
      if (userEmail.toLowerCase() !== invite.invitedEmail.toLowerCase()) {
        return {
          valid: false,
          error: 'This invite was sent to a different email address',
          invitedEmail: invite.invitedEmail,
          invite
        };
      }
    }

    return { valid: true, invite };
  };

  // Use an invite (mark as used and add user to org)
  useInvite = async (code: string, userId: string, userEmail: string) => {
    // Validate with email check
    const validation = await this.validateInvite(code, userEmail);

    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const invite = validation.invite!;
    const now = new Date().toISOString();

    // Mark invite as used in both locations
    const updateData = {
      used: true,
      usedBy: userId,
      usedAt: now,
    };

    await updateDoc(doc(this.db, 'invites', code), updateData);
    await updateDoc(doc(this.db, ORGANIZATIONSPATH, invite.orgId, 'invites', code), updateData);

    // Fetch org type and category from the organization document
    const orgDoc = await getDoc(doc(this.db, ORGANIZATIONSPATH, invite.orgId));
    const orgData = orgDoc.exists() ? orgDoc.data() : null;
    const orgType = orgData?.type || 'artist';
    const orgCategory = orgData?.category || null;

    // Add user to the organization
    await this.addUserToOrganization(userId, invite.orgId, invite.orgName, invite.role, orgType, orgCategory);

    // Add user to org's members subcollection (for admin to view)
    await setDoc(doc(this.db, ORGANIZATIONSPATH, invite.orgId, 'members', userId), {
      joinedAt: now,
      role: invite.role,
      invitedBy: invite.createdBy,
      inviteCode: code,
    });

    return { ...invite, orgType, orgCategory };
  };

  // Get all invites for an organization (for admin)
  getOrgInvites = async (orgId: string) => {
    const invitesRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'invites');
    const snapshot = await getDocs(invitesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Delete/revoke an invite
  deleteInvite = async (orgId: string, code: string) => {
    await deleteDoc(doc(this.db, 'invites', code));
    await deleteDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'invites', code));
  };

  // Get all members of an organization (for admin)
  getOrgMembers = async (orgId: string) => {
    const membersRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'members');
    const snapshot = await getDocs(membersRef);

    // Fetch user details for each member
    const membersWithDetails = await Promise.all(
      snapshot.docs.map(async (memberDoc) => {
        const memberData = memberDoc.data();
        const userId = memberDoc.id;

        // Try to get user details from users collection
        let username = null;
        let email = null;
        try {
          const userDoc = await getDoc(doc(this.db, USERSPATH, userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            username = userData.username || null;
            email = userData.email || null;
          }
        } catch (err) {
          // User document might not exist or be inaccessible
          console.error(`Failed to fetch user details for ${userId}:`, err);
        }

        return {
          id: userId,
          username,
          email,
          ...memberData,
        };
      })
    );

    return membersWithDetails;
  };

  /**
   * Read notification preferences for a user in a given org. Defaults
   * (in-app on, email off) applied for any missing keys. Pattern mirrors
   * Music Vault's getMemberNotificationPreferences but extended to dual
   * channels (inApp + email) and 7 notification types.
   */
  getMemberNotificationPreferences = async (orgId: string, userId: string) => {
    const { mergeWithDefaults } = await import('../../services/notificationPreferences');
    const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
    const memberDoc = await getDoc(memberRef);
    if (!memberDoc.exists()) return mergeWithDefaults(null);
    return mergeWithDefaults(memberDoc.data()?.notifications);
  };

  /** Write notification preferences to a member doc, merging into other fields. */
  updateMemberNotificationPreferences = async (
    orgId: string,
    userId: string,
    notifications: { inApp: Record<string, boolean>; email: Record<string, boolean> },
  ) => {
    const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
    await setDoc(memberRef, { notifications }, { merge: true });
  };

  // Update a member's role in an organization
  updateMemberRole = async (orgId: string, userId: string, newRole: 'ADMIN' | 'OPERATOR' | 'USER') => {
    // Update in org's members subcollection
    await updateDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId), {
      role: newRole,
    });

    // Update in user's organizations array
    const userRef = doc(this.db, USERSPATH, userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const organizations = (userData.organizations || []).map((o: any) =>
        o.orgId === orgId ? { ...o, role: newRole } : o
      );
      await updateDoc(userRef, { organizations });
    }
  };

  // Remove a member from an organization
  removeMember = async (orgId: string, userId: string) => {
    // Remove from org's members subcollection
    await deleteDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId));

    // Remove from user's organizations array
    try {
      await this.removeUserFromOrganization(userId, orgId);
    } catch (err) {
      console.error('Failed to remove org from user document:', err);
      // Re-throw so the UI knows it failed
      throw err;
    }
  };

  // Soft delete an organization
  // - Marks org as deleted (doesn't remove data)
  // - Removes org from all members' organizations array
  // - Unlinks global entries (artists-global, client, agency) and stores their IDs for restore
  softDeleteOrganization = async (orgId: string, deletedBy: string) => {
    // Get all members first
    const membersRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'members');
    const membersSnapshot = await getDocs(membersRef);

    // Remove org from each member's organizations array
    const removePromises = membersSnapshot.docs.map(async (memberDoc) => {
      const userId = memberDoc.id;
      try {
        await this.removeUserFromOrganization(userId, orgId);
      } catch (err) {
        console.error(`Failed to remove org from user ${userId}:`, err);
        // Continue with other members even if one fails
      }
    });

    await Promise.all(removePromises);

    // Find and unlink global entries that reference this org
    const linkedEntries: { collection: string; docId: string }[] = [];
    const globalCollections = [GLOBAL_ARTISTPATH, CLIENTPATH, AGENCYPATH, BROADCASTERPATH, MEDIAOWNERPATH];

    for (const collectionPath of globalCollections) {
      try {
        const collectionRef = collection(this.db, collectionPath);
        const q = query(collectionRef, where('linkedOrgId', '==', orgId));
        const snapshot = await getDocs(q);

        for (const docSnap of snapshot.docs) {
          // Store the linked entry info for potential restore
          linkedEntries.push({ collection: collectionPath, docId: docSnap.id });

          // Remove the link from the global entry
          await updateDoc(doc(this.db, collectionPath, docSnap.id), {
            linkedOrgId: deleteField(),
            linkedAt: deleteField(),
          });
        }
      } catch (err) {
        console.error(`Failed to unlink global entries in ${collectionPath}:`, err);
        // Continue with other collections
      }
    }

    // Mark the organization as deleted (soft delete)
    // Store linked entries so they can be restored later
    const orgRef = doc(this.db, ORGANIZATIONSPATH, orgId);
    await updateDoc(orgRef, {
      deletedAt: new Date().toISOString(),
      deletedBy: deletedBy,
      isDeleted: true,
      _linkedEntries: linkedEntries.length > 0 ? linkedEntries : deleteField(),
    });

    return true;
  };

  // Restore a soft-deleted organization
  // Re-adds the org to all members' organizations arrays, removes deleted flags,
  // and re-links global entries that were unlinked during soft delete
  restoreOrganization = async (orgId: string) => {
    // Get the organization document
    const orgRef = doc(this.db, ORGANIZATIONSPATH, orgId);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }

    const orgData = orgDoc.data();
    const orgName = orgData.name;
    const orgType = orgData.type;
    const orgCategory = orgData.category;

    // Get all members from the members subcollection
    const membersRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'members');
    const membersSnapshot = await getDocs(membersRef);

    // Re-add org to each member's organizations array
    const restorePromises = membersSnapshot.docs.map(async (memberDoc) => {
      const userId = memberDoc.id;
      const memberData = memberDoc.data();
      const role = memberData.role || 'USER';

      try {
        await this.addUserToOrganization(userId, orgId, orgName, role, orgType, orgCategory);
      } catch (err) {
        console.error(`Failed to restore org for user ${userId}:`, err);
        // Continue with other members even if one fails
      }
    });

    await Promise.all(restorePromises);

    // Restore global entry links if they were stored during soft delete
    const linkedEntries = orgData._linkedEntries as { collection: string; docId: string }[] | undefined;
    if (linkedEntries && linkedEntries.length > 0) {
      for (const entry of linkedEntries) {
        try {
          await updateDoc(doc(this.db, entry.collection, entry.docId), {
            linkedOrgId: orgId,
            linkedAt: Date.now(),
          });
        } catch (err) {
          console.error(`Failed to restore link for ${entry.collection}/${entry.docId}:`, err);
          // Continue with other entries
        }
      }
    }

    // Remove the deleted flags and linked entries backup
    await updateDoc(orgRef, {
      deletedAt: deleteField(),
      deletedBy: deleteField(),
      isDeleted: deleteField(),
      _linkedEntries: deleteField(),
    });

    return true;
  };

  // Check for organizations pending restoration and restore them
  // Triggered on user login - checks if any org has isDeleted = false
  // (meaning admin changed it from true to false to trigger restore)
  // and the user is in that org's members subcollection
  checkAndRestorePendingOrgs = async (userId: string) => {
    try {
      // Query all orgs with isDeleted = false (restore trigger)
      const orgsRef = collection(this.db, ORGANIZATIONSPATH);
      const restoreQuery = query(orgsRef, where('isDeleted', '==', false));
      const snapshot = await getDocs(restoreQuery);

      for (const orgDoc of snapshot.docs) {
        const orgId = orgDoc.id;

        // Check if this user is in the members subcollection
        const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
        const memberDoc = await getDoc(memberRef);

        if (memberDoc.exists()) {
          // User is a member - restore the org
          await this.restoreOrganization(orgId);
        }
      }
    } catch (err) {
      console.error('Error checking for pending org restorations:', err);
      // Don't throw - this shouldn't block login
    }
  };

  // ==========================================================================
  // *** ORGANIZATION-SCOPED AUDIO META API ***
  // ==========================================================================

  // Get org-scoped audio meta reference
  getOrgAudioMetaRef = (orgId: string) =>
    collection(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH));

  // Save audio meta data to organization
  saveOrgAudioMetaData = (orgId: string, metaData: any) =>
    setDoc(
      doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), metaData.audioMetaRef),
      metaData
    );

  // Get audio meta data from organization
  getOrgAudioMetaData = (orgId: string, docId: string) =>
    getDoc(doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), docId));

  // Delete audio meta data from organization
  deleteOrgAudioMetaData = (orgId: string, docId: string) =>
    deleteDoc(doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), docId));

  // Org-scoped audio meta list query
  orgAudioMetaList = (orgId: string, config: any): Query<DocumentData> => {
    const orgPath = getOrgCollectionPath(orgId, AUDIOMETAPATH);
    let col = collection(this.db, orgPath);
    const { searchFilter, sortBy } = config;
    const multiSearch = searchFilter?.multi;
    const multiActive = multiSearch?.active;
    const dateSearch = searchFilter?.date;
    const dateActive = dateSearch?.active;
    const subSearch = searchFilter?.subFilters;
    const subSearchActive = subSearch?.active;

    const useMulti =
      searchFilter &&
      multiSearch?.data &&
      Object.keys(multiSearch?.data).length > 0 &&
      multiActive;
    const useDate =
      searchFilter && Object.keys(searchFilter?.date).length > 0 && dateActive;
    const useSubSearch =
      searchFilter &&
      searchFilter?.subFilters?.filters?.length > 0 &&
      subSearchActive;
    const canFilter = useMulti || useDate || useSubSearch;

    let sortByCol = sortBy.column;

    let q: Query<DocumentData> = col;
    if (canFilter) {
      if (useMulti) {
        const useValue = !!multiSearch.useValue;
        q = query(
          q,
          where(
            useValue ? multiSearch?.useValue : multiSearch?.value,
            'array-contains-any',
            [...searchFilter.multi.data]
          )
        );
      }
      if (useDate) {
        const { from, to, type } = dateSearch;
        const oneDay = 24 * 60 * 60 * 1000;
        const useType = type === 'completed' ? 'dateCompleted' : 'dateExpires';
        sortByCol = useType;
        q = query(q, where(useType, '>=', from - oneDay), where(useType, '<=', to));
      }
      if (useSubSearch) {
        searchFilter.subFilters.filters.forEach((subFilter: any) => {
          if (subFilter.key === 'bpm') {
            const [minStr, maxStr] = subFilter.value.value.split('-');
            const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
            const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;

            if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
              q = query(q, where('bpm', '>=', min), where('bpm', '<=', max));
            } else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
              q = query(q, where('bpm', '>=', min));
            } else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
              q = query(q, where('bpm', '<=', max));
            }
          } else if (subFilter.key === 'tags') {
            q = query(q, where('tags', 'array-contains-any', subFilter.value));
          } else {
            q = query(q, where(subFilter.key + '.label', '==', subFilter.value.label));
          }
        });
      }
    }
    q = query(q, orderBy(sortByCol, sortBy.ascending ? 'asc' : 'desc'));
    return q;
  };

  // ==========================================================================
  // *** ORGANIZATION-SCOPED STORAGE API ***
  // ==========================================================================

  // Get org-scoped audio storage reference
  getOrgAudioRef = (orgId: string) =>
    storageRef(this.storage, `${AUDIOPATH}${orgId}`);

  // Upload file to org-scoped storage
  doOrgUpload = (orgId: string, trackId: string, fileData: any) => {
    const path = getOrgStoragePath(orgId, trackId);
    return uploadBytes(
      storageRef(this.storage, `${path}${fileData.name}`),
      fileData.file,
      fileData.metadata
    );
  };

  // Delete file from org-scoped storage
  deleteOrgFile = (orgId: string, trackId: string, fileName: string) => {
    const path = getOrgStoragePath(orgId, trackId);
    return deleteObject(storageRef(this.storage, `${path}${fileName}`));
  };

  // List files in org-scoped track folder
  listOrgTrackFiles = (orgId: string, trackId: string) => {
    const path = getOrgStoragePath(orgId, trackId);
    return listAll(storageRef(this.storage, path));
  };

  // Byte size of a single org-scoped file, read from Storage object
  // metadata. Used to backfill `size` onto legacy audiometa file entries
  // (uploaded before size was persisted) at save time. Returns null when
  // the object is missing or unreadable so callers can skip it silently.
  getOrgFileSize = async (orgId: string, trackId: string, fileName: string): Promise<number | null> => {
    try {
      const path = getOrgStoragePath(orgId, trackId);
      const meta = await getMetadata(storageRef(this.storage, `${path}${fileName}`));
      return typeof meta.size === 'number' ? meta.size : null;
    } catch {
      return null;
    }
  };

  // ==========================================================================
  // *** ORGANIZATION-SCOPED GENERIC COLLECTION API ***
  // ==========================================================================

  // Get org-scoped collection reference
  getOrgCollection = (orgId: string, collectionName: string) =>
    collection(this.db, getOrgCollectionPath(orgId, collectionName));

  // Save to org-scoped collection
  saveToOrgCollection = (orgId: string, collectionName: string, docId: string, data: any) =>
    setDoc(doc(this.db, getOrgCollectionPath(orgId, collectionName), docId), data);

  // Get from org-scoped collection
  getFromOrgCollection = (orgId: string, collectionName: string, docId: string) =>
    getDoc(doc(this.db, getOrgCollectionPath(orgId, collectionName), docId));

  // Delete from org-scoped collection
  deleteFromOrgCollection = (orgId: string, collectionName: string, docId: string) =>
    deleteDoc(doc(this.db, getOrgCollectionPath(orgId, collectionName), docId));

  // Query org-scoped collection
  queryOrgCollection = (orgId: string, collectionName: string, ...queryConstraints: any[]) =>
    query(collection(this.db, getOrgCollectionPath(orgId, collectionName)), ...queryConstraints);

  // ==========================================================================
  // *** LICENSE SUBCOLLECTION API ***
  // ==========================================================================

  // Get licenses collection reference for a track
  getLicensesCollection = (orgId: string, trackId: string) =>
    collection(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses');

  // Create a new license document
  createLicense = async (
    orgId: string,
    trackId: string,
    licenseData: any
  ): Promise<string> => {
    const licensesRef = this.getLicensesCollection(orgId, trackId);
    const newLicenseRef = doc(licensesRef);

    const fullLicenseData = {
      ...licenseData,
      id: newLicenseRef.id,
      sourceOrgId: orgId,
      createdAt: Date.now(),
    };

    await setDoc(newLicenseRef, fullLicenseData);

    // Note: For direct shares to existing orgs, we no longer create audiometa immediately.
    // The license remains 'pending' and audiometa is created when the client claims it.
    // This gives clients explicit control over what enters their library.

    // If this is an exclusive license, unpublish the track from master catalogue
    if (licenseData.isExclusive) {
      const masterDoc = await this.getMasterCatalogueTrack(trackId);
      if (masterDoc) {
        await this.unpublishFromMasterCatalogue(orgId, trackId);
        console.log('Track unpublished from master catalogue due to exclusive license');
      }
    }

    return newLicenseRef.id;
  };

  /**
   * Create an audiometa document in the client org from license data
   * Used for both direct sharing and claim flow
   */
  private createClientAudiometaFromLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string,
    clientOrgId: string,
    licenseData: any,
    additionalFields?: Record<string, any>
  ): Promise<void> => {
    const clientTrackRef = doc(
      this.db,
      getOrgCollectionPath(clientOrgId, AUDIOMETAPATH),
      trackId
    );

    const licensePath = `${getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH)}/${trackId}/licenses/${licenseId}`;

    const clientTrackData = {
      // Track metadata from license snapshot
      trackName: licenseData.trackName || 'Unknown Track',
      key: licenseData.key || null,
      bpm: licenseData.bpm || null,
      genre: licenseData.genre || null,
      tags: licenseData.tags || [],
      composer: licenseData.composer || [],
      artist: licenseData.artist || null,
      album: licenseData.album || null,
      recordLabel: licenseData.recordLabel || null,
      publisher: licenseData.publisher || null,
      sessionArtists: licenseData.sessionArtists || [],
      trackColor: licenseData.trackColor || null,

      // Audio files for playback
      mp3Files: licenseData.mp3Files || [],
      wavFiles: licenseData.wavFiles || [],
      defaultMp3Id: licenseData.defaultMp3Id || null,

      // License-specific metadata
      jobTitle: licenseData.jobTitle || null,
      client: licenseData.client || null,
      agency: licenseData.agency || null,
      producer: licenseData.producer || null,
      creativedirector: licenseData.creativeDirectors || [],
      flightingPlatform: licenseData.flightingPlatform || [],
      territories: licenseData.territories || [],
      dateExpires: licenseData.dateExpires || null,
      dateStart: licenseData.dateStart || null,

      // License reference fields
      isLicensedTrack: true,
      sourceOrgId: sourceOrgId,
      sourceTrackId: trackId,
      licenseId: licenseId,
      licensePath: licensePath,
      sourceOrgName: licenseData.sourceOrgName || 'Unknown Organization',

      // Timestamps
      createdAt: Date.now(),

      // Additional fields (e.g., claimedAt, claimedBy for claim flow)
      ...additionalFields,
    };

    await setDoc(clientTrackRef, clientTrackData);
  };

  // Get a specific license document
  getLicense = async (orgId: string, trackId: string, licenseId: string) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    const docSnap = await getDoc(licenseRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  };

  // Get all licenses for a track
  getLicensesForTrack = async (orgId: string, trackId: string) => {
    const licensesRef = this.getLicensesCollection(orgId, trackId);
    const q = query(licensesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Get all licenses for a track that belong to a specific client
  // Used by clients to see their license history for a track
  getLicensesForClient = async (sourceOrgId: string, trackId: string, clientOrgId: string) => {
    const licensesRef = this.getLicensesCollection(sourceOrgId, trackId);
    const q = query(
      licensesRef,
      where('clientOrgId', '==', clientOrgId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Real-time listener for all licenses for a track (for artist/owner view)
  subscribeLicensesForTrack = (
    orgId: string,
    trackId: string,
    callback: (licenses: any[]) => void
  ) => {
    const licensesRef = this.getLicensesCollection(orgId, trackId);
    const q = query(licensesRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const licenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(licenses);
    });
  };

  // Real-time listener for licenses belonging to a specific client
  subscribeLicensesForClient = (
    sourceOrgId: string,
    trackId: string,
    clientOrgId: string,
    callback: (licenses: any[]) => void
  ) => {
    const licensesRef = this.getLicensesCollection(sourceOrgId, trackId);
    const q = query(
      licensesRef,
      where('clientOrgId', '==', clientOrgId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const licenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(licenses);
    });
  };

  // Update a license document
  updateLicense = async (
    orgId: string,
    trackId: string,
    licenseId: string,
    updates: any
  ) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    await updateDoc(licenseRef, updates);
  };

  // Delete a license document
  deleteLicense = async (orgId: string, trackId: string, licenseId: string) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    await deleteDoc(licenseRef);
  };

  // Deactivate all licenses for a track (set status to 'inactive')
  deactivateTrackLicenses = async (orgId: string, trackId: string) => {
    const licenses = await this.getLicensesForTrack(orgId, trackId);
    const updatePromises = licenses.map(license =>
      this.updateLicense(orgId, trackId, license.id, {
        status: 'inactive',
        deactivatedAt: Date.now(),
        deactivatedBy: this.auth.currentUser?.uid || null,
      })
    );
    await Promise.all(updatePromises);

    // Update track's license status since all licenses are now inactive
    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
    await updateDoc(trackRef, {
      dateExpires: null,
      licenseStatus: {
        isLicensed: false,
        durationType: 'none',
        startDate: null,
        expiryDate: null,
      },
    });

    return licenses.length;
  };

  // Deactivate a single license and update track's dateExpires to greatest remaining active license
  deactivateSingleLicense = async (orgId: string, trackId: string, licenseId: string) => {
    // First, get the license data to check if it was exclusive
    const licenseData = await this.getLicense(orgId, trackId, licenseId) as any;
    const wasExclusive = licenseData?.isExclusive === true;

    // Deactivate the specific license
    await this.updateLicense(orgId, trackId, licenseId, {
      status: 'inactive',
      deactivatedAt: Date.now(),
      deactivatedBy: this.auth.currentUser?.uid || null,
    });

    // If this was an exclusive license, unhide the track and create notification
    if (wasExclusive) {
      // Check if there are any other active exclusive licenses
      const activeExclusive = await this.getActiveExclusiveLicense(orgId, trackId);
      if (!activeExclusive) {
        // No active exclusive licenses - create notification for artist to republish
        await this.createExclusiveLicenseEndedNotification(orgId, trackId, licenseData);
        console.log('Exclusive license ended - notification created for republishing');
      }
    }

    // Get all licenses for the track to find remaining active ones
    const allLicenses = await this.getLicensesForTrack(orgId, trackId);

    // Filter to only active licenses (excluding the one we just deactivated)
    const activeLicenses = allLicenses.filter((license: any) => {
      if (license.id === licenseId) return false;
      if (license.status === 'inactive') return false;
      // Check if expired
      if (license.dateExpires && license.dateExpires < Date.now()) return false;
      return true;
    });

    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);

    if (activeLicenses.length === 0) {
      // No active licenses remaining - clear track's license status
      await updateDoc(trackRef, {
        dateExpires: null,
        licenseStatus: {
          isLicensed: false,
          durationType: 'none',
          startDate: null,
          expiryDate: null,
        },
      });
    } else {
      // Find the greatest dateExpires among active licenses
      // null dateExpires means blanket license (perpetual)
      let maxDateExpires: number | null = null;
      let hasBlanket = false;

      for (const license of activeLicenses as any[]) {
        if (license.dateExpires === null) {
          hasBlanket = true;
        } else if (maxDateExpires === null || license.dateExpires > maxDateExpires) {
          maxDateExpires = license.dateExpires;
        }
      }

      // If there's a blanket license, dateExpires should be null (perpetual)
      // Otherwise use the greatest dateExpires
      const newDateExpires = hasBlanket ? null : maxDateExpires;

      // Find the most recent active license for licenseType
      const mostRecentActive = (activeLicenses as any[]).sort((a, b) => b.createdAt - a.createdAt)[0];

      await updateDoc(trackRef, {
        dateExpires: newDateExpires,
        licenseStatus: {
          isLicensed: true,
          durationType: mostRecentActive.licenseType || 'custom',
          startDate: mostRecentActive.dateStart || null,
          expiryDate: newDateExpires,
        },
      });
    }
  };

  // Share a license (update with clientOrgId or generate claim code)
  shareLicense = async (
    orgId: string,
    trackId: string,
    licenseId: string,
    shareData: { clientOrgId?: string | null; claimCode?: string }
  ) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    const updates: any = {
      sharedAt: Date.now(),
      sharedBy: this.auth.currentUser?.uid || null,
    };

    if (shareData.clientOrgId) {
      updates.clientOrgId = shareData.clientOrgId;
      updates.status = 'active';
    } else if (shareData.claimCode) {
      updates.claimCode = shareData.claimCode;
      updates.status = 'pending';
    }

    await updateDoc(licenseRef, updates);
  };

  /**
   * Get license by claim code (for claim flow)
   * Uses collectionGroup query to search across all organizations' licenses
   * Returns license data with embedded snapshot fields for display
   * Note: License doc contains trackName, sourceOrgName snapshots to avoid auth-required reads
   */
  getLicenseByClaimCode = async (claimCode: string): Promise<{
    license: any;
    track: any;
    sourceOrg: any;
    trackId: string;
    orgId: string;
    licenseId: string;
  } | null> => {
    try {
      // Query all licenses subcollections for matching claim code
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('claimCode', '==', claimCode)
      );

      const querySnapshot = await getDocs(licensesQuery);

      if (querySnapshot.empty) {
        return null;
      }

      // Get the first matching license
      const licenseDoc = querySnapshot.docs[0];
      const licenseData = { id: licenseDoc.id, ...licenseDoc.data() } as any;

      // Parse the path to get orgId and trackId
      // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
      const pathSegments = licenseDoc.ref.path.split('/');
      const orgId = pathSegments[1];
      const trackId = pathSegments[3];

      // Use snapshot fields from license doc (avoids needing authenticated reads)
      // These are stored when the license is created
      const trackData = {
        id: licenseData.trackId || trackId,
        trackName: licenseData.trackName || 'Unknown Track',
      };

      const orgData = {
        id: orgId,
        name: licenseData.sourceOrgName || 'Unknown Organization',
      };

      return {
        license: licenseData,
        track: trackData,
        sourceOrg: orgData,
        trackId,
        orgId,
        licenseId: licenseDoc.id,
      };
    } catch (error) {
      console.error('Error fetching license by claim code:', error);
      return null;
    }
  };

  /**
   * Claim a license - update clientOrgId and mark as active
   * Called when a user claims a license with their organization
   * Also links the claiming org to any global client/agency entries on the license
   */
  claimLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string,
    claimingOrgId: string,
    claimingUserId: string,
    claimingOrgName?: string
  ): Promise<boolean> => {
    try {
      const licenseRef = doc(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses',
        licenseId
      );

      // Get the license data first to check for client/agency fields
      const licenseSnap = await getDoc(licenseRef);
      const licenseData = licenseSnap.exists() ? licenseSnap.data() : null;

      if (!licenseData) {
        console.error('License not found');
        return false;
      }

      // Update the license with claiming org info
      await updateDoc(licenseRef, {
        clientOrgId: claimingOrgId,
        clientOrgName: claimingOrgName || null,
        claimCode: null, // Clear the claim code once claimed
        status: 'active',
        claimedAt: Date.now(),
        claimedBy: claimingUserId,
      });

      // Create audiometa document in the client org's collection
      await this.createClientAudiometaFromLicense(
        sourceOrgId,
        trackId,
        licenseId,
        claimingOrgId,
        licenseData,
        {
          claimedAt: Date.now(),
          claimedBy: claimingUserId,
        }
      );

      // Link the claiming org to global client/agency entries if they exist and aren't already linked
      await this.linkOrgToGlobalEntries(licenseData, claimingOrgId);

      return true;
    } catch (error) {
      console.error('Error claiming license:', error);
      return false;
    }
  };

  /**
   * Link an organization to global client/agency entries
   * Only updates entries that don't already have a linkedOrgId
   */
  private linkOrgToGlobalEntries = async (
    licenseData: any,
    orgId: string
  ): Promise<void> => {
    try {
      const updates: Promise<void>[] = [];

      // Check client field
      if (licenseData.client?.value) {
        updates.push(this.linkOrgToGlobalEntry(CLIENTPATH, licenseData.client.value, orgId));
      }

      // Check agency field
      if (licenseData.agency?.value) {
        updates.push(this.linkOrgToGlobalEntry(AGENCYPATH, licenseData.agency.value, orgId));
      }

      await Promise.all(updates);
    } catch (error) {
      // Log but don't fail the claim if linking fails
      console.error('Error linking org to global entries:', error);
    }
  };

  /**
   * Link an organization to a specific global entry (client or agency)
   * Only updates if the entry doesn't already have a linkedOrgId
   */
  private linkOrgToGlobalEntry = async (
    collectionPath: string,
    entryId: string,
    orgId: string
  ): Promise<void> => {
    try {
      const entryRef = doc(this.db, collectionPath, entryId);
      const entrySnap = await getDoc(entryRef);

      if (entrySnap.exists()) {
        const entryData = entrySnap.data();
        // Only link if not already linked to an org
        if (!entryData.linkedOrgId) {
          await updateDoc(entryRef, {
            linkedOrgId: orgId,
            linkedAt: Date.now(),
          });
          console.log(`Linked org ${orgId} to ${collectionPath}/${entryId}`);
        }
      }
    } catch (error) {
      console.error(`Error linking org to ${collectionPath}/${entryId}:`, error);
    }
  };

  /**
   * Validate a claim code
   * Returns validation result and license info if valid
   */
  validateClaimCode = async (claimCode: string): Promise<{
    valid: boolean;
    error?: string;
    licenseInfo?: {
      license: any;
      track: any;
      sourceOrg: any;
      trackId: string;
      orgId: string;
      licenseId: string;
    };
  }> => {
    try {
      const result = await this.getLicenseByClaimCode(claimCode);

      if (!result) {
        return { valid: false, error: 'Invalid claim code' };
      }

      const { license } = result;

      // Check if already claimed
      if (license.clientOrgId) {
        return { valid: false, error: 'This license has already been claimed' };
      }

      // Check if license is expired
      if (license.dateExpires && license.dateExpires < Date.now()) {
        return { valid: false, error: 'This license has expired' };
      }

      // Check if license status is not pending (should be pending for claim)
      if (license.status !== 'pending') {
        return { valid: false, error: 'This license is not available for claiming' };
      }

      return { valid: true, licenseInfo: result };
    } catch (error) {
      console.error('Error validating claim code:', error);
      return { valid: false, error: 'Error validating claim code' };
    }
  };

  // Update track's dateExpires field (for backwards compatibility with existing license status)
  updateTrackLicenseStatus = async (
    orgId: string,
    trackId: string,
    updates: { dateExpires?: number | null; licenseStatus?: any }
  ) => {
    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
    await updateDoc(trackRef, updates);
  };

  /**
   * Get all tracks licensed TO an organization (where they are the client)
   * Used to display licensed tracks in the client org's library
   * Returns track-like objects with license info embedded
   */
  getLicensedTracksForOrg = async (clientOrgId: string): Promise<any[]> => {
    try {
      // Query all licenses where this org is the client
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'active')
      );

      const querySnapshot = await getDocs(licensesQuery);

      if (querySnapshot.empty) {
        return [];
      }

      // Transform licenses into track-like objects for the UI
      const licensedTracks = querySnapshot.docs.map((licenseDoc) => {
        const licenseData = licenseDoc.data();

        // Parse the path to get sourceOrgId and trackId
        // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
        const pathSegments = licenseDoc.ref.path.split('/');
        const sourceOrgId = pathSegments[1];
        const trackId = pathSegments[3];

        // Return a track-like object with license info
        return {
          // Use the original trackId so audio playback works
          id: trackId,
          trackId: trackId,

          // Track display info from license snapshot
          trackName: licenseData.trackName || 'Unknown Track',

          // License metadata
          licenseId: licenseDoc.id,
          licenseType: licenseData.licenseType,
          dateExpires: licenseData.dateExpires,
          dateStart: licenseData.dateStart,
          jobTitle: licenseData.jobTitle,
          client: licenseData.client,
          agency: licenseData.agency,
          producer: licenseData.producer,
          creativeDirectors: licenseData.creativeDirectors,
          flightingPlatform: licenseData.flightingPlatform || [],
          territories: licenseData.territories,
          notes: licenseData.notes,

          // Source info
          sourceOrgId: sourceOrgId,
          sourceOrgName: licenseData.sourceOrgName || 'Unknown Organization',

          // Flag to indicate this is a licensed track (not owned)
          isLicensedTrack: true,

          // License status for display
          licenseStatus: {
            isLicensed: true,
            durationType: licenseData.licenseType,
            expiryDate: licenseData.dateExpires,
            startDate: licenseData.dateStart,
          },

          // Claimed info
          claimedAt: licenseData.claimedAt,

          // Audio files snapshot from license for playback
          mp3Files: licenseData.mp3Files || [],
          wavFiles: licenseData.wavFiles || [],
          defaultMp3Id: licenseData.defaultMp3Id || null,

          // Default empty arrays for fields expected by TableRows
          tags: [],
          composer: [],
          creativedirector: licenseData.creativeDirectors || [],
          sessionArtists: [],
          flightingCodes: [],
        };
      });

      return licensedTracks;
    } catch (error) {
      console.error('Error fetching licensed tracks:', error);
      return [];
    }
  };

  /**
   * Get pending licenses for an organization
   * These are licenses shared with the org but not yet claimed
   */
  getPendingLicensesForOrg = async (clientOrgId: string): Promise<{
    id: string;
    licenseId: string;
    trackId: string;
    trackName: string;
    sourceOrgId: string;
    sourceOrgName: string;
    licenseType: string;
    dateExpires?: number;
    dateStart?: number;
    sharedAt?: number;
    licensePath: string;
  }[]> => {
    try {
      // Query all licenses where this org is the client and status is pending
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(licensesQuery);

      if (querySnapshot.empty) {
        return [];
      }

      const pendingLicenses = querySnapshot.docs.map((licenseDoc) => {
        const licenseData = licenseDoc.data();

        // Parse the path to get sourceOrgId and trackId
        // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
        const pathSegments = licenseDoc.ref.path.split('/');
        const sourceOrgId = pathSegments[1];
        const trackId = pathSegments[3];

        return {
          id: licenseDoc.id,
          licenseId: licenseDoc.id,
          trackId: trackId,
          trackName: licenseData.trackName || 'Unknown Track',
          sourceOrgId: sourceOrgId,
          sourceOrgName: licenseData.sourceOrgName || 'Unknown Organization',
          licenseType: licenseData.licenseType || 'unknown',
          dateExpires: licenseData.dateExpires,
          dateStart: licenseData.dateStart,
          sharedAt: licenseData.sharedAt,
          licensePath: licenseDoc.ref.path,
        };
      });

      return pendingLicenses;
    } catch (error) {
      console.error('Error fetching pending licenses:', error);
      return [];
    }
  };

  /**
   * Claim a pending license that was shared directly to an org
   * Updates license status and creates audiometa in client org
   */
  claimPendingLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string,
    claimingOrgId: string,
    claimingUserId: string,
    claimingOrgName?: string
  ): Promise<boolean> => {
    try {
      const licenseRef = doc(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses',
        licenseId
      );

      // Get the license data
      const licenseSnap = await getDoc(licenseRef);
      const licenseData = licenseSnap.exists() ? licenseSnap.data() : null;

      if (!licenseData) {
        console.error('License not found');
        return false;
      }

      // Verify this license is for the claiming org
      if (licenseData.clientOrgId !== claimingOrgId) {
        console.error('License is not assigned to this organization');
        return false;
      }

      // Update the license status to active (and org name if not already set)
      const updateData: any = {
        status: 'active',
        claimedAt: Date.now(),
        claimedBy: claimingUserId,
      };
      if (claimingOrgName && !licenseData.clientOrgName) {
        updateData.clientOrgName = claimingOrgName;
      }
      await updateDoc(licenseRef, updateData);

      // Create audiometa document in the client org's collection
      await this.createClientAudiometaFromLicense(
        sourceOrgId,
        trackId,
        licenseId,
        claimingOrgId,
        licenseData,
        {
          claimedAt: Date.now(),
          claimedBy: claimingUserId,
        }
      );

      return true;
    } catch (error) {
      console.error('Error claiming pending license:', error);
      return false;
    }
  };

  /**
   * Search for share targets (organizations + global client/agency entries)
   * Returns combined results with indicators for direct sharing vs claim link
   */
  searchShareTargets = async (searchTerm: string, excludeOrgId?: string): Promise<{
    type: 'org' | 'client' | 'agency';
    id: string;
    name: string;
    linkedOrgId: string | null;
    hasOrg: boolean;
  }[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const results: {
      type: 'org' | 'client' | 'agency';
      id: string;
      name: string;
      linkedOrgId: string | null;
      hasOrg: boolean;
    }[] = [];

    const searchLower = searchTerm.toLowerCase();

    try {
      // Search organizations
      const orgsSnapshot = await getDocs(collection(this.db, ORGANIZATIONSPATH));
      orgsSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const orgName = data.name || '';
        if (
          orgName.toLowerCase().includes(searchLower) &&
          docSnap.id !== excludeOrgId
        ) {
          results.push({
            type: 'org',
            id: docSnap.id,
            name: orgName,
            linkedOrgId: docSnap.id,
            hasOrg: true,
          });
        }
      });

      // Search global client collection
      const clientsSnapshot = await getDocs(collection(this.db, CLIENTPATH));
      clientsSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const clientName = data.label || data.name || '';
        if (clientName.toLowerCase().includes(searchLower)) {
          // Check if already added as org
          const existingOrg = results.find(
            (r) => r.type === 'org' && r.linkedOrgId === data.linkedOrgId
          );
          if (!existingOrg) {
            results.push({
              type: 'client',
              id: docSnap.id,
              name: clientName,
              linkedOrgId: data.linkedOrgId || null,
              hasOrg: !!data.linkedOrgId,
            });
          }
        }
      });

      // Search global agency collection
      const agenciesSnapshot = await getDocs(collection(this.db, AGENCYPATH));
      agenciesSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const agencyName = data.label || data.name || '';
        if (agencyName.toLowerCase().includes(searchLower)) {
          // Check if already added as org or client
          const existingOrg = results.find(
            (r) => r.linkedOrgId === data.linkedOrgId && data.linkedOrgId
          );
          if (!existingOrg) {
            results.push({
              type: 'agency',
              id: docSnap.id,
              name: agencyName,
              linkedOrgId: data.linkedOrgId || null,
              hasOrg: !!data.linkedOrgId,
            });
          }
        }
      });

      // Deduplicate by name: if the same name appears as org + agency/client,
      // keep the org entry (which has the direct share capability)
      const deduped: typeof results = [];
      const seenNames = new Map<string, number>(); // name (lowercase) -> index in deduped

      // Sort so orgs come first, then linked entries, then unlinked
      results.sort((a, b) => {
        if (a.type === 'org' && b.type !== 'org') return -1;
        if (a.type !== 'org' && b.type === 'org') return 1;
        if (a.hasOrg !== b.hasOrg) return a.hasOrg ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

      for (const result of results) {
        const nameLower = result.name.toLowerCase();
        const existingIdx = seenNames.get(nameLower);
        if (existingIdx !== undefined) {
          // Duplicate name — keep the one with an org (better for direct sharing)
          if (!deduped[existingIdx].hasOrg && result.hasOrg) {
            deduped[existingIdx] = result;
          }
          // Otherwise skip — we already have the better entry
        } else {
          seenNames.set(nameLower, deduped.length);
          deduped.push(result);
        }
      }

      return deduped.slice(0, 20); // Limit to 20 results
    } catch (error) {
      console.error('Error searching share targets:', error);
      return [];
    }
  };

  /**
   * Search for organizations and global entries (artists, clients, agencies)
   * Used during org creation to find existing entities to link with
   */
  searchOrganizationsAndEntries = async (searchTerm: string): Promise<{
    type: 'org' | 'artist' | 'client' | 'agency' | 'broadcaster' | 'mediaowner';
    id: string;
    name: string;
    linkedOrgId: string | null;
    hasOrg: boolean;
    orgType?: string;
  }[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase();
    const results: {
      type: 'org' | 'artist' | 'client' | 'agency' | 'broadcaster' | 'mediaowner';
      id: string;
      name: string;
      linkedOrgId: string | null;
      hasOrg: boolean;
      orgType?: string;
    }[] = [];

    // Search each collection independently - don't let one failure stop others

    // Search organizations
    try {
      const orgsRef = collection(this.db, ORGANIZATIONSPATH);
      const orgsSnapshot = await getDocs(orgsRef);
      orgsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.name?.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'org',
            id: docSnap.id,
            name: data.name,
            linkedOrgId: docSnap.id,
            hasOrg: true,
            orgType: data.type,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global artists
    try {
      const artistsRef = collection(this.db, GLOBAL_ARTISTPATH);
      const artistsSnapshot = await getDocs(artistsRef);
      artistsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const artistName = data.label || data.name || '';
        if (artistName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'artist',
            id: docSnap.id,
            name: artistName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global clients
    try {
      const clientsRef = collection(this.db, CLIENTPATH);
      const clientsSnapshot = await getDocs(clientsRef);
      clientsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const clientName = data.label || data.name || '';
        if (clientName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'client',
            id: docSnap.id,
            name: clientName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global agencies
    try {
      const agenciesRef = collection(this.db, AGENCYPATH);
      const agenciesSnapshot = await getDocs(agenciesRef);
      agenciesSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const agencyName = data.label || data.name || '';
        if (agencyName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'agency',
            id: docSnap.id,
            name: agencyName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global broadcasters
    try {
      const broadcastersRef = collection(this.db, BROADCASTERPATH);
      const broadcastersSnapshot = await getDocs(broadcastersRef);
      broadcastersSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const broadcasterName = data.label || data.name || '';
        if (broadcasterName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'broadcaster',
            id: docSnap.id,
            name: broadcasterName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global media owners
    try {
      const mediaOwnersRef = collection(this.db, MEDIAOWNERPATH);
      const mediaOwnersSnapshot = await getDocs(mediaOwnersRef);
      mediaOwnersSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const moName = data.label || data.name || '';
        if (moName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'mediaowner',
            id: docSnap.id,
            name: moName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to return results
    }

    // Sort results: unlinked entries first (they can be claimed), then by name
    results.sort((a, b) => {
      if (a.hasOrg !== b.hasOrg) return a.hasOrg ? 1 : -1;
      return a.name.localeCompare(b.name);
    });

    return results.slice(0, 20);
  };

  /**
   * Resolve — or create — a lookup entry in a global collection
   * (`/agency` or `/artists-global`) that matches the campaign header
   * fields. Used by the campaign → audiometa propagation so the value
   * stored on `audiometa.studio` / `.agency` corresponds to a real
   * lookup entry (with a stable `id`), which keeps:
   *   - the library's Studio / Agency filter dropdown consistent,
   *   - the AddAudioForm's Autocomplete hydration working when editing,
   *   - duplicate creation avoided (matches by linkedOrgId or label).
   *
   * Match priority:
   *   1. Entry with `linkedOrgId === orgId` (when orgId is provided)
   *   2. Entry with case-insensitive matching `label`
   *   3. None → create a new doc with `label`, `value`, and
   *      `linkedOrgId` (when provided)
   *
   * Returns the shape audiometa expects: `{ id, label, value }`.
   */
  resolveOrCreateLookupEntry = async (
    collectionPath: string,
    args: { orgId?: string | null; name: string },
  ): Promise<{ id: string; label: string; value: string } | null> => {
    const label = (args.name || '').trim();
    if (!label) return null;

    const colRef = collection(this.db, collectionPath);
    const snap = await getDocs(colRef);

    // 1. Try match by linkedOrgId first (strongest link)
    if (args.orgId) {
      const byOrg = snap.docs.find((d) => d.data()?.linkedOrgId === args.orgId);
      if (byOrg) {
        const data: any = byOrg.data();
        // Self-heal: the org's canonical /organizations.name is authoritative.
        // The denormalized `label`/`name` on this lookup doc can drift (e.g. the
        // org was renamed before the rename-cascade in globalOrgManager existed).
        // Prefer the current org name for the returned label AND patch the
        // lookup doc so the dropdowns that read it are corrected too. Best-
        // effort: on any read failure we fall back to the stored label, so this
        // never breaks resolution. `value` (the slug/matching key) is left
        // as-is to avoid touching match logic.
        let orgName = '';
        try {
          const orgSnap = await getDoc(doc(this.db, ORGANIZATIONSPATH, args.orgId));
          orgName = (orgSnap.exists() ? (orgSnap.data()?.name || '') : '').toString().trim();
        } catch { /* best effort — fall back to the stored label below */ }
        if (orgName && (data.label !== orgName || data.name !== orgName)) {
          try { await updateDoc(byOrg.ref, { label: orgName, name: orgName }); } catch { /* best effort */ }
        }
        return {
          id: byOrg.id,
          label: orgName || data.label || data.name || label,
          value: data.value || data.label || data.name || label,
        };
      }
    }

    // 2. Fall back to case-insensitive label match
    const target = label.toLowerCase();
    const byLabel = snap.docs.find((d) => {
      const data: any = d.data();
      const l = (data.label || data.name || '').toString().toLowerCase();
      return l === target;
    });
    if (byLabel) {
      const data: any = byLabel.data();
      // If we found by label but this entry has no linkedOrgId and the
      // caller has one, patch it in so future lookups resolve via
      // priority-1 (stronger link).
      if (args.orgId && !data.linkedOrgId) {
        try {
          await updateDoc(byLabel.ref, { linkedOrgId: args.orgId });
        } catch { /* best effort */ }
      }
      return {
        id: byLabel.id,
        label: data.label || data.name || label,
        value: data.value || data.label || data.name || label,
      };
    }

    // 3. Create a new entry
    const newDocRef = doc(colRef);
    await setDoc(newDocRef, {
      label,
      value: label,
      ...(args.orgId ? { linkedOrgId: args.orgId } : {}),
      createdAt: Date.now(),
      createdBy: this.auth.currentUser?.uid || '',
    });
    return { id: newDocRef.id, label, value: label };
  };

  /**
   * Create a global entry (artist, client, or agency) linked to an organization
   */
  createGlobalEntryForOrg = async (
    orgId: string,
    orgName: string,
    orgType: 'artist' | 'client' | 'agency' | 'broadcaster' | 'mediaowner'
  ): Promise<string> => {
    try {
      let collectionPath: string;
      switch (orgType) {
        case 'artist':
          collectionPath = GLOBAL_ARTISTPATH;
          break;
        case 'client':
          collectionPath = CLIENTPATH;
          break;
        case 'agency':
          collectionPath = AGENCYPATH;
          break;
        case 'broadcaster':
          collectionPath = BROADCASTERPATH;
          break;
        case 'mediaowner':
          collectionPath = MEDIAOWNERPATH;
          break;
        default:
          throw new Error(`Invalid org type: ${orgType}`);
      }

      const entryRef = doc(collection(this.db, collectionPath));
      const entryData = {
        label: orgName,
        value: orgName.toLowerCase().replace(/\s+/g, '-'),
        linkedOrgId: orgId,
        createdAt: Date.now(),
      };

      await setDoc(entryRef, entryData);
      return entryRef.id;
    } catch (error) {
      console.error('Error creating global entry:', error);
      throw error;
    }
  };

  /**
   * Link an existing global entry to an organization
   */
  linkGlobalEntryToOrg = async (
    entryType: 'artist' | 'client' | 'agency' | 'broadcaster' | 'mediaowner',
    entryId: string,
    orgId: string
  ): Promise<void> => {
    try {
      let collectionPath: string;
      switch (entryType) {
        case 'artist':
          collectionPath = GLOBAL_ARTISTPATH;
          break;
        case 'client':
          collectionPath = CLIENTPATH;
          break;
        case 'agency':
          collectionPath = AGENCYPATH;
          break;
        case 'broadcaster':
          collectionPath = BROADCASTERPATH;
          break;
        case 'mediaowner':
          collectionPath = MEDIAOWNERPATH;
          break;
        default:
          throw new Error(`Invalid entry type: ${entryType}`);
      }

      const entryRef = doc(this.db, collectionPath, entryId);
      await updateDoc(entryRef, {
        linkedOrgId: orgId,
        linkedAt: Date.now(),
      });
    } catch (error) {
      console.error('Error linking global entry to org:', error);
      throw error;
    }
  };

  // ========== LICENSE REQUEST METHODS ==========

  /**
   * Create a license request from a client to an artist
   * Stored in the artist's org under licenseRequests subcollection
   */
  createLicenseRequest = async (
    artistOrgId: string,
    trackId: string,
    trackName: string,
    requestingOrgId: string,
    requestingOrgName: string,
    requestData: {
      licenseType: string;
      dateExpires: number | null;
      dateStart: number | null;
      jobTitle?: string;
      client?: { label: string; value: string } | null;
      agency?: { label: string; value: string } | null;
      flightingPlatform?: { label: string; value: string }[];
      territories?: { label: string; value: string }[];
      notes?: string;
    }
  ): Promise<string> => {
    try {
      const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
      const newRequestRef = doc(requestsRef);

      // Filter out undefined values from requestData (Firestore doesn't accept undefined)
      const cleanedRequestData: Record<string, any> = {};
      for (const [key, value] of Object.entries(requestData)) {
        if (value !== undefined) {
          cleanedRequestData[key] = value;
        }
      }

      // Get the user's name from their Firestore document
      let createdByName = this.auth.currentUser?.email || 'Unknown User';
      if (this.auth.currentUser?.uid) {
        const userDetails = await this.getUserDetails(this.auth.currentUser.uid);
        if (userDetails?.name) {
          createdByName = userDetails.name;
        } else if (userDetails?.username) {
          createdByName = userDetails.username;
        }
      }

      const fullRequestData = {
        id: newRequestRef.id,
        trackId,
        trackName,
        artistOrgId,
        requestingOrgId,
        requestingOrgName,
        ...cleanedRequestData,
        status: 'pending' as const,
        createdAt: Date.now(),
        createdBy: this.auth.currentUser?.uid || '',
        createdByName,
      };

      await setDoc(newRequestRef, fullRequestData);
      return newRequestRef.id;
    } catch (error) {
      console.error('Error creating license request:', error);
      throw error;
    }
  };

  /**
   * Get pending license requests for an artist's organization
   */
  getPendingLicenseRequestsForOrg = async (artistOrgId: string): Promise<{
    id: string;
    trackId: string;
    trackName: string;
    requestingOrgId: string;
    requestingOrgName: string;
    licenseType: string;
    dateExpires: number | null;
    dateStart: number | null;
    jobTitle?: string;
    client?: { label: string; value: string } | null;
    agency?: { label: string; value: string } | null;
    flightingPlatform?: { label: string; value: string }[];
    territories?: { label: string; value: string }[];
    notes?: string;
    createdAt: number;
    status: string;
  }[]> => {
    try {
      const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
      const q = query(
        requestsRef,
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as any[];
    } catch (error) {
      console.error('Error fetching pending license requests:', error);
      return [];
    }
  };

  /**
   * Approve a license request - creates a pending license for the client to claim
   */
  approveLicenseRequest = async (
    artistOrgId: string,
    requestId: string
  ): Promise<boolean> => {
    try {
      // Get the request data
      const requestRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests', requestId);
      const requestSnap = await getDoc(requestRef);

      if (!requestSnap.exists()) {
        console.error('License request not found');
        return false;
      }

      const requestData = requestSnap.data();

      // Get track data for the license snapshot
      const trackRef = doc(this.db, getOrgCollectionPath(artistOrgId, AUDIOMETAPATH), requestData.trackId);
      const trackSnap = await getDoc(trackRef);
      const trackData = trackSnap.exists() ? trackSnap.data() : null;

      // Get artist org name
      const orgRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId);
      const orgSnap = await getDoc(orgRef);
      const orgName = orgSnap.exists() ? orgSnap.data().name : 'Unknown Organization';

      // Create the license with pending status (client needs to claim)
      const licenseData = {
        licenseType: requestData.licenseType,
        dateExpires: requestData.dateExpires,
        dateStart: requestData.dateStart || Date.now(),
        jobTitle: requestData.jobTitle || null,
        client: requestData.client || null,
        agency: requestData.agency || null,
        flightingPlatform: requestData.flightingPlatform || [],
        territories: requestData.territories || [],
        notes: requestData.notes || null,
        // Set client org info so they can claim it
        clientOrgId: requestData.requestingOrgId,
        clientOrgName: requestData.requestingOrgName,
        status: 'pending',
        sharedAt: Date.now(),
        // Track and org snapshots
        trackName: requestData.trackName,
        trackId: requestData.trackId,
        sourceOrgId: artistOrgId,
        sourceOrgName: orgName,
        // Audio files and metadata snapshots from track
        mp3Files: trackData?.mp3Files || [],
        wavFiles: trackData?.wavFiles || [],
        defaultMp3Id: trackData?.defaultMp3Id || null,
        key: trackData?.key || null,
        bpm: trackData?.bpm || null,
        genre: trackData?.genre || null,
        tags: trackData?.tags || [],
        composer: trackData?.composer || [],
        artist: trackData?.artist || null,
        album: trackData?.album || null,
        recordLabel: trackData?.recordLabel || null,
        publisher: trackData?.publisher || null,
        sessionArtists: trackData?.sessionArtists || [],
        trackColor: trackData?.trackColor || null,
        // Request reference
        fromRequestId: requestId,
        createdBy: this.auth.currentUser?.uid || '',
      };

      // Create the license
      await this.createLicense(artistOrgId, requestData.trackId, licenseData);

      // Update request status to approved
      await updateDoc(requestRef, {
        status: 'approved',
        approvedAt: Date.now(),
        approvedBy: this.auth.currentUser?.uid || '',
      });

      return true;
    } catch (error) {
      console.error('Error approving license request:', error);
      return false;
    }
  };

  /**
   * Reject a license request
   */
  rejectLicenseRequest = async (
    artistOrgId: string,
    requestId: string,
    rejectionReason?: string
  ): Promise<boolean> => {
    try {
      const requestRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests', requestId);

      await updateDoc(requestRef, {
        status: 'rejected',
        rejectedAt: Date.now(),
        rejectedBy: this.auth.currentUser?.uid || '',
        rejectionReason: rejectionReason || null,
        dismissedByClient: false,
      });

      return true;
    } catch (error) {
      console.error('Error rejecting license request:', error);
      return false;
    }
  };

  /**
   * Get license requests made by a client organization
   * Used to show the client their pending/rejected requests
   */
  getClientLicenseRequests = async (clientOrgId: string): Promise<{
    id: string;
    trackId: string;
    trackName: string;
    artistOrgId: string;
    licenseType: string;
    status: string;
    createdAt: number;
    rejectedAt?: number;
    rejectionReason?: string;
  }[]> => {
    try {
      // Query all licenseRequests subcollections using collection group
      const requestsQuery = query(
        collectionGroup(this.db, 'licenseRequests'),
        where('requestingOrgId', '==', clientOrgId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(requestsQuery);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          trackId: data.trackId,
          trackName: data.trackName,
          artistOrgId: data.artistOrgId,
          licenseType: data.licenseType,
          status: data.status,
          createdAt: data.createdAt,
          rejectedAt: data.rejectedAt,
          rejectionReason: data.rejectionReason,
        };
      });
    } catch (error) {
      console.error('Error fetching client license requests:', error);
      return [];
    }
  };

  /**
   * Get license requests for a specific track made by a client
   * Used to show pending requests in the client's license history
   */
  getClientLicenseRequestsForTrack = async (
    artistOrgId: string,
    trackId: string,
    clientOrgId: string
  ): Promise<any[]> => {
    try {
      const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
      const q = query(
        requestsRef,
        where('trackId', '==', trackId),
        where('requestingOrgId', '==', clientOrgId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching client license requests for track:', error);
      return [];
    }
  };

  /**
   * Real-time listener for license requests for a specific track made by a client
   */
  subscribeClientLicenseRequestsForTrack = (
    artistOrgId: string,
    trackId: string,
    clientOrgId: string,
    callback: (requests: any[]) => void
  ) => {
    const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
    const q = query(
      requestsRef,
      where('trackId', '==', trackId),
      where('requestingOrgId', '==', clientOrgId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(requests);
    });
  };

  /**
   * Get rejected license requests for a client org that haven't been dismissed
   * Used to notify clients when their requests are rejected
   */
  getRejectedLicenseRequestsForClient = async (clientOrgId: string): Promise<any[]> => {
    try {
      const requestsQuery = query(
        collectionGroup(this.db, 'licenseRequests'),
        where('requestingOrgId', '==', clientOrgId),
        where('status', '==', 'rejected'),
        orderBy('rejectedAt', 'desc')
      );

      const querySnapshot = await getDocs(requestsQuery);
      const results = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        } as any))
        .filter(request => request.dismissedByClient !== true);
      return results;
    } catch (error) {
      console.error('Error fetching rejected license requests:', error);
      return [];
    }
  };

  /**
   * Dismiss a rejected license request (mark as seen by client)
   */
  dismissRejectedLicenseRequest = async (
    artistOrgId: string,
    requestId: string
  ): Promise<boolean> => {
    try {
      const requestRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests', requestId);
      await updateDoc(requestRef, {
        dismissedByClient: true,
        dismissedAt: Date.now(),
      });
      return true;
    } catch (error) {
      console.error('Error dismissing rejected request:', error);
      return false;
    }
  };

  /**
   * Get expired or revoked licenses for a client org that haven't been acknowledged
   * Used to notify clients when their licenses expire or are deactivated
   */
  getExpiredLicensesForClient = async (clientOrgId: string): Promise<any[]> => {
    try {
      // Query all licenses subcollections for this client
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'active'), // Only look at licenses that were active
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(licensesQuery);
      const now = Date.now();

      // Filter for expired licenses that haven't been acknowledged
      const expiredLicenses = querySnapshot.docs
        .map(docSnap => {
          const data = docSnap.data();
          // Get the path to determine sourceOrgId and trackId
          const pathParts = docSnap.ref.path.split('/');
          // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
          const sourceOrgId = pathParts[1];
          const trackId = pathParts[3];

          return {
            id: docSnap.id,
            licenseId: docSnap.id,
            sourceOrgId,
            trackId,
            licensePath: docSnap.ref.path,
            ...data,
          } as any;
        })
        .filter((license: any) => {
          // Check if license is expired and not acknowledged
          const isExpired = license.dateExpires && license.dateExpires < now;
          const isAcknowledged = license.clientAcknowledgedExpiry === true;
          return isExpired && !isAcknowledged;
        });

      return expiredLicenses;
    } catch (error) {
      console.error('Error fetching expired licenses:', error);
      return [];
    }
  };

  /**
   * Get deactivated (revoked) licenses for a client org that haven't been acknowledged
   */
  getDeactivatedLicensesForClient = async (clientOrgId: string): Promise<any[]> => {
    try {
      // Query for deactivated licenses
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'inactive'),
        orderBy('deactivatedAt', 'desc')
      );

      const querySnapshot = await getDocs(licensesQuery);

      // Filter for deactivated licenses that haven't been acknowledged
      const deactivatedLicenses = querySnapshot.docs
        .map(docSnap => {
          const data = docSnap.data();
          const pathParts = docSnap.ref.path.split('/');
          const sourceOrgId = pathParts[1];
          const trackId = pathParts[3];

          return {
            id: docSnap.id,
            licenseId: docSnap.id,
            sourceOrgId,
            trackId,
            licensePath: docSnap.ref.path,
            ...data,
          } as any;
        })
        .filter((license: any) => {
          // Only show if not acknowledged and was deactivated (not just created as inactive)
          const isAcknowledged = license.clientAcknowledgedExpiry === true;
          const wasDeactivated = license.deactivatedAt != null;
          return wasDeactivated && !isAcknowledged;
        });

      return deactivatedLicenses;
    } catch (error) {
      console.error('Error fetching deactivated licenses:', error);
      return [];
    }
  };

  /**
   * Acknowledge an expired or deactivated license (client accepts/dismisses the notification)
   */
  acknowledgeExpiredLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string
  ): Promise<boolean> => {
    try {
      const licenseRef = doc(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses',
        licenseId
      );
      await updateDoc(licenseRef, {
        clientAcknowledgedExpiry: true,
        clientAcknowledgedAt: Date.now(),
      });
      return true;
    } catch (error) {
      console.error('Error acknowledging expired license:', error);
      return false;
    }
  };

  // =====================================================
  // PUBLIC CATALOGUE METHODS
  // =====================================================

  /**
   * Publish or update a track in the public catalogue (Master Library).
   * Called automatically on every track save — all tracks are public by default.
   * Uses setDoc with merge so it works for both create and update.
   */
  publishToMasterCatalogue = async (
    sourceOrgId: string,
    sourceOrgName: string,
    trackId: string,
    trackData: any
  ): Promise<boolean> => {
    try {
      const masterDoc: any = {
        // Source reference
        sourceOrgId,
        sourceTrackId: trackId,
        sourceOrgName,

        // Core track info
        trackName: trackData.trackName || '',
        tags: trackData.tags || [],
        language: trackData.language || [],
        client: trackData.client || null,
        agency: trackData.agency || null,
        producer: trackData.producer || null,
        studio: trackData.studio || null,
        jobTitle: trackData.jobTitle || '',
        jobId: trackData.jobId || null,
        jobCampaign: trackData.jobCampaign || '',

        // Audio files
        mp3Files: trackData.mp3Files || [],
        wavFiles: trackData.wavFiles || [],
        defaultMp3Id: trackData.defaultMp3Id || null,

        // Pipeline status
        approval_status: trackData.approval_status || '',
        qc_status: trackData.qc_status || '',
        is_broadcast_master: trackData.is_broadcast_master || false,
        broadcast_master_file: trackData.broadcast_master_file || null,
        fileVersion: trackData.fileVersion || 1,
        qcVersion: trackData.qcVersion || 1,

        // Publishing metadata
        publishedAt: Date.now(),
        isPublic: true,
      };

      const masterRef = doc(this.db, MASTER_CATALOGUEPATH, trackId);
      await setDoc(masterRef, masterDoc, { merge: true });

      return true;
    } catch (error) {
      console.error('Error publishing to master catalogue:', error);
      return false;
    }
  };

  /**
   * Sync a track's current status to the public catalogue.
   * Call after QC status changes, approval changes, or file updates.
   */
  syncMasterCatalogue = async (
    orgId: string,
    trackId: string,
    updates: Record<string, any>
  ): Promise<void> => {
    try {
      const masterRef = doc(this.db, MASTER_CATALOGUEPATH, trackId);
      await setDoc(masterRef, updates, { merge: true });
      // Also sync to publicCatalogue if the track exists there
      try {
        const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
        const pubSnap = await getDoc(pubRef);
        if (pubSnap.exists()) {
          await setDoc(pubRef, updates, { merge: true });
        }
      } catch { /* best effort */ }
    } catch (error) {
      console.error('Error syncing master catalogue:', error);
    }
  };

  /**
   * Reconcile the studio + agency campaign copies against the source job's
   * current tags. Called on save (create + edit) and on any sync-trigger
   * downstream (status changes, station edits, etc.).
   *
   * Behaviour:
   *   1. Reads the existing source doc (if it exists) to capture OLD studio
   *      and agency tags. New jobs have no prior copy state.
   *   2. For each role (studio, agency), figures out the desired target org
   *      from `jobData.studioOrgId` / `jobData.agencyOrgId`:
   *        - Empty / matches the source org → no copy is desired.
   *        - Different org → that org should have a copy.
   *   3. If the OLD tag pointed at a different org than the NEW one, the
   *      old copy is deleted (orphan cleanup).
   *   4. If a target is desired, writes the full jobData to that org's
   *      `/jobs/{jobId}` with `sourceJobId`, `sourceOrgId`, `isCampaignCopy:
   *      true`, `copyRole`, `copyUpdatedAt`. Same doc id as the source so
   *      cross-org references stay trivial.
   *
   * Per-org state on copies (e.g. linkedTrackIds from a copy-side auto-match)
   * is NOT preserved by this function — `setDoc { merge: true }` overlays
   * the source fields on top. Step 6 of the refactor will introduce a more
   * selective sync that respects per-org fields.
   */
  syncCampaignCopies = async (
    sourceOrgId: string,
    jobId: string,
    jobData: Record<string, any>,
    /**
     * OPTIONAL — the source doc's studio/agency tags AS THEY WERE
     * BEFORE this save. Callers that update the source doc first and
     * then call syncCampaignCopies MUST pass these explicitly, because
     * by the time this function runs the source has already been
     * overwritten and the Firestore fallback below would read the NEW
     * tag values (making old === new and skipping orphan cleanup — a
     * historical bug that survived until 2026-08-06). Callers that
     * invoke this function BEFORE writing the source, or that only
     * ever ADD tags (never change or remove), can omit this param.
     */
    oldTags?: { studioOrgId?: string; agencyOrgId?: string },
  ): Promise<{ studioWritten: boolean; agencyWritten: boolean; orphansDeleted: number }> => {
    const result = { studioWritten: false, agencyWritten: false, orphansDeleted: 0 };
    try {
      const sourceRef = doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId);
      // Prefer caller-supplied oldTags (correct — captured before the
      // source save). Fall back to reading the source doc for backward
      // compatibility with older callers, accepting that the fallback
      // is only right when the caller hasn't yet written the source.
      let oldStudioOrgId = '';
      let oldAgencyOrgId = '';
      if (oldTags) {
        oldStudioOrgId = oldTags.studioOrgId || '';
        oldAgencyOrgId = oldTags.agencyOrgId || '';
      } else {
        try {
          const snap = await getDoc(sourceRef);
          if (snap.exists()) {
            const d = snap.data() as any;
            oldStudioOrgId = d.studioOrgId || '';
            oldAgencyOrgId = d.agencyOrgId || '';
          }
        } catch { /* new doc — no old state */ }
      }

      const newStudioOrgId = (jobData.studioOrgId || '') as string;
      const newAgencyOrgId = (jobData.agencyOrgId || '') as string;

      // Roles to reconcile. Per-role: oldOrgId, newOrgId, label.
      const roles: { old: string; next: string; copyRole: 'studio' | 'agency' }[] = [
        { old: oldStudioOrgId, next: newStudioOrgId, copyRole: 'studio' },
        { old: oldAgencyOrgId, next: newAgencyOrgId, copyRole: 'agency' },
      ];

      // Bake the source org's display name onto the copy so studio /
      // agency views can render "from Juice Audio" in the Source
      // column instead of falling through to `fileName` (which is the
      // source's chase-list filename — meaningless to the recipient).
      // Mirrors what copyDeliveryToBroadcaster does for broadcaster
      // copies via `isDeliveredJob + sourceOrgName`.
      let sourceOrgName = '';
      try {
        const orgSnap = await getDoc(doc(this.db, ORGANIZATIONSPATH, sourceOrgId));
        if (orgSnap.exists()) sourceOrgName = (orgSnap.data() as any)?.name || '';
      } catch { /* best-effort — sourceOrgName just stays empty */ }

      const copyPayload = {
        ...jobData,
        sourceJobId: jobId,
        sourceOrgId,
        sourceOrgName,
        isCampaignCopy: true,
        copyUpdatedAt: Date.now(),
      };

      // Collect linkedTrackIds from the source campaign so orphan
      // cleanup below can also remove the audiometa copies pushed into
      // the detagged org by syncTrackToCampaignOrgs. Reading from
      // jobData (the fresh save payload) rather than Firestore keeps
      // this in-memory and consistent with the source's post-save state.
      const detagCleanupTrackIds = Array.from(new Set(
        ((jobData.stations || []) as any[])
          .map((s) => s && s.linkedTrackId)
          .filter((id: any): id is string => typeof id === 'string' && id.length > 0),
      ));

      for (const r of roles) {
        // Orphan cleanup: old target differs from new AND old wasn't the
        // source itself.
        if (r.old && r.old !== sourceOrgId && r.old !== r.next) {
          try {
            await this.deleteJobDeep(r.old, jobId);
            result.orphansDeleted++;
          } catch (err) {
            console.warn(`[syncCampaignCopies] deleteJobDeep failed for ${r.old}:`, err);
          }

          // Also clean up audiometa copies that were pushed to the
          // detagged org via syncTrackToCampaignOrgs. Uses the existing
          // helper's safety net: only deletes docs marked
          // `isCampaignCopy: true` (never a user's own upload or a
          // delivery / review copy), and only when no OTHER campaign
          // in that org still references the track. Silent best-effort
          // per track — a failure on one shouldn't stop the others.
          for (const trackId of detagCleanupTrackIds) {
            try {
              await this.cleanupOrphanTrackCopies(sourceOrgId, trackId, [r.old]);
            } catch (err) {
              console.warn(
                `[syncCampaignCopies] audiometa cleanup for track ${trackId} in detagged org ${r.old} failed (best effort):`,
                err,
              );
            }
          }
        }
        // Write new copy if desired (target is set + isn't the source).
        if (r.next && r.next !== sourceOrgId) {
          try {
            await setDoc(
              doc(this.db, getOrgCollectionPath(r.next, 'jobs'), jobId),
              { ...copyPayload, copyRole: r.copyRole },
              { merge: true },
            );
            if (r.copyRole === 'studio') result.studioWritten = true;
            else result.agencyWritten = true;
          } catch (err) {
            console.warn(`Failed to sync campaign copy to ${r.copyRole} org ${r.next}:`, err);
          }
        }
      }
    } catch (err) {
      console.warn('syncCampaignCopies failed:', err);
    }
    return result;
  };

  /**
   * Mirror an audiometa track into the orgs related to the campaign that
   * just matched it. Called from `handleAutoMatch` after a station's
   * flighting code is linked to a track.
   *
   * Three possible targets:
   *   - `studioOrgId`     → studio tagged on the campaign header
   *   - `agencyOrgId`     → agency tagged on the campaign header
   *   - `campaignSourceOrgId` → the org that OWNS the source job doc (the
   *     campaign creator). This is what enables non-master creators (e.g.
   *     an agency creating a campaign, or the master org itself) to end
   *     up with a library copy of the audio so they can deliver / share
   *     for review. Analogous to the master-org convenience copy that used
   *     to live inline in JobDetail's auto-match.
   *
   * Behaviour:
   *   - Skip a target when its id is empty OR equals `sourceOrgId` (the
   *     audio owner already has it).
   *   - For each remaining target, write the full trackData to
   *     `/organizations/{target}/audiometa/{trackId}` with `isCampaignCopy:
   *     true`, `sourceOrgId`, `sourceTrackId`, `copyRole`, `copyUpdatedAt`.
   *   - Uses `{ merge: true }` so any pre-existing per-org fields on the
   *     copy aren't blown away.
   *
   * The audiometa copy mirrors `syncCampaignCopies` (the jobs equivalent)
   * but does NOT do orphan cleanup — tracks can belong to multiple
   * campaigns, so a copy on the studio/agency side isn't tied to a single
   * source campaign's lifetime.
   */
  syncTrackToCampaignOrgs = async (
    sourceOrgId: string,
    trackId: string,
    trackData: Record<string, any>,
    targets: { studioOrgId?: string | null; agencyOrgId?: string | null; campaignSourceOrgId?: string | null },
  ): Promise<{ studioWritten: boolean; agencyWritten: boolean; creatorWritten: boolean }> => {
    const result = { studioWritten: false, agencyWritten: false, creatorWritten: false };
    const writes: { orgId: string; role: 'studio' | 'agency' | 'creator' }[] = [
      { orgId: (targets.studioOrgId || '') as string,         role: 'studio' },
      { orgId: (targets.agencyOrgId || '') as string,         role: 'agency' },
      { orgId: (targets.campaignSourceOrgId || '') as string, role: 'creator' },
    ];
    const copyPayload = {
      ...trackData,
      isCampaignCopy: true,
      sourceOrgId,
      sourceTrackId: trackId,
      copyUpdatedAt: Date.now(),
    };
    // De-dup by orgId so if the same org happens to fill more than one role
    // (e.g. creator == studio) we only write once, preferring the role
    // ordering above (studio > agency > creator).
    const seen = new Set<string>();
    for (const w of writes) {
      if (!w.orgId || w.orgId === sourceOrgId || seen.has(w.orgId)) continue;
      seen.add(w.orgId);
      try {
        await setDoc(
          doc(this.db, getOrgCollectionPath(w.orgId, AUDIOMETAPATH), trackId),
          { ...copyPayload, copyRole: w.role },
          { merge: true },
        );
        if (w.role === 'studio') result.studioWritten = true;
        else if (w.role === 'agency') result.agencyWritten = true;
        else result.creatorWritten = true;
      } catch (err) {
        console.warn(`syncTrackToCampaignOrgs: failed for ${w.role} org ${w.orgId}:`, err);
      }
    }
    return result;
  };

  /**
   * Clean up orphan audiometa copies for a track. For each target org,
   * scans its /jobs collection for any station still referencing the
   * trackId. If none, deletes the
   * `/organizations/{targetOrgId}/audiometa/{trackId}` doc — but ONLY if
   * the doc carries one of the marker fields in `options.allowedMarkers`
   * (safety: never touches a user's own uploads or unrelated copies).
   *
   * Default marker set is `['isCampaignCopy']` (backward compatible with
   * pre-2026-08-06 callers). Full campaign delete cascade passes
   * `['isCampaignCopy', 'isDeliveryTrack']` so it can also reach the
   * copies that broadcaster-side delivery flow creates (marked
   * `isDeliveryTrack: true` by `copyDeliveryToBroadcaster`).
   *
   * The audio owner is always skipped — they own the canonical track.
   *
   * Returns which target orgs actually had a copy deleted vs kept
   * (either because another campaign still references it, or the doc
   * didn't carry any of the allowed markers).
   */
  cleanupOrphanTrackCopies = async (
    audioOwnerOrgId: string,
    trackId: string,
    targetOrgIds: (string | null | undefined)[],
    options?: { allowedMarkers?: Array<'isCampaignCopy' | 'isDeliveryTrack'> },
  ): Promise<{ deleted: string[]; kept: string[] }> => {
    const result = { deleted: [] as string[], kept: [] as string[] };
    const allowedMarkers = options?.allowedMarkers && options.allowedMarkers.length > 0
      ? options.allowedMarkers
      : ['isCampaignCopy' as const];
    const uniqueTargets = Array.from(new Set(
      targetOrgIds.filter((id): id is string => !!id && id !== audioOwnerOrgId),
    ));
    for (const targetOrgId of uniqueTargets) {
      try {
        // Any other job in the target org still linked to this track?
        // Firestore can't natively query into an object array's field, so
        // fetch all jobs and client-side filter. Fine for the typical
        // per-org campaign volume (dozens to hundreds of docs max).
        const jobsSnap = await getDocs(collection(this.db, getOrgCollectionPath(targetOrgId, 'jobs')));
        let stillReferenced = false;
        for (const jobDoc of jobsSnap.docs) {
          const stations = (jobDoc.data().stations || []) as any[];
          if (stations.some((s) => s && s.linkedTrackId === trackId)) {
            stillReferenced = true;
            break;
          }
        }
        if (stillReferenced) {
          result.kept.push(targetOrgId);
          continue;
        }
        // Safety: only delete if the doc carries one of the allowed
        // markers. Never touch a user's own upload (no marker).
        const copyRef = doc(this.db, getOrgCollectionPath(targetOrgId, AUDIOMETAPATH), trackId);
        const copySnap = await getDoc(copyRef);
        if (!copySnap.exists()) {
          result.kept.push(targetOrgId);
          continue;
        }
        const data = copySnap.data() as any;
        const hasAllowedMarker = allowedMarkers.some((m) => data?.[m] === true);
        if (hasAllowedMarker) {
          await deleteDoc(copyRef);
          result.deleted.push(targetOrgId);
        } else {
          result.kept.push(targetOrgId);
        }
      } catch (err) {
        console.warn(`cleanupOrphanTrackCopies: failed for target org ${targetOrgId}:`, err);
      }
    }
    return result;
  };

  /**
   * Append a campaign history entry. Best-effort — a failed log write
   * never rolls back the actual campaign save. Entries live at
   * /organizations/{sourceOrgId}/jobs/{jobId}/history/{entryId}.
   *
   * `sourceOrgId` is always the CAMPAIGN SOURCE org (the org that owns
   * the source job doc). Studio and agency copies do NOT get their own
   * history — the audit trail is centralised on the source.
   *
   * Silently skips writing when `changes` is empty so we don't spam the
   * log with no-op save clicks.
   */
  logCampaignChange = async (
    sourceOrgId: string,
    jobId: string,
    entry: {
      actorUid: string;
      actorName: string;
      actorOrgId: string;
      actorOrgName: string;
      actorOrgRole: 'source' | 'studio' | 'agency' | 'creator';
      changes: any[];
    },
  ): Promise<void> => {
    if (!Array.isArray(entry.changes) || entry.changes.length === 0) return;
    try {
      const historyRef = collection(
        this.db,
        getOrgCollectionPath(sourceOrgId, 'jobs'),
        jobId,
        'history',
      );
      const newEntryRef = doc(historyRef);
      await setDoc(newEntryRef, {
        id: newEntryRef.id,
        timestamp: Date.now(),
        actorUid: entry.actorUid || '',
        actorName: entry.actorName || '',
        actorOrgId: entry.actorOrgId || '',
        actorOrgName: entry.actorOrgName || '',
        actorOrgRole: entry.actorOrgRole,
        changes: entry.changes,
      });
    } catch (err) {
      console.warn('logCampaignChange failed (best effort):', err);
    }
  };

  /**
   * Remove a track from the public catalogue (on delete)
   */
  removeFromMasterCatalogue = async (trackId: string): Promise<void> => {
    try {
      const masterRef = doc(this.db, MASTER_CATALOGUEPATH, trackId);
      await deleteDoc(masterRef);
      // Also remove from publicCatalogue if it exists there
      try {
        const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
        await deleteDoc(pubRef);
      } catch { /* may not exist */ }
    } catch (error) {
      console.error('Error removing from master catalogue:', error);
    }
  };

  /**
   * Unpublish a track from the public catalogue
   * Deletes the public catalogue document
   */
  unpublishFromMasterCatalogue = async (
    sourceOrgId: string,
    trackId: string
  ): Promise<boolean> => {
    try {
      const masterRef = doc(this.db, MASTER_CATALOGUEPATH, trackId);
      await deleteDoc(masterRef);
      // Also remove from publicCatalogue if it exists there
      try {
        const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
        await deleteDoc(pubRef);
      } catch { /* may not exist */ }

      // Update the source audiometa to mark as not public
      const sourceRef = doc(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), trackId);
      await updateDoc(sourceRef, { isPublic: false });

      console.log('Track unpublished from master catalogue:', trackId);
      return true;
    } catch (error) {
      console.error('Error unpublishing from master catalogue:', error);
      return false;
    }
  };

  /**
   * Copy a track into the public catalogue. Unlike publishToMasterCatalogue
   * (which owns the master index used by cross-org linking), this ONLY
   * touches /publicCatalogue — the user-facing public library. Master is
   * populated automatically by every other Radio Vault write path (auto-
   * match, QC, review cascade) so master state stays canonical without
   * needing to be re-written here.
   *
   * Called by the per-track PublicToggle in the library.
   *
   * SOURCE OF TRUTH: /publicCatalogue MEMBERSHIP. Every org subscribes
   * to /publicCatalogue via usePublicTracksStore at app boot, so a write
   * here immediately propagates to every org's PublicToggle without any
   * per-org isPublic mirroring or cross-org cascade. This is why we
   * don't touch per-org audiometa docs from here.
   *
   * `audioOwnerOrgId` is the org that OWNS the audio (the studio/artist
   * that uploaded it). Stored on the public doc as `sourceOrgId` so the
   * public library UI can trace where the audio comes from.
   *
   * `actingOrgId` / `actingOrgName` is the org that pressed the toggle
   * (typically agency or master). Recorded on the public doc for audit.
   */
  addToPublicCatalogue = async (
    audioOwnerOrgId: string,
    actingOrgId: string,
    actingOrgName: string,
    trackId: string,
    trackData: any,
  ): Promise<boolean> => {
    try {
      const publicDoc: any = {
        // Source reference — same shape as publishToMasterCatalogue emits
        // so the public library UI can share rendering with master.
        sourceOrgId: audioOwnerOrgId,
        sourceTrackId: trackId,
        sourceOrgName: trackData.sourceOrgName || actingOrgName,

        // Core track info
        trackName: trackData.trackName || '',
        tags: trackData.tags || [],
        language: trackData.language || [],
        client: trackData.client || null,
        agency: trackData.agency || null,
        producer: trackData.producer || null,
        studio: trackData.studio || null,
        jobTitle: trackData.jobTitle || '',
        jobId: trackData.jobId || null,
        jobCampaign: trackData.jobCampaign || '',

        // Audio files
        mp3Files: trackData.mp3Files || [],
        wavFiles: trackData.wavFiles || [],
        defaultMp3Id: trackData.defaultMp3Id || null,

        // Pipeline status snapshot
        approval_status: trackData.approval_status || '',
        qc_status: trackData.qc_status || '',
        is_broadcast_master: trackData.is_broadcast_master || false,
        broadcast_master_file: trackData.broadcast_master_file || null,
        fileVersion: trackData.fileVersion || 1,
        qcVersion: trackData.qcVersion || 1,

        // Publishing metadata — who pressed the toggle, when.
        publishedAt: Date.now(),
        publishedByOrgId: actingOrgId,
        publishedByOrgName: actingOrgName,
        isPublic: true,
      };

      const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
      await setDoc(pubRef, publicDoc, { merge: true });

      return true;
    } catch (error) {
      console.error('Error adding to public catalogue:', error);
      return false;
    }
  };

  /**
   * Delete a track from the public catalogue. Since PublicToggle state
   * is derived from /publicCatalogue membership, removing the doc is
   * enough — every org's toggle sees the change via the shared
   * publicTracks-store snapshot subscription. Master catalogue is
   * untouched (it stays as the internal cross-org linking index).
   *
   * `audioOwnerOrgId` is currently accepted for signature symmetry with
   * `addToPublicCatalogue` and future use (e.g. auditing which org's
   * track was unpublished); not read in the current implementation.
   */
  removeFromPublicCatalogue = async (
    _audioOwnerOrgId: string,
    trackId: string,
  ): Promise<boolean> => {
    try {
      const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
      await deleteDoc(pubRef);
      return true;
    } catch (error) {
      console.error('Error removing from public catalogue:', error);
      return false;
    }
  };

  /**
   * Update a track in the public catalogue (when source track is edited)
   * Only updates if the track is currently public
   */
  updateMasterCatalogueTrack = async (
    sourceOrgId: string,
    sourceOrgName: string,
    trackId: string,
    trackData: any
  ): Promise<boolean> => {
    try {
      const masterRef = doc(this.db, MASTER_CATALOGUEPATH, trackId);
      const masterSnap = await getDoc(masterRef);

      if (!masterSnap.exists()) {
        return true;
      }

      const existingData = masterSnap.data();

      const updateData = {
        trackName: trackData.trackName || '',
        artist: trackData.artist || null,
        album: trackData.album || null,
        genre: trackData.genre || null,
        tags: trackData.tags || [],
        key: trackData.key || null,
        bpm: trackData.bpm || null,
        mp3Files: trackData.mp3Files || [],
        waveformUrl: trackData.waveformUrl || null,
        trackColor: trackData.trackColor || null,
        sourceOrgName,
        updatedAt: Date.now(),
        isHiddenByExclusiveLicense: existingData.isHiddenByExclusiveLicense || false,
        exclusiveLicenseExpiresAt: existingData.exclusiveLicenseExpiresAt || null,
      };

      await updateDoc(masterRef, updateData);
      // Also update publicCatalogue if the track exists there
      try {
        const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
        const pubSnap = await getDoc(pubRef);
        if (pubSnap.exists()) {
          await updateDoc(pubRef, updateData);
        }
      } catch { /* best effort */ }
      return true;
    } catch (error) {
      console.error('Error updating master catalogue track:', error);
      return false;
    }
  };

  /**
   * Get a track from the public catalogue
   */
  getMasterCatalogueTrack = async (trackId: string): Promise<any | null> => {
    try {
      const masterRef = doc(this.db, MASTER_CATALOGUEPATH, trackId);
      const masterSnap = await getDoc(masterRef);

      if (!masterSnap.exists()) {
        return null;
      }

      return { id: masterSnap.id, ...masterSnap.data() };
    } catch (error) {
      console.error('Error getting master catalogue track:', error);
      return null;
    }
  };

  /**
   * Set track hidden status due to exclusive license
   * Called when an exclusive license is created or expires/revoked
   */
  setTrackHiddenByExclusiveLicense = async (
    trackId: string,
    isHidden: boolean,
    expiresAt: number | null = null
  ): Promise<boolean> => {
    try {
      const updates = {
        isHiddenByExclusiveLicense: isHidden,
        exclusiveLicenseExpiresAt: expiresAt,
      };
      // Update master catalogue
      const masterRef = doc(this.db, MASTER_CATALOGUEPATH, trackId);
      const masterSnap = await getDoc(masterRef);
      if (masterSnap.exists()) {
        await updateDoc(masterRef, updates);
      }
      // Also update public catalogue if it exists there
      try {
        const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
        const pubSnap = await getDoc(pubRef);
        if (pubSnap.exists()) {
          await updateDoc(pubRef, updates);
        }
      } catch { /* best effort */ }
      return true;
    } catch (error) {
      console.error('Error setting track hidden by exclusive license:', error);
      return false;
    }
  };

  /**
   * Create a notification for when an exclusive license ends
   * This prompts the artist to make the track public again
   */
  createExclusiveLicenseEndedNotification = async (
    orgId: string,
    trackId: string,
    licenseData: any
  ): Promise<boolean> => {
    try {
      // Store notification in org's notifications subcollection
      const notificationsRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'exclusiveLicenseNotifications');
      const notificationDoc = doc(notificationsRef);

      await setDoc(notificationDoc, {
        id: notificationDoc.id,
        trackId,
        trackName: licenseData?.trackName || 'Unknown Track',
        licenseId: licenseData?.id,
        clientName: licenseData?.client?.label || 'Unknown Client',
        endedAt: Date.now(),
        type: 'revoked', // or 'expired' for scheduled checks
        dismissed: false,
        republished: false,
        createdAt: Date.now(),
      });

      return true;
    } catch (error) {
      console.error('Error creating exclusive license notification:', error);
      return false;
    }
  };

  /**
   * Get pending exclusive license notifications for an org
   */
  getExclusiveLicenseNotifications = async (orgId: string): Promise<any[]> => {
    try {
      const notificationsRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'exclusiveLicenseNotifications');
      const q = query(
        notificationsRef,
        where('dismissed', '==', false),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching exclusive license notifications:', error);
      return [];
    }
  };

  /**
   * Dismiss an exclusive license notification
   */
  dismissExclusiveLicenseNotification = async (
    orgId: string,
    notificationId: string,
    republish: boolean = false
  ): Promise<boolean> => {
    try {
      const notificationRef = doc(
        this.db,
        ORGANIZATIONSPATH,
        orgId,
        'exclusiveLicenseNotifications',
        notificationId
      );
      await updateDoc(notificationRef, {
        dismissed: true,
        republished: republish,
        dismissedAt: Date.now(),
      });
      return true;
    } catch (error) {
      console.error('Error dismissing exclusive license notification:', error);
      return false;
    }
  };

  /**
   * Check if a track has an active exclusive license
   * Returns the license details if one exists, null otherwise
   */
  getActiveExclusiveLicense = async (
    sourceOrgId: string,
    trackId: string
  ): Promise<any | null> => {
    try {
      const licensesRef = collection(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses'
      );

      const now = Date.now();
      const licensesQuery = query(
        licensesRef,
        where('isExclusive', '==', true),
        where('status', '==', 'active')
      );

      const querySnapshot = await getDocs(licensesQuery);

      // Find an exclusive license that hasn't expired
      for (const docSnap of querySnapshot.docs) {
        const license = docSnap.data();
        const isExpired = license.dateExpires && license.dateExpires < now;
        if (!isExpired) {
          return { id: docSnap.id, ...license };
        }
      }

      return null;
    } catch (error) {
      console.error('Error checking for active exclusive license:', error);
      return null;
    }
  };

  /**
   * Get all visible tracks from the public catalogue
   * Filters out tracks hidden by exclusive licenses
   */
  getMasterCatalogueTracks = async (): Promise<any[]> => {
    try {
      const masterRef = collection(this.db, MASTER_CATALOGUEPATH);
      const masterQuery = query(
        masterRef,
        where('isPublic', '==', true),
        where('isHiddenByExclusiveLicense', '==', false),
        orderBy('publishedAt', 'desc')
      );

      const querySnapshot = await getDocs(masterQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching master catalogue tracks:', error);
      return [];
    }
  };

  getMasterCatalogueRef = () => {
    return collection(this.db, MASTER_CATALOGUEPATH);
  };

  getPublicCatalogueRef = () => {
    return collection(this.db, PUBLIC_CATALOGUEPATH);
  };

  // ===== APPROVAL REVIEW SYSTEM =====

  /**
   * Sync updates to all isReviewTrack copies of a track across client orgs.
   * Finds all reviews for the track, collects unique clientOrgIds, and updates each copy.
   */
  syncReviewTrackCopies = async (
    sourceOrgId: string,
    trackId: string,
    updates: Record<string, any>
  ): Promise<void> => {
    try {
      const reviewsRef = collection(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), trackId, 'reviews');
      const reviewsSnap = await getDocs(reviewsRef);
      const clientOrgIds = new Set<string>();
      reviewsSnap.docs.forEach(d => {
        const cid = d.data().clientOrgId;
        if (cid) clientOrgIds.add(cid);
      });
      for (const clientOrgId of Array.from(clientOrgIds)) {
        try {
          const clientTrackRef = doc(this.db, getOrgCollectionPath(clientOrgId, AUDIOMETAPATH), trackId);
          const clientSnap = await getDoc(clientTrackRef);
          if (clientSnap.exists() && clientSnap.data()?.isReviewTrack) {
            await updateDoc(clientTrackRef, updates);
          }
        } catch { /* best effort */ }
      }
    } catch { /* best effort */ }
  };

  /**
   * Cascade a track's field updates (QC status, approval status, file
   * changes, etc.) to EVERY audiometa copy of the track across all orgs.
   * This is the unifying primitive for "when any authorised org acts on a
   * track, all related orgs see the change".
   *
   * Discovery uses a `collectionGroup('audiometa')` query on `sourceTrackId`
   * — which is set on every copy created by syncTrackToCampaignOrgs (studio,
   * agency, creator/master), by delivery flows (broadcaster/mediaowner),
   * and by review flows (isReviewTrack copies). Requires the collection-
   * group single-field index defined in firestore.indexes.json.
   *
   * The source-of-audio doc itself does NOT have `sourceTrackId` set — it's
   * the origin. The caller is responsible for updating the source directly
   * BEFORE calling this helper. This helper only updates copies.
   *
   * All writes are best-effort per-doc: a failing target org (e.g. rules
   * denial for that specific org's audiometa) won't stop the rest of the
   * cascade.
   *
   * Also invokes syncMasterCatalogue so master/public catalogue snapshots
   * stay consistent.
   */
  syncTrackFieldsAcrossOrgs = async (
    sourceOrgId: string,
    trackId: string,
    updates: Record<string, any>,
  ): Promise<{ updated: number; failed: number }> => {
    let updated = 0;
    let failed = 0;
    try {
      const cgQuery = query(
        collectionGroup(this.db, AUDIOMETAPATH),
        where('sourceTrackId', '==', trackId),
      );
      const snap = await getDocs(cgQuery);
      for (const d of snap.docs) {
        try {
          await updateDoc(d.ref, updates);
          updated++;
        } catch (err) {
          failed++;
          console.warn(`syncTrackFieldsAcrossOrgs: failed for ${d.ref.path}:`, err);
        }
      }
    } catch (err) {
      console.warn('syncTrackFieldsAcrossOrgs: collection-group query failed:', err);
    }
    // Master + public catalogues also reflect state. Best-effort.
    try {
      // Only pass fields the master catalogue cares about — status +
      // version-ish. Anything more is a no-op because syncMasterCatalogue
      // does a merge:true setDoc so extra fields are just noise.
      await this.syncMasterCatalogue(sourceOrgId, trackId, updates);
    } catch { /* best effort */ }
    return { updated, failed };
  };

  /**
   * Create a review request for a track (share for client approval)
   * Stores review record as a subcollection on the track, similar to licenses
   */
  createReviewRequest = async (
    orgId: string,
    trackId: string,
    reviewData: {
      reviewCode: string | null;
      shareTarget: any | null;
      notes: string;
      sharedAt: number;
      trackName: string;
      sourceOrgName: string;
      // Track data snapshots for external review page
      mp3Files?: any[];
      wavFiles?: any[];
      defaultMp3Id?: string;
      language?: any[];
      client?: any;
      agency?: any;
      tags?: any[];
      fileVersion?: number;
      /** Track description (from audiometa.notes — originates from chase-list
       *  description on auto-match, or manual entry). Snapshotted into the
       *  review lookup so external reviewers can read it without needing
       *  audiometa access, and into the client-org review copy so the
       *  in-app PendingReviewsModal picks it up. Separate from the
       *  reviewer-instructions `notes` field above. */
      trackNotes?: string;
    }
  ): Promise<string> => {
    // For cross-org tracks, write reviews to source org's subcollection (shared history)
    const sourceOrgId = (reviewData as any).sourceTrackOrgId;
    const reviewOrgId = (sourceOrgId && sourceOrgId !== orgId) ? sourceOrgId : orgId;

    const reviewsRef = collection(
      this.db,
      getOrgCollectionPath(reviewOrgId, AUDIOMETAPATH),
      trackId,
      'reviews'
    );
    const newReviewRef = doc(reviewsRef);

    const fullReviewData = {
      id: newReviewRef.id,
      ...reviewData,
      sourceOrgId: reviewOrgId,
      trackId: trackId,
      status: 'pending', // pending | approved | rejected
      dismissedByArtist: false, // set to true when artist dismisses the notification
      createdAt: Date.now(),
      createdBy: this.auth.currentUser?.uid || '',
      createdByName: this.auth.currentUser?.displayName || this.auth.currentUser?.email || '',
      createdByEmail: this.auth.currentUser?.email || '',
      // Share target details
      clientOrgId: reviewData.shareTarget?.linkedOrgId || null,
      clientOrgName: reviewData.shareTarget?.name || null,
      // Link expiry (7 days for external links)
      expiresAt: reviewData.reviewCode ? Date.now() + (7 * 24 * 60 * 60 * 1000) : null,
      // File version being reviewed
      fileVersion: reviewData.fileVersion || 1,
    };

    await setDoc(newReviewRef, fullReviewData);

    // If there's a review code, create a top-level lookup document for public access
    // This avoids collectionGroup queries and permission issues for external reviewers
    if (reviewData.reviewCode) {
      const lookupRef = doc(this.db, 'reviewCodes', reviewData.reviewCode);
      await setDoc(lookupRef, {
        reviewCode: reviewData.reviewCode,
        orgId: reviewOrgId,
        trackId: trackId,
        reviewId: newReviewRef.id,
        status: 'pending',
        expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000),
        createdAt: Date.now(),
        // Snapshot fields so the review page can display without extra reads
        trackName: reviewData.trackName,
        sourceOrgName: reviewData.sourceOrgName,
        notes: reviewData.notes || '',
        // Spot description / brief (sourced from audiometa.notes). Separate
        // from `notes` above which is reviewer instructions.
        trackNotes: reviewData.trackNotes || '',
        // Audio file snapshots for playback on external review page
        mp3Files: reviewData.mp3Files || [],
        wavFiles: reviewData.wavFiles || [],
        defaultMp3Id: reviewData.defaultMp3Id || null,
        // Track metadata for display
        language: reviewData.language || [],
        client: reviewData.client || null,
        agency: reviewData.agency || null,
        tags: reviewData.tags || [],
      });
    }

    // If shared with an org (in-app), write a denormalised entry into the
    // client org's /incomingReviews subcollection. This is what powers the
    // bell modal — replaces the old approach of dumping a copy of the track
    // into the client org's audiometa.
    // Self-skip: don't write if the client IS the source org (review of
    // own work is meaningless).
    const clientOrgId = reviewData.shareTarget?.linkedOrgId;
    if (clientOrgId && clientOrgId !== reviewOrgId) {
      // Pull source job metadata for context on the bell card.
      let sourceJobFields: any = {};
      try {
        const srcRef = doc(this.db, getOrgCollectionPath(reviewOrgId, AUDIOMETAPATH), trackId);
        const srcSnap = await getDoc(srcRef);
        if (srcSnap.exists()) {
          const src = srcSnap.data();
          if (src.jobId) sourceJobFields.jobId = src.jobId;
          if (src.jobCampaign) sourceJobFields.jobCampaign = src.jobCampaign;
          if (src.jobTitle) sourceJobFields.jobTitle = src.jobTitle;
        }
      } catch { /* best effort */ }

      const incomingRef = doc(
        this.db,
        getOrgCollectionPath(clientOrgId, 'incomingReviews'),
        newReviewRef.id,
      );
      await setDoc(incomingRef, {
        id: newReviewRef.id,
        reviewId: newReviewRef.id,
        sourceOrgId: reviewOrgId,
        sourceOrgName: reviewData.sourceOrgName,
        sourceTrackId: trackId,
        trackName: reviewData.trackName,
        // Reviewer-facing text. `notes` is the spot description (from
        // audiometa.notes); `reviewerInstructions` is the freeform message
        // the artist wrote in the share modal. Renamed at the storage
        // layer to avoid the historical name collision between the two.
        notes: reviewData.trackNotes || '',
        reviewerInstructions: reviewData.notes || '',
        // Audio for playback
        mp3Files: reviewData.mp3Files || [],
        wavFiles: reviewData.wavFiles || [],
        defaultMp3Id: reviewData.defaultMp3Id || null,
        // Display metadata
        language: reviewData.language || [],
        client: reviewData.client || null,
        agency: reviewData.agency || null,
        tags: reviewData.tags || [],
        ...sourceJobFields,
        // Lifecycle
        sharedAt: reviewData.sharedAt,
        sharedByOrgId: orgId,
        sharedByOrgName: reviewData.sourceOrgName,
        reviewCode: reviewData.reviewCode || null,
        fileVersion: reviewData.fileVersion || 1,
      });
    }

    // Update calling org's track status
    const callerTrackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
    await updateDoc(callerTrackRef, { approval_status: 'pending' }).catch(() => {});

    // Also update source org's track status if cross-org
    if (reviewOrgId !== orgId) {
      try {
        const srcTrackRef = doc(this.db, getOrgCollectionPath(reviewOrgId, AUDIOMETAPATH), trackId);
        await updateDoc(srcTrackRef, { approval_status: 'pending' });
      } catch { /* best effort */ }
    }

    // Cascade approval_status: 'pending' to every campaign / delivery copy of
    // the track across all orgs. Without this, other campaign copies (e.g.
    // agency's mirror when master initiates the review) stay on the previous
    // status until manual refresh. Matches the QC cascade pattern.
    try { await this.syncTrackFieldsAcrossOrgs(reviewOrgId, trackId, { approval_status: 'pending' }); } catch { /* best effort */ }

    // Sync to master catalogue
    try { await this.syncMasterCatalogue(orgId, trackId, { approval_status: 'pending' }); } catch { /* best effort */ }

    return newReviewRef.id;
  };

  /**
   * Create a batch review — multiple tracks under one review code.
   * Creates individual review records per track but a single batch lookup doc.
   */
  createBatchReviewRequest = async (
    orgId: string,
    tracks: {
      trackId: string;
      trackName: string;
      mp3Files: any[];
      wavFiles: any[];
      defaultMp3Id?: string;
      language?: any[];
      client?: any;
      agency?: any;
      tags?: any[];
      fileVersion?: number;
      sourceOrgId?: string | null;
      notes?: string;
      /** Snapshot of station rows this spot flights on. Sent by
       *  JobDetail.handleBatchReviewSubmit so reviewers see the campaign
       *  context (which broadcasters + flighting codes each spot is running
       *  under). Not populated for library-originated reviews — those
       *  reviews go through createReviewRequest and omit stations by
       *  design (the spot often isn't attached to a campaign yet). */
      stations?: Array<{ station: string; mediaOwner: string; flightingCode: string }>;
    }[],
    reviewData: {
      batchReviewCode: string;
      shareTarget: any | null;
      notes: string;
      sourceOrgName: string;
      campaign: string;
    }
  ): Promise<string[]> => {
    const reviewIds: string[] = [];
    const clientOrgId = reviewData.shareTarget?.linkedOrgId || null;

    // Create individual review records for each track
    // For cross-org tracks, write reviews to source org's subcollection (shared history)
    for (const track of tracks) {
      const reviewOrgId = (track.sourceOrgId && track.sourceOrgId !== orgId) ? track.sourceOrgId : orgId;
      const reviewsRef = collection(
        this.db,
        getOrgCollectionPath(reviewOrgId, AUDIOMETAPATH),
        track.trackId,
        'reviews'
      );
      const newReviewRef = doc(reviewsRef);

      const fullReviewData: any = {
        id: newReviewRef.id,
        reviewCode: reviewData.batchReviewCode,
        batchReviewCode: reviewData.batchReviewCode,
        sourceOrgId: reviewOrgId,
        trackId: track.trackId,
        status: 'pending',
        dismissedByArtist: false,
        createdAt: Date.now(),
        createdBy: this.auth.currentUser?.uid || '',
        createdByName: this.auth.currentUser?.displayName || this.auth.currentUser?.email || '',
        createdByEmail: this.auth.currentUser?.email || '',
        clientOrgId,
        clientOrgName: reviewData.shareTarget?.name || null,
        expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000),
        fileVersion: track.fileVersion || 1,
        notes: reviewData.notes,
        sharedAt: Date.now(),
        trackName: track.trackName,
        sourceOrgName: reviewData.sourceOrgName,
        sharedByOrgId: orgId,
        sharedByOrgName: reviewData.sourceOrgName,
      };

      await setDoc(newReviewRef, fullReviewData);
      reviewIds.push(newReviewRef.id);

      // Write per-track entry into client org's /incomingReviews (bell modal
      // source). Self-skip if the client IS the source. Replaces the old
      // approach of dumping a copy of the track into client org's audiometa.
      if (clientOrgId && clientOrgId !== reviewOrgId) {
        let batchSourceJobFields: any = {};
        try {
          const srcRef = doc(this.db, getOrgCollectionPath(reviewOrgId, AUDIOMETAPATH), track.trackId);
          const srcSnap = await getDoc(srcRef);
          if (srcSnap.exists()) {
            const src = srcSnap.data();
            if (src.jobId) batchSourceJobFields.jobId = src.jobId;
            if (src.jobCampaign) batchSourceJobFields.jobCampaign = src.jobCampaign;
            if (src.jobTitle) batchSourceJobFields.jobTitle = src.jobTitle;
          }
        } catch { /* best effort */ }

        const incomingRef = doc(
          this.db,
          getOrgCollectionPath(clientOrgId, 'incomingReviews'),
          newReviewRef.id,
        );
        await setDoc(incomingRef, {
          id: newReviewRef.id,
          reviewId: newReviewRef.id,
          sourceOrgId: reviewOrgId,
          sourceOrgName: reviewData.sourceOrgName,
          sourceTrackId: track.trackId,
          trackName: track.trackName,
          notes: track.notes || '',
          reviewerInstructions: reviewData.notes || '',
          mp3Files: track.mp3Files || [],
          wavFiles: track.wavFiles || [],
          defaultMp3Id: track.defaultMp3Id || null,
          language: track.language || [],
          client: track.client || null,
          agency: track.agency || null,
          tags: track.tags || [],
          // Station snapshot — surfaces "which broadcasters is this spot
          // flighting on" in the in-app bell modal.
          stations: track.stations || [],
          ...batchSourceJobFields,
          sharedAt: Date.now(),
          sharedByOrgId: orgId,
          sharedByOrgName: reviewData.sourceOrgName,
          batchReviewCode: reviewData.batchReviewCode,
          fileVersion: track.fileVersion || 1,
        });
      }

      // Update calling org's track status
      const callerTrackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), track.trackId);
      await updateDoc(callerTrackRef, { approval_status: 'pending' }).catch(() => {});

      // Also update source org's track status if cross-org
      if (reviewOrgId !== orgId) {
        try {
          const sourceTrackRef = doc(this.db, getOrgCollectionPath(reviewOrgId, AUDIOMETAPATH), track.trackId);
          await updateDoc(sourceTrackRef, { approval_status: 'pending' });
        } catch { /* best effort */ }
      }

      // Sync to master catalogue
      try { await this.syncMasterCatalogue(orgId, track.trackId, { approval_status: 'pending' }); } catch { /* best effort */ }
    }

    // Create single batch lookup doc for external access
    const lookupRef = doc(this.db, 'reviewCodes', reviewData.batchReviewCode);
    await setDoc(lookupRef, {
      reviewCode: reviewData.batchReviewCode,
      isBatch: true,
      orgId,
      status: 'pending',
      expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000),
      createdAt: Date.now(),
      sourceOrgName: reviewData.sourceOrgName,
      campaign: reviewData.campaign,
      notes: reviewData.notes || '',
      clientOrgId,
      clientOrgName: reviewData.shareTarget?.name || null,
      // Track snapshots for external display
      tracks: tracks.map((t, i) => ({
        trackId: t.trackId,
        reviewId: reviewIds[i],
        orgId: (t.sourceOrgId && t.sourceOrgId !== orgId) ? t.sourceOrgId : orgId,
        trackName: t.trackName,
        mp3Files: t.mp3Files || [],
        wavFiles: t.wavFiles || [],
        defaultMp3Id: t.defaultMp3Id || null,
        language: t.language || [],
        client: t.client || null,
        agency: t.agency || null,
        tags: t.tags || [],
        // Spot description so external reviewers see it next to the audio.
        notes: t.notes || '',
        // Station snapshot — surfaces "which broadcasters is this spot
        // flighting on" on the external review page. Only present when
        // the review was generated from a campaign (batch path); the
        // library path stores an empty array by design.
        stations: t.stations || [],
        status: 'pending',
      })),
    });

    return reviewIds;
  };

  /**
   * Validate an external review code
   * Uses top-level reviewCodes lookup collection for public access (no auth required)
   */
  validateReviewCode = async (reviewCode: string): Promise<{
    valid: boolean;
    error?: string;
    lookupData?: any;
    reviewInfo?: {
      review: any;
      track: any;
      sourceOrg: any;
      trackId: string;
      orgId: string;
      reviewId: string;
    };
  }> => {
    try {
      // Look up the review code from the top-level collection
      const lookupRef = doc(this.db, 'reviewCodes', reviewCode);
      const lookupDoc = await getDoc(lookupRef);

      if (!lookupDoc.exists()) {
        return { valid: false, error: 'Invalid review link' };
      }

      const lookup = lookupDoc.data();

      // Batch review — return the full lookup data for the batch review page
      if (lookup.isBatch) {
        if (lookup.expiresAt && lookup.expiresAt < Date.now()) {
          return { valid: false, error: 'This review link has expired' };
        }
        return { valid: true, lookupData: lookup };
      }

      // Check if already reviewed or track deleted
      if (lookup.status === 'expired') {
        return { valid: false, error: lookup.expiredReason || 'This review link has expired' };
      }
      if (lookup.status !== 'pending') {
        return { valid: false, error: `This spot has already been ${lookup.status}` };
      }

      // Check if link has expired
      if (lookup.expiresAt && lookup.expiresAt < Date.now()) {
        return { valid: false, error: 'This review link has expired' };
      }

      const { orgId, trackId, reviewId } = lookup;

      // Build review info from lookup snapshot (always available, even for unauthenticated users)
      let review: any = {
        id: reviewId,
        ...lookup,
      };
      let track: any = {
        id: trackId,
        trackName: lookup.trackName,
        mp3Files: lookup.mp3Files || [],
        wavFiles: lookup.wavFiles || [],
        defaultMp3Id: lookup.defaultMp3Id || null,
        language: lookup.language || [],
        client: lookup.client || null,
        agency: lookup.agency || null,
        tags: lookup.tags || [],
        // Spot description snapshot. Stored on the lookup as `trackNotes` to
        // avoid colliding with the reviewer-instructions `notes`, but
        // exposed here under the canonical `notes` field name so UI code
        // reads it the same as the audiometa doc.
        notes: lookup.trackNotes || '',
      };
      let sourceOrg: any = {
        id: orgId,
        name: lookup.sourceOrgName,
      };

      // Try to fetch full data (will succeed for authenticated users, fail gracefully for external)
      try {
        const reviewRef = doc(
          this.db,
          getOrgCollectionPath(orgId, AUDIOMETAPATH),
          trackId,
          'reviews',
          reviewId
        );
        const reviewDoc = await getDoc(reviewRef);
        if (reviewDoc.exists()) {
          review = { id: reviewDoc.id, ...reviewDoc.data() };
        }

        const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
        const trackDoc = await getDoc(trackRef);
        if (trackDoc.exists()) {
          track = { id: trackDoc.id, ...trackDoc.data() };
        }

        const orgRef = doc(this.db, 'organizations', orgId);
        const orgDoc = await getDoc(orgRef);
        if (orgDoc.exists()) {
          sourceOrg = { id: orgDoc.id, ...orgDoc.data() };
        }
      } catch {
        // External users won't have access to org-scoped docs — use lookup snapshots
      }

      return {
        valid: true,
        reviewInfo: {
          review,
          track,
          sourceOrg,
          trackId,
          orgId,
          reviewId,
        },
      };
    } catch (error) {
      console.error('Error validating review code:', error);
      return { valid: false, error: 'Error validating review link' };
    }
  };

  /**
   * Submit a review decision (approve or reject)
   * Updates both the review record and the track's approval_status
   */
  /**
   * Get pending review tracks for an org (tracks shared for approval that are still pending)
   * Queries the org's audiometa for tracks with isReviewTrack=true and approval_status='pending'
   */
  getPendingReviewsForOrg = async (orgId: string): Promise<any[]> => {
    try {
      const audioMetaRef = collection(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH));
      const reviewQuery = query(
        audioMetaRef,
        where('isReviewTrack', '==', true),
        where('approval_status', '==', 'pending')
      );

      const snapshot = await getDocs(reviewQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      return [];
    }
  };

  /**
   * Update a track's approval_status (used to sync status from review notifications)
   */
  updateTrackApprovalStatus = async (
    orgId: string,
    trackId: string,
    status: string
  ): Promise<void> => {
    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
    const trackDoc = await getDoc(trackRef);
    if (trackDoc.exists() && trackDoc.data()?.approval_status !== status) {
      await updateDoc(trackRef, { approval_status: status });
      // Sync to master catalogue
      try { await this.syncMasterCatalogue(orgId, trackId, { approval_status: status }); } catch { /* best effort */ }
    }
  };

  /**
   * Real-time listener for pending reviews in an org (for notification bell + modal)
   * Returns an unsubscribe function
   */
  subscribePendingReviewsForOrg = (
    orgId: string,
    callback: (reviews: any[]) => void
  ) => {
    // Source of truth for incoming reviews moved from
    // `audiometa[isReviewTrack=true,approval_status=pending]` to a dedicated
    // /incomingReviews subcollection. Every doc in the subcollection IS a
    // pending review — entries are deleted on submit/recall — so the query
    // is collection-wide with no where-filters.
    const incomingRef = collection(this.db, getOrgCollectionPath(orgId, 'incomingReviews'));
    return onSnapshot(incomingRef, (snapshot) => {
      const reviews = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(reviews);
    });
  };

  /**
   * Real-time listener for review result notifications for an artist org.
   * Listens to the org-scoped reviewNotifications collection.
   */
  subscribeReviewResultsForOrg = (
    orgId: string,
    callback: (results: any[]) => void
  ) => {
    const notificationsRef = collection(
      this.db,
      getOrgCollectionPath(orgId, 'reviewNotifications')
    );
    const q = query(notificationsRef, orderBy('reviewedAt', 'desc'));

    return onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(results);
    });
  };

  /**
   * Dismiss a review result notification (artist has seen it)
   */
  dismissReviewResult = async (
    orgId: string,
    notificationId: string
  ): Promise<boolean> => {
    try {
      const notifRef = doc(
        this.db,
        getOrgCollectionPath(orgId, 'reviewNotifications'),
        notificationId
      );
      await deleteDoc(notifRef);
      return true;
    } catch (error) {
      console.error('Error dismissing review result:', error);
      return false;
    }
  };

  /**
   * Recall a pending review — resets approval_status to 'unshared' and marks review as recalled.
   * Creates a history entry in the reviews subcollection.
   */
  recallReview = async (orgId: string, trackId: string): Promise<boolean> => {
    try {
      // Update the track's approval_status
      const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
      await updateDoc(trackRef, { approval_status: 'unshared' });

      // Sync to master catalogue
      try { await this.syncMasterCatalogue(orgId, trackId, { approval_status: 'unshared' }); } catch { /* best effort */ }

      // Find the most recent pending review and mark it as recalled
      const reviewsRef = collection(
        this.db,
        getOrgCollectionPath(orgId, AUDIOMETAPATH),
        trackId,
        'reviews'
      );
      // Simple query without composite index requirement
      const pendingQuery = query(
        reviewsRef,
        where('status', '==', 'pending')
      );
      const pendingSnapshot = await getDocs(pendingQuery);

      if (!pendingSnapshot.empty) {
        // If multiple pending reviews, pick the most recent by createdAt
        const sortedDocs = pendingSnapshot.docs.sort((a, b) =>
          (b.data().createdAt || 0) - (a.data().createdAt || 0)
        );
        const latestReview = sortedDocs[0];
        const reviewData = latestReview.data();

        await updateDoc(latestReview.ref, {
          status: 'recalled',
          recalledAt: Date.now(),
          recalledBy: this.auth.currentUser?.uid || '',
        });

        // If there was a review code, update the lookup doc
        if (reviewData.reviewCode) {
          const lookupRef = doc(this.db, 'reviewCodes', reviewData.reviewCode);
          await updateDoc(lookupRef, { status: 'recalled' }).catch(() => {});
        }

        // If the review was shared with a client org in-app, remove the
        // bell entry from their /incomingReviews so the modal stops showing
        // a recalled spot.
        if (reviewData.clientOrgId) {
          try {
            await deleteDoc(doc(
              this.db,
              getOrgCollectionPath(reviewData.clientOrgId, 'incomingReviews'),
              latestReview.id,
            ));
          } catch { /* best effort — may not exist if old flow */ }
          // Legacy cleanup: also remove the old isReviewTrack audiometa
          // copy from pre-refactor shares so libraries don't show orphan
          // pending-review tracks after a recall.
          const clientTrackRef = doc(
            this.db,
            getOrgCollectionPath(reviewData.clientOrgId, AUDIOMETAPATH),
            trackId
          );
          try {
            const clientTrackSnap = await getDoc(clientTrackRef);
            if (clientTrackSnap.exists() && clientTrackSnap.data()?.isReviewTrack) {
              await deleteDoc(clientTrackRef);
            }
          } catch { /* best effort */ }
        }
      }

      return true;
    } catch (error) {
      console.error('Error recalling review:', error);
      return false;
    }
  };

  /**
   * Get all review records for a track (for review history display)
   */
  getReviewsForTrack = async (orgId: string, trackId: string): Promise<any[]> => {
    try {
      const reviewsRef = collection(
        this.db,
        getOrgCollectionPath(orgId, AUDIOMETAPATH),
        trackId,
        'reviews'
      );
      const reviewsQuery = query(reviewsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(reviewsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching reviews for track:', error);
      return [];
    }
  };

  submitReview = async (
    orgId: string,
    trackId: string,
    reviewId: string,
    decision: 'approved' | 'rejected',
    reviewerInfo?: {
      rejectionReason?: string;
      reviewerName?: string;
      reviewerEmail?: string;
      reviewedVia?: 'in-app' | 'external-link';
      reviewerOrgId?: string;
      reviewerOrgName?: string;
    }
  ): Promise<boolean> => {
    try {
      // Update the review record
      const reviewRef = doc(
        this.db,
        getOrgCollectionPath(orgId, AUDIOMETAPATH),
        trackId,
        'reviews',
        reviewId
      );

      const currentUser = this.auth.currentUser;
      const reviewUpdate: any = {
        status: decision,
        reviewedAt: Date.now(),
        reviewedBy: currentUser?.uid || 'external',
        reviewedByName: reviewerInfo?.reviewerName || currentUser?.displayName || currentUser?.email || 'External Reviewer',
        reviewedByEmail: reviewerInfo?.reviewerEmail || currentUser?.email || null,
        reviewedVia: reviewerInfo?.reviewedVia || (currentUser ? 'in-app' : 'external-link'),
      };
      if (reviewerInfo?.rejectionReason) {
        reviewUpdate.rejectionReason = reviewerInfo.rejectionReason;
      }
      // Record the reviewer's org if they have one
      if (reviewerInfo?.reviewerOrgId) {
        reviewUpdate.reviewerOrgId = reviewerInfo.reviewerOrgId;
        reviewUpdate.reviewerOrgName = reviewerInfo.reviewerOrgName || null;
      }

      // Check if review doc still exists (may have been recalled)
      const reviewSnapshot = await getDoc(reviewRef);
      if (!reviewSnapshot.exists()) {
        console.warn('Review document not found — may have been recalled');
        return false;
      }

      // Check if review is still pending (reject if already recalled/reviewed)
      const currentStatus = reviewSnapshot.data()?.status;
      if (currentStatus !== 'pending') {
        console.warn(`Review is ${currentStatus}, not pending — cannot submit`);
        return false;
      }

      await updateDoc(reviewRef, reviewUpdate);

      // Update the source track's approval_status
      // This requires auth — will fail silently for external reviewers
      const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
      try {
        const trackSnapshot = await getDoc(trackRef);
        if (trackSnapshot.exists()) {
          await updateDoc(trackRef, { approval_status: decision });
          // Sync to master catalogue
          try {
            await this.syncMasterCatalogue(orgId, trackId, { approval_status: decision });
          } catch { /* best effort */ }
          // If this is a cross-org track (e.g. master org created the review),
          // also update the source org's audiometa
          const trackData = trackSnapshot.data();
          if (trackData?.sourceOrgId && trackData.sourceOrgId !== orgId) {
            try {
              const sourceRef = doc(this.db, getOrgCollectionPath(trackData.sourceOrgId, AUDIOMETAPATH), trackId);
              await updateDoc(sourceRef, { approval_status: decision });
            } catch { /* best effort */ }
          }
        }
      } catch {
        // External users can't update org-scoped audiometa — track status updated via reviewCodes lookup
      }

      // Get the full review data to check for client org and review code
      // Re-read the review doc (we already confirmed it exists and have public read access)
      const reviewData = (await getDoc(reviewRef)).data();

      // Cascade the approval decision to every copy of this track across
      // all orgs — campaign copies (studio/agency/creator), delivery
      // copies (broadcaster/mediaowner), review copies. Uses the
      // collection-group query on `sourceTrackId`, so any doc that was
      // originated as a mirror of this track gets updated in one pass.
      // syncReviewTrackCopies is kept as a legacy fallback for isReviewTrack
      // docs pre-refactor that may lack sourceTrackId.
      try {
        const reviewCopyUpdate: any = {
          approval_status: decision,
          reviewedAt: Date.now(),
          reviewedBy: reviewUpdate.reviewedBy,
          reviewedByName: reviewUpdate.reviewedByName,
          reviewedByEmail: reviewUpdate.reviewedByEmail,
        };
        // Read source track to include latest job metadata
        try {
          const srcTrackSnap = await getDoc(trackRef);
          if (srcTrackSnap.exists()) {
            const src = srcTrackSnap.data();
            if (src.jobId) reviewCopyUpdate.jobId = src.jobId;
            if (src.jobCampaign) reviewCopyUpdate.jobCampaign = src.jobCampaign;
            if (src.jobTitle) reviewCopyUpdate.jobTitle = src.jobTitle;
          }
        } catch { /* best effort */ }
        await this.syncTrackFieldsAcrossOrgs(orgId, trackId, reviewCopyUpdate);
        // Legacy fallback (does not overlap because syncReviewTrackCopies
        // only touches docs it discovers via the source's /reviews subcol).
        await this.syncReviewTrackCopies(orgId, trackId, reviewCopyUpdate);
      } catch { /* best effort */ }

      // External-link + authenticated reviewer: record their org on the
      // review record so the artist can see who approved/rejected. NO
      // library copy is created — the agency's library should be
      // populated via campaign tagging + auto-match, not as a side effect
      // of reviewing.
      if (reviewerInfo?.reviewerOrgId && !reviewData?.clientOrgId) {
        await updateDoc(reviewRef, {
          clientOrgId: reviewerInfo.reviewerOrgId,
          clientOrgName: reviewerInfo.reviewerOrgName || null,
        });
      }

      // Remove the bell entry from the client org's /incomingReviews now
      // that the review is resolved. Quiet failure if missing (e.g. older
      // shares pre-refactor, or the share was external-only).
      if (reviewData?.clientOrgId) {
        try {
          await deleteDoc(doc(
            this.db,
            getOrgCollectionPath(reviewData.clientOrgId, 'incomingReviews'),
            reviewId,
          ));
        } catch { /* best effort */ }
      }

      // Update the top-level reviewCodes lookup if it exists
      if (reviewData?.reviewCode) {
        const lookupRef = doc(this.db, 'reviewCodes', reviewData.reviewCode);
        await updateDoc(lookupRef, { status: decision }).catch(() => {
          // Lookup doc may not exist if shared in-app without a code
        });
      }

      // Create a notification for the artist org so they know the result
      const notificationsRef = collection(
        this.db,
        getOrgCollectionPath(orgId, 'reviewNotifications')
      );
      const notifDoc = doc(notificationsRef);
      await setDoc(notifDoc, {
        id: notifDoc.id,
        trackId: trackId,
        reviewId: reviewId,
        trackName: reviewData?.trackName || 'Unknown Spot',
        decision: decision,
        reviewedAt: Date.now(),
        reviewedByName: reviewUpdate.reviewedByName,
        reviewedByEmail: reviewUpdate.reviewedByEmail,
        reviewedVia: reviewUpdate.reviewedVia,
        rejectionReason: reviewerInfo?.rejectionReason || null,
        clientOrgName: reviewData?.clientOrgName || null,
      }).catch((err) => {
        console.error('Error creating review notification:', err);
      });

      return true;
    } catch (error) {
      console.error('Error submitting review:', error);
      return false;
    }
  };
  // =====================================================
  // BROADCASTER DELIVERY
  // =====================================================

  // ===== RAPID UPLOAD SYSTEM =====

  /**
   * Get or generate a persistent rapid upload code for a broadcaster org.
   * The code doc lives at /uploadCodes/{code} with broadcasterOrgId + orgName.
   * Also stores a pointer at /organizations/{orgId}/settings/rapidUpload → { code }
   */
  getOrGenerateUploadCode = async (
    orgId: string,
    orgName: string
  ): Promise<string> => {
    const settingsRef = doc(this.db, getOrgCollectionPath(orgId, 'settings'), 'rapidUpload');
    const settingsSnap = await getDoc(settingsRef);
    if (settingsSnap.exists() && settingsSnap.data()?.code) {
      return settingsSnap.data().code;
    }
    // Generate a new code
    const { generateClaimCode } = await import('../../types/license');
    const code = generateClaimCode();

    // Create public lookup
    const codeRef = doc(this.db, 'uploadCodes', code);
    await setDoc(codeRef, {
      code,
      broadcasterOrgId: orgId,
      broadcasterOrgName: orgName,
      createdAt: Date.now(),
      active: true,
    });
    // Store on org settings
    await setDoc(settingsRef, { code, createdAt: Date.now() });
    return code;
  };

  /**
   * Force-generate a fresh rapid upload code for an org, invalidating
   * the previous one. Used to heal orgs whose /uploadCodes/{code} doc
   * has been deleted or corrupted (the settings pointer still cached
   * the broken code, so getOrGenerateUploadCode would just keep
   * returning it). Deactivates the old code doc (best-effort — may
   * already be gone) so anyone visiting an old shared link sees a
   * clean "deactivated" error rather than a raw missing-doc error.
   */
  regenerateUploadCode = async (
    orgId: string,
    orgName: string
  ): Promise<string> => {
    const settingsRef = doc(this.db, getOrgCollectionPath(orgId, 'settings'), 'rapidUpload');
    const settingsSnap = await getDoc(settingsRef);
    const oldCode = settingsSnap.exists() ? (settingsSnap.data() || {}).code : null;

    // Best-effort deactivation of the old code doc. If it doesn't exist
    // (the whole point of regenerating for many callers), swallow the
    // failure — the fresh code write below is what matters.
    if (oldCode) {
      try {
        await updateDoc(doc(this.db, 'uploadCodes', oldCode), {
          active: false,
          deactivatedAt: Date.now(),
          deactivatedReason: 'regenerated',
        });
      } catch { /* old code doc missing — ignore */ }
    }

    const { generateClaimCode } = await import('../../types/license');
    const code = generateClaimCode();

    await setDoc(doc(this.db, 'uploadCodes', code), {
      code,
      broadcasterOrgId: orgId,
      broadcasterOrgName: orgName,
      createdAt: Date.now(),
      active: true,
    });
    await setDoc(settingsRef, { code, createdAt: Date.now(), regeneratedAt: Date.now() });
    return code;
  };

  /**
   * Validate an upload code (public, no auth required).
   * Returns broadcaster info if valid.
   */
  validateUploadCode = async (code: string): Promise<{
    valid: boolean;
    error?: string;
    broadcasterOrgId?: string;
    broadcasterOrgName?: string;
  }> => {
    try {
      const codeRef = doc(this.db, 'uploadCodes', code);
      const codeSnap = await getDoc(codeRef);
      if (!codeSnap.exists()) {
        return { valid: false, error: 'Invalid upload link' };
      }
      const data = codeSnap.data();
      if (data.active === false) {
        return { valid: false, error: 'This upload link has been deactivated' };
      }
      return {
        valid: true,
        broadcasterOrgId: data.broadcasterOrgId,
        broadcasterOrgName: data.broadcasterOrgName,
      };
    } catch (err: any) {
      return { valid: false, error: err.message || 'Error validating upload link' };
    }
  };

  /**
   * Submit a rapid upload (public, no auth required).
   * Creates a delivery notification in the broadcaster's org pending acceptance.
   * Files are already uploaded to storage by the caller; this creates the metadata record.
   */
  submitRapidUpload = async (
    code: string,
    data: {
      campaign: string;
      agency: string;
      agencyOrgId?: string | null;
      senderName?: string;
      senderEmail?: string;
      files: {
        trackId: string; // Pre-generated Firestore doc ID — matches storage folder
        trackName: string;
        mp3Files?: any[];
        wavFiles?: any[];
        defaultMp3Id?: string | null;
      }[];
    }
  ): Promise<{ success: boolean; deliveryCode?: string; error?: string }> => {
    try {
      const validation = await this.validateUploadCode(code);
      if (!validation.valid) return { success: false, error: validation.error };
      const broadcasterOrgId = validation.broadcasterOrgId!;
      const broadcasterOrgName = validation.broadcasterOrgName!;

      // Generate a delivery code for this upload (so broadcaster can see the items)
      const { generateClaimCode } = await import('../../types/license');
      const deliveryCode = generateClaimCode();

      // Build stations array (one entry per track file)
      const stationsWithAudio = data.files.map(f => ({
        station: 'Rapid Upload',
        flightingCode: f.trackName,
        trackName: f.trackName,
        trackId: f.trackId, // Pre-generated doc ID — becomes audiometa doc ID on accept
        mp3Files: f.mp3Files || [],
        wavFiles: f.wavFiles || [],
        defaultMp3Id: f.defaultMp3Id || null,
        language: [],
        tags: [],
        approval_status: '',
        qc_status: '',
        is_broadcast_master: false,
        broadcast_master_file: null,
      }));

      // Public lookup for external delivery link display
      const lookupRef = doc(this.db, 'deliveryCodes', deliveryCode);
      await setDoc(lookupRef, {
        deliveryCode,
        broadcasterOrgId,
        broadcasterOrgName,
        status: 'delivered',
        createdAt: Date.now(),
        campaign: data.campaign || '',
        client: '',
        period: '',
        sourceOrgName: data.agency || 'Rapid Upload',
        isRapidUpload: true,
        stations: stationsWithAudio,
      });

      // Create notification in broadcaster's deliveryNotifications
      const notifRef = doc(collection(this.db, getOrgCollectionPath(broadcasterOrgId, 'deliveryNotifications')));
      await setDoc(notifRef, {
        id: notifRef.id,
        sourceOrgId: data.agencyOrgId || null,
        sourceOrgName: data.agency || 'Rapid Upload',
        jobId: null,
        deliveryId: null,
        deliveryCode,
        campaign: data.campaign || '',
        client: '',
        period: '',
        stationCount: 1,
        trackCount: data.files.length,
        deliveredAt: Date.now(),
        deliveredByName: data.senderName || data.senderEmail || 'Rapid Upload',
        status: 'pending',
        isRapidUpload: true,
      });

      return { success: true, deliveryCode };
    } catch (err: any) {
      console.error('Error submitting rapid upload:', err);
      return { success: false, error: err.message || 'Upload failed' };
    }
  };

  /**
   * Upload a file to rapid-upload storage (public, no auth required).
   * Files land at /rapid-upload/{orgId}/{trackId}/ — broadcaster can move to permanent audio path on accept.
   */
  uploadRapidFile = async (
    broadcasterOrgId: string,
    trackId: string,
    fileData: { name: string; file: Blob | File; metadata?: any }
  ) => {
    const path = `rapid-upload/${broadcasterOrgId}/${trackId}/`;
    return uploadBytes(
      storageRef(this.storage, `${path}${fileData.name}`),
      fileData.file,
      fileData.metadata
    );
  };

  // ===== ACCESS REQUEST SYSTEM =====

  /**
   * Real-time listener for pending access requests in an org (incoming requests).
   * Returns an unsubscribe function.
   */
  subscribeAccessRequestsForOrg = (
    orgId: string,
    callback: (requests: any[]) => void
  ) => {
    const accessRequestsRef = collection(this.db, getOrgCollectionPath(orgId, 'accessRequests'));
    const q = query(accessRequestsRef, where('status', '==', 'pending'));
    return onSnapshot(q, (snapshot: any) => {
      const requests = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
      callback(requests);
    }, (err: any) => {
      console.error('Error subscribing to access requests:', err);
    });
  };

  /**
   * Real-time listener for access requests MADE BY a requester org (outgoing requests).
   * Used to show "Pending" state on the Request Access button after submission.
   * Queries across all orgs via collectionGroup.
   */
  subscribeMyAccessRequests = (
    requesterOrgId: string,
    callback: (requests: any[]) => void
  ) => {
    const q = query(
      collectionGroup(this.db, 'accessRequests'),
      where('requesterOrgId', '==', requesterOrgId),
      where('status', '==', 'pending')
    );
    return onSnapshot(q, (snapshot: any) => {
      const requests = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
      callback(requests);
    }, (err: any) => {
      console.error('Error subscribing to my access requests:', err);
    });
  };

  /**
   * Create an access request from a requesting org to the source org of a track.
   * Creates a notification in the source org's accessRequests subcollection.
   */
  createAccessRequest = async (
    sourceOrgId: string,
    trackId: string,
    requesterOrgId: string,
    requesterOrgName: string,
    trackName: string,
    note: string
  ): Promise<string> => {
    const user = this.auth.currentUser;
    const accessRequestsRef = collection(this.db, getOrgCollectionPath(sourceOrgId, 'accessRequests'));
    const newRef = doc(accessRequestsRef);
    await setDoc(newRef, {
      id: newRef.id,
      trackId,
      trackName,
      requesterOrgId,
      requesterOrgName,
      requesterUid: user?.uid || '',
      requesterName: user?.displayName || user?.email || '',
      requesterEmail: user?.email || '',
      note: note || '',
      status: 'pending',
      createdAt: Date.now(),
    });
    return newRef.id;
  };

  /**
   * Approve an access request — copies the track to the requester's audiometa.
   */
  approveAccessRequest = async (
    sourceOrgId: string,
    requestId: string
  ): Promise<boolean> => {
    try {
      const reqRef = doc(this.db, getOrgCollectionPath(sourceOrgId, 'accessRequests'), requestId);
      const reqSnap = await getDoc(reqRef);
      if (!reqSnap.exists()) return false;
      const req = reqSnap.data();

      // Copy the track from source org to requester org
      const sourceTrackRef = doc(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), req.trackId);
      const sourceTrackSnap = await getDoc(sourceTrackRef);
      if (!sourceTrackSnap.exists()) return false;
      const sourceTrack = sourceTrackSnap.data();

      const requesterTrackRef = doc(this.db, getOrgCollectionPath(req.requesterOrgId, AUDIOMETAPATH), req.trackId);
      await setDoc(requesterTrackRef, {
        ...sourceTrack,
        isDeliveryTrack: true,
        sourceOrgId,
        sourceTrackId: req.trackId,
        sourceOrgName: sourceTrack.sourceOrgName || '',
        grantedViaAccessRequest: true,
        grantedAt: Date.now(),
        createdAt: Date.now(),
      }, { merge: true });

      // Update request status
      await updateDoc(reqRef, {
        status: 'approved',
        resolvedAt: Date.now(),
        resolvedBy: this.auth.currentUser?.uid || '',
      });

      // Create a notification in the requester's org so they know access was granted
      try {
        const resultRef = doc(collection(this.db, getOrgCollectionPath(req.requesterOrgId, 'accessRequestResults')));
        await setDoc(resultRef, {
          id: resultRef.id,
          requestId,
          trackId: req.trackId,
          trackName: req.trackName || sourceTrack.trackName || '',
          sourceOrgId,
          sourceOrgName: sourceTrack.sourceOrgName || '',
          decision: 'approved',
          decidedAt: Date.now(),
          decidedBy: this.auth.currentUser?.uid || '',
          decidedByName: this.auth.currentUser?.displayName || this.auth.currentUser?.email || '',
          dismissed: false,
        });
      } catch { /* best effort */ }

      return true;
    } catch (err) {
      console.error('Error approving access request:', err);
      return false;
    }
  };

  /**
   * Deny an access request.
   */
  denyAccessRequest = async (
    sourceOrgId: string,
    requestId: string,
    reason?: string
  ): Promise<boolean> => {
    try {
      const reqRef = doc(this.db, getOrgCollectionPath(sourceOrgId, 'accessRequests'), requestId);
      const reqSnap = await getDoc(reqRef);
      const req = reqSnap.exists() ? reqSnap.data() : null;

      await updateDoc(reqRef, {
        status: 'denied',
        denyReason: reason || '',
        resolvedAt: Date.now(),
        resolvedBy: this.auth.currentUser?.uid || '',
      });

      // Create a notification in the requester's org
      if (req) {
        try {
          // Look up source org name so the email + in-app modal can show
          // who denied (the approve path already includes this).
          let sourceOrgName = '';
          try {
            const orgDoc = await getDoc(doc(this.db, 'organizations', sourceOrgId));
            sourceOrgName = orgDoc.data()?.name || '';
          } catch { /* ignore */ }

          const resultRef = doc(collection(this.db, getOrgCollectionPath(req.requesterOrgId, 'accessRequestResults')));
          await setDoc(resultRef, {
            id: resultRef.id,
            requestId,
            trackId: req.trackId,
            trackName: req.trackName || '',
            sourceOrgId,
            sourceOrgName,
            decision: 'denied',
            denyReason: reason || '',
            decidedAt: Date.now(),
            decidedBy: this.auth.currentUser?.uid || '',
            decidedByName: this.auth.currentUser?.displayName || this.auth.currentUser?.email || '',
            dismissed: false,
          });
        } catch { /* best effort */ }
      }

      return true;
    } catch (err) {
      console.error('Error denying access request:', err);
      return false;
    }
  };

  /**
   * Real-time listener for access request results (approvals/denials) for the current org.
   */
  subscribeAccessRequestResultsForOrg = (
    orgId: string,
    callback: (results: any[]) => void
  ) => {
    const resultsRef = collection(this.db, getOrgCollectionPath(orgId, 'accessRequestResults'));
    const q = query(resultsRef, where('dismissed', '==', false));
    return onSnapshot(q, (snapshot: any) => {
      const results = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
      callback(results);
    }, (err: any) => {
      console.error('Error subscribing to access request results:', err);
    });
  };

  /**
   * Dismiss an access request result notification.
   */
  dismissAccessRequestResult = async (orgId: string, resultId: string): Promise<boolean> => {
    try {
      const ref = doc(this.db, getOrgCollectionPath(orgId, 'accessRequestResults'), resultId);
      await updateDoc(ref, { dismissed: true });
      return true;
    } catch (err) {
      console.error('Error dismissing access request result:', err);
      return false;
    }
  };

  /**
   * PHASE 2 — cascade-delete stations that were already delivered.
   *
   * Called by ChaseListUploader when the editor detects that saving
   * this edit would remove one or more stations whose
   * `deliveryStatus === 'delivered'`. The source's own job doc has
   * ALREADY been rewritten with the reduced stations array by the
   * time this runs, and `syncCampaignCopies` has already propagated
   * that reduced array to the studio + agency copies. What THIS
   * function does:
   *
   *   1. Reads every delivery record for the job. For every station
   *      snapshot inside a delivery record whose id is being deleted,
   *      marks it `deleted: true, deletedAt`. If a delivery ends up
   *      with 100% of its stations deleted, marks the delivery record
   *      `status: 'deleted'` and disables the public /deliveryCodes/{code}
   *      doc so the external link stops working. Notifications sent to
   *      broadcasters that were never accepted also get deleted.
   *   2. For every affected broadcaster / media-house org (any org
   *      that received one of the deleted stations in a delivery),
   *      reads their campaign copy at /jobs/{jobId}. Removes the
   *      deleted stations from the stations array. If the copy ends
   *      up with zero stations, deletes the campaign copy doc entirely
   *      (per user's Option 1 — broadcaster copies are per-delivery
   *      ephemeral; source/studio/agency keep empty shells).
   *   3. Audiometa cleanup for every org that could hold a copy of a
   *      removed track (source + studio + agency + broadcasters).
   *      For each removed linkedTrackId, scans every job's stations
   *      array in that org. If nothing else references the trackId, deletes
   *      the audiometa doc. The existing Cloud Function
   *      `deleteOrgTrackData` (functions/index.js) then scans OTHER
   *      orgs and, when the last reference is gone, deletes the raw
   *      storage files. This is the pattern requested to prevent
   *      stale tracks from auto-matching to future stations after a
   *      "delete → re-upload with same code" cycle.
   */
  /**
   * Delete a job doc plus its subcollections (`history`, `deliveries`).
   * Firestore does not cascade — deleting the parent alone leaves the
   * subcollection docs stranded and the parent renders as an italicised
   * "ghost" in the console. Callers that just want the doc gone should
   * use this rather than a raw deleteDoc.
   */
  deleteJobDeep = async (orgId: string, jobId: string): Promise<void> => {
    const jobPath = getOrgCollectionPath(orgId, 'jobs');
    for (const sub of ['history', 'deliveries']) {
      try {
        const snap = await getDocs(collection(this.db, jobPath, jobId, sub));
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      } catch (err) {
        console.warn(`[deleteJobDeep] failed clearing ${sub} for ${orgId}/${jobId}:`, err);
      }
    }
    await deleteDoc(doc(this.db, jobPath, jobId));
  };

  /**
   * Full campaign delete cascade — removes a source campaign AND every
   * cross-org copy: studio + agency campaign copies, broadcaster
   * delivery copies, all associated audiometa copies (studio / agency /
   * broadcaster), public /deliveryCodes lookups, and pending
   * broadcaster notifications.
   *
   * User-intent trigger: source studio (or an authorized editor) clicks
   * Delete on the campaign. Aggressive cascade is intentional — source
   * is single source of truth, so deletion at the source means the
   * campaign is being pulled from every downstream org too. Broadcaster
   * data is included regardless of delivery status. See
   * docs/Cross-Org Sync.md for the design.
   *
   * Safety:
   *   - Source-owned audio uploads (no `isCampaignCopy` marker) are
   *     never deleted from the source org — the source can clean their
   *     library manually if they want.
   *   - Audiometa cleanup in each downstream org only touches docs
   *     marked `isCampaignCopy: true` (studio / agency) or
   *     `isDeliveryTrack: true` (broadcaster) that are not referenced
   *     by any other job in that org.
   *   - Cloud Function `deleteOrgTrackData` cleans raw storage files
   *     once the last audiometa reference is gone.
   *
   * Ordering: reads first (capture cross-org targets BEFORE anything is
   * deleted), then broadcaster deletes, then studio/agency deletes,
   * then source delete, then audiometa cleanup last (so the "still
   * referenced?" scan sees the accurate post-delete state).
   */
  cascadeDeleteCampaign = async (
    sourceOrgId: string,
    jobId: string,
  ): Promise<{
    studioCopyDeleted: boolean;
    agencyCopyDeleted: boolean;
    broadcasterCopiesDeleted: string[];
    deliveryCodesDisabled: number;
    pendingNotificationsDeleted: number;
    audiometaDeleted: number;
    errors: string[];
  }> => {
    const result = {
      studioCopyDeleted: false,
      agencyCopyDeleted: false,
      broadcasterCopiesDeleted: [] as string[],
      deliveryCodesDisabled: 0,
      pendingNotificationsDeleted: 0,
      audiometaDeleted: 0,
      errors: [] as string[],
    };
    const now = Date.now();

    // 1. Read source job — capture tags + all trackIds BEFORE deletion.
    const sourceJobRef = doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId);
    let sourceData: any = null;
    try {
      const snap = await getDoc(sourceJobRef);
      if (!snap.exists()) {
        result.errors.push(`Source job /organizations/${sourceOrgId}/jobs/${jobId} does not exist`);
        return result;
      }
      sourceData = snap.data() || {};
    } catch (err) {
      result.errors.push(`Source read failed: ${(err as any)?.message || err}`);
      return result;
    }
    const studioOrgId = (sourceData.studioOrgId || '') as string;
    const agencyOrgId = (sourceData.agencyOrgId || '') as string;
    const stations: any[] = Array.isArray(sourceData.stations) ? sourceData.stations : [];
    const allTrackIds: string[] = Array.from(new Set<string>(
      stations
        .map((s: any) => (s && s.linkedTrackId) as unknown)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    ));

    // 2. Read deliveries — capture broadcaster orgs + delivery codes.
    const broadcasterOrgIds = new Set<string>();
    const deliveryCodes: string[] = [];
    try {
      const dSnap = await getDocs(collection(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId, 'deliveries'));
      for (const d of dSnap.docs) {
        const dData = d.data() || {};
        if (dData.broadcasterOrgId) broadcasterOrgIds.add(dData.broadcasterOrgId);
        if (dData.deliveryCode) deliveryCodes.push(dData.deliveryCode);
      }
    } catch (err) {
      // Undelivered campaign — subcollection may be empty. Continue.
      result.errors.push(`Deliveries read failed (non-fatal): ${(err as any)?.message || err}`);
    }

    // 3. Delete broadcaster job copies.
    for (const bOrgId of Array.from(broadcasterOrgIds)) {
      try {
        await this.deleteJobDeep(bOrgId, jobId);
        result.broadcasterCopiesDeleted.push(bOrgId);
      } catch (err) {
        result.errors.push(`broadcaster ${bOrgId} job delete failed: ${(err as any)?.message || err}`);
      }
    }

    // 4. Disable public /deliveryCodes lookups so external links stop resolving.
    for (const code of deliveryCodes) {
      try {
        await updateDoc(doc(this.db, 'deliveryCodes', code), {
          status: 'deleted',
          deletedAt: now,
        });
        result.deliveryCodesDisabled++;
      } catch { /* best effort — some codes may already be gone */ }
    }

    // 5. Delete pending broadcaster notifications for this jobId.
    for (const bOrgId of Array.from(broadcasterOrgIds)) {
      try {
        const notifsSnap = await getDocs(query(
          collection(this.db, getOrgCollectionPath(bOrgId, 'deliveryNotifications')),
          where('jobId', '==', jobId),
        ));
        for (const n of notifsSnap.docs) {
          try {
            await deleteDoc(n.ref);
            result.pendingNotificationsDeleted++;
          } catch { /* best effort */ }
        }
      } catch { /* best effort */ }
    }

    // 6. Delete studio + agency campaign copies (skip when same-as-source
    //    or when studio and agency happen to be the same org).
    if (studioOrgId && studioOrgId !== sourceOrgId) {
      try {
        await this.deleteJobDeep(studioOrgId, jobId);
        result.studioCopyDeleted = true;
      } catch (err) {
        result.errors.push(`studio ${studioOrgId} job delete failed: ${(err as any)?.message || err}`);
      }
    }
    if (agencyOrgId && agencyOrgId !== sourceOrgId && agencyOrgId !== studioOrgId) {
      try {
        await this.deleteJobDeep(agencyOrgId, jobId);
        result.agencyCopyDeleted = true;
      } catch (err) {
        result.errors.push(`agency ${agencyOrgId} job delete failed: ${(err as any)?.message || err}`);
      }
    }

    // 7. Delete source job LAST — everything above needed to read from it.
    try {
      await this.deleteJobDeep(sourceOrgId, jobId);
    } catch (err) {
      result.errors.push(`source ${sourceOrgId} job delete failed: ${(err as any)?.message || err}`);
    }

    // 8. Audiometa cleanup across every affected org. Runs last so
    //    the "still referenced by another job in this org?" scan sees
    //    the accurate post-delete state. Downstream orgs get their
    //    campaign / delivery copy markers; source is limited to
    //    `isCampaignCopy` so we never touch a user's own upload there.
    const cleanupTargets: Array<{ orgId: string; markers: Array<'isCampaignCopy' | 'isDeliveryTrack'> }> = [];
    if (studioOrgId && studioOrgId !== sourceOrgId) {
      cleanupTargets.push({ orgId: studioOrgId, markers: ['isCampaignCopy'] });
    }
    if (agencyOrgId && agencyOrgId !== sourceOrgId && agencyOrgId !== studioOrgId) {
      cleanupTargets.push({ orgId: agencyOrgId, markers: ['isCampaignCopy'] });
    }
    for (const bOrgId of Array.from(broadcasterOrgIds)) {
      cleanupTargets.push({ orgId: bOrgId, markers: ['isDeliveryTrack'] });
    }
    // Source last — only clean up isCampaignCopy docs (which the
    // source picked up as an agency or downstream org for someone
    // else's campaign). Never touches uploads the source owns.
    cleanupTargets.push({ orgId: sourceOrgId, markers: ['isCampaignCopy'] });

    for (const trackId of allTrackIds) {
      for (const { orgId, markers } of cleanupTargets) {
        try {
          const cleaned = await this.cleanupOrphanTrackCopies(
            sourceOrgId,
            trackId,
            [orgId],
            { allowedMarkers: markers },
          );
          result.audiometaDeleted += cleaned.deleted.length;
        } catch (err) {
          result.errors.push(`audiometa cleanup for track ${trackId} in ${orgId} failed: ${(err as any)?.message || err}`);
        }
      }
    }

    return result;
  };

  cascadeDeleteStations = async (
    sourceOrgId: string,
    jobId: string,
    /** Full station objects being removed — carries `id` for delivery-
     *  record matching AND `linkedTrackId` for audiometa cleanup.
     *  Passing the full objects (not just ids) lets us seed the
     *  trackId set from the input, so non-delivered station deletions
     *  also trigger audiometa cleanup in studio/agency copies. Prior
     *  version only harvested trackIds from delivery records, which
     *  are absent for never-delivered stations. */
    deletedStations: { id: string; linkedTrackId?: string | null }[],
  ): Promise<{
    deliveriesTouched: number;
    deliveriesMarkedDeleted: number;
    broadcasterCopiesReduced: string[];
    broadcasterCopiesDeleted: string[];
    audiometaDeleted: number;
    errors: string[];
  }> => {
    const result = {
      deliveriesTouched: 0,
      deliveriesMarkedDeleted: 0,
      broadcasterCopiesReduced: [] as string[],
      broadcasterCopiesDeleted: [] as string[],
      audiometaDeleted: 0,
      errors: [] as string[],
    };

    if (deletedStations.length === 0) return result;
    const deletedStationIds = deletedStations.map((s) => s.id).filter(Boolean);
    if (deletedStationIds.length === 0) return result;
    const deletedIdSet = new Set(deletedStationIds);
    const now = Date.now();

    // Read source job to know studio / agency orgs for audiometa
    // cleanup fan-out.
    let studioOrgId = '';
    let agencyOrgId = '';
    try {
      const sourceJobSnap = await getDoc(doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId));
      if (sourceJobSnap.exists()) {
        const d = sourceJobSnap.data() as any;
        studioOrgId = d.studioOrgId || '';
        agencyOrgId = d.agencyOrgId || '';
      }
    } catch (err) {
      result.errors.push(`source job read failed: ${(err as any)?.message || err}`);
    }

    // Enumerate deliveries: gather (a) which broadcaster orgs are
    // affected, (b) which trackIds might need cleanup, (c) update
    // delivery records + public deliveryCodes lookups.
    //
    // Seed `deletedTrackIds` from the input UP FRONT. Previously this
    // was populated only from the delivery-record iteration below,
    // meaning non-delivered stations (draft/active campaign edits)
    // contributed no trackIds and their audiometa in studio/agency
    // copies survived indefinitely. Seeding from input makes the
    // audiometa cleanup fire for every deleted station regardless of
    // delivery state (Phase 4 fix, 2026-08-07).
    const affectedBroadcasterOrgIds = new Set<string>();
    const deletedTrackIds = new Set<string>();
    for (const s of deletedStations) {
      if (s.linkedTrackId && typeof s.linkedTrackId === 'string') {
        deletedTrackIds.add(s.linkedTrackId);
      }
    }
    let deliveriesSnap: any;
    try {
      deliveriesSnap = await getDocs(collection(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId, 'deliveries'));
    } catch (err) {
      result.errors.push(`deliveries read failed: ${(err as any)?.message || err}`);
      return result;
    }

    for (const dSnap of deliveriesSnap.docs) {
      const dData = dSnap.data();
      const dStations: any[] = dData.stations || [];
      let touched = false;
      const updatedStations = dStations.map((s: any) => {
        if (s?.id && deletedIdSet.has(s.id) && !s.deleted) {
          touched = true;
          if (dData.broadcasterOrgId) affectedBroadcasterOrgIds.add(dData.broadcasterOrgId);
          if (s.linkedTrackId) deletedTrackIds.add(s.linkedTrackId);
          return { ...s, deleted: true, deletedAt: now };
        }
        return s;
      });
      if (!touched) continue;

      // All-deleted check: if every station in this delivery is now
      // marked deleted, the delivery itself is deleted.
      const remainingCount = updatedStations.filter((s: any) => !s.deleted).length;
      const isFullyDeleted = remainingCount === 0;

      const deliveryUpdate: any = { stations: updatedStations };
      if (isFullyDeleted && dData.status !== 'deleted') {
        deliveryUpdate.status = 'deleted';
        deliveryUpdate.deletedAt = now;
      }
      try {
        await updateDoc(dSnap.ref, deliveryUpdate);
        result.deliveriesTouched++;
        if (isFullyDeleted) result.deliveriesMarkedDeleted++;
      } catch (err) {
        result.errors.push(`delivery ${dSnap.id} update failed: ${(err as any)?.message || err}`);
      }

      // Public deliveryCodes lookup — keep it in sync so the external
      // link either shows the reduced set of stations or refuses to
      // resolve at all when the delivery is fully deleted.
      if (dData.deliveryCode) {
        try {
          const lookupUpdate: any = { stations: updatedStations };
          if (isFullyDeleted) {
            lookupUpdate.status = 'deleted';
            lookupUpdate.deletedAt = now;
          }
          await updateDoc(doc(this.db, 'deliveryCodes', dData.deliveryCode), lookupUpdate);
        } catch { /* best effort */ }
      }

      // If the delivery is fully deleted AND the broadcaster hasn't
      // accepted it yet, the pending notification is now pointing at
      // nothing useful — remove it so the modal doesn't offer a
      // dead accept. We can't tell from here whether it's been
      // accepted; deleteDoc is idempotent so we try either way. This
      // is best-effort — permission may deny cross-org writes on
      // some deployments.
      if (isFullyDeleted && dData.broadcasterOrgId && dData.status !== 'accepted') {
        try {
          const notifsRef = collection(this.db, getOrgCollectionPath(dData.broadcasterOrgId, 'deliveryNotifications'));
          const notifsSnap = await getDocs(query(notifsRef, where('deliveryId', '==', dSnap.id)));
          for (const nSnap of notifsSnap.docs) {
            try { await deleteDoc(nSnap.ref); } catch { /* best effort */ }
          }
        } catch { /* best effort */ }
      }
    }

    // Broadcaster / media-house campaign copies — reduce or delete.
    for (const broadcasterOrgId of Array.from(affectedBroadcasterOrgIds)) {
      try {
        const copyRef = doc(this.db, getOrgCollectionPath(broadcasterOrgId, 'jobs'), jobId);
        const copySnap = await getDoc(copyRef);
        if (!copySnap.exists()) continue;
        const copyData = copySnap.data();
        const copyStations: any[] = copyData.stations || [];
        const remaining = copyStations.filter((s: any) => !s?.id || !deletedIdSet.has(s.id));
        if (remaining.length === 0) {
          // Per user's Option 1: broadcaster / media-house copies are
          // per-delivery ephemeral. Empty shell has no purpose;
          // delete the whole campaign copy.
          await deleteDoc(copyRef);
          result.broadcasterCopiesDeleted.push(broadcasterOrgId);
        } else {
          await updateDoc(copyRef, {
            stations: remaining,
            stationCount: remaining.length,
            linkedCount: remaining.filter((s: any) => s.linkedTrackId).length,
          });
          result.broadcasterCopiesReduced.push(broadcasterOrgId);
        }
      } catch (err) {
        result.errors.push(`broadcaster copy ${broadcasterOrgId} cascade failed: ${(err as any)?.message || err}`);
      }
    }

    // Audiometa cleanup — for every affected org, delete audiometa
    // docs for trackIds that no station in ANY job in that org
    // references. The Cloud Function `deleteOrgTrackData` then scans
    // other orgs and cleans up storage when the last reference is
    // gone. Scoping to "any job in the org" (not just this campaign)
    // matches the user's requirement — prevents stale tracks from
    // auto-matching to new stations after a delete → re-upload with
    // the same code cycle.
    const audiometaCleanupOrgs = new Set<string>([sourceOrgId]);
    if (studioOrgId && studioOrgId !== sourceOrgId) audiometaCleanupOrgs.add(studioOrgId);
    if (agencyOrgId && agencyOrgId !== sourceOrgId) audiometaCleanupOrgs.add(agencyOrgId);
    for (const b of Array.from(affectedBroadcasterOrgIds)) audiometaCleanupOrgs.add(b);

    for (const orgId of Array.from(audiometaCleanupOrgs)) {
      try {
        // Collect every trackId referenced by ANY station in ANY job
        // in this org. (Deleted broadcaster copies won't show up in
        // this scan, which is correct — those tracks are eligible
        // for cleanup.)
        const jobsSnap = await getDocs(collection(this.db, getOrgCollectionPath(orgId, 'jobs')));
        const referencedTrackIds = new Set<string>();
        for (const jSnap of jobsSnap.docs) {
          const stations: any[] = jSnap.data()?.stations || [];
          for (const s of stations) {
            if (s?.linkedTrackId) referencedTrackIds.add(s.linkedTrackId);
          }
        }

        for (const trackId of Array.from(deletedTrackIds)) {
          if (referencedTrackIds.has(trackId)) continue; // still in use somewhere
          try {
            const audiometaRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
            const audiometaSnap = await getDoc(audiometaRef);
            if (!audiometaSnap.exists()) continue;
            // SAFETY — only delete audiometa docs that are marked as
            // copies (isCampaignCopy for studio/agency, isDeliveryTrack
            // for broadcaster). Source-owned uploads carry NEITHER
            // marker; deleting them would silently wipe the source
            // org's library when an agency (or the source themselves)
            // removes a station from a delivered campaign. The source
            // retains their asset store — if they want a stale track
            // gone, they can delete it manually from the library UI.
            //
            // Downstream orgs (studio / agency / broadcasters) only
            // ever carry audiometa via the campaign/delivery push
            // paths, so both marker checks catch every legitimate
            // "delete this copy" case there while also protecting any
            // uploads a downstream org happens to own.
            const audiometaData = audiometaSnap.data() as any;
            const isCopyDoc = audiometaData?.isCampaignCopy === true || audiometaData?.isDeliveryTrack === true;
            if (!isCopyDoc) continue;

            await deleteDoc(audiometaRef);
            result.audiometaDeleted++;
            // Cloud Function `deleteOrgTrackData` fires and:
            //   - cleans reviews + qcRecords subcollections
            //   - expires reviewCodes
            //   - cascades from masterCatalogue + publicCatalogue
            //     ONLY when the deleted doc is the canonical source
            //     (guarded by isCampaignCopy/isDeliveryTrack marker
            //     check in the function itself, added 2026-08-07)
            //   - scans OTHER orgs; if no other org references the
            //     trackId, deletes the audio files from storage
          } catch (err) {
            result.errors.push(`audiometa ${trackId} delete in ${orgId} failed: ${(err as any)?.message || err}`);
          }
        }
      } catch (err) {
        result.errors.push(`audiometa scan in ${orgId} failed: ${(err as any)?.message || err}`);
      }
    }

    return result;
  };

  /**
   * Recompute a source job's station-only delivery counters from its live
   * delivery records and absolute-set them on the job doc.
   *
   * Why this exists: the delivery flow writes `deliveryCount`
   * INCREMENTALLY (`existing + newDeliveries`) and never decrements, and
   * cascadeDeleteStations marks a withdrawn delivery `deleted` without
   * touching the counter. So after a re-deliver (Option A — a corrected
   * station is withdrawn from its stale delivery, then re-shipped) the
   * stored counter is transiently inflated until the next download makes
   * the stampDeliveryDownload CF absolute-set it. Calling this right after
   * the withdrawal makes the "X/Y downloaded" badge exact immediately.
   *
   * Counting rule MATCHES the CF's source aggregate exactly
   * (functions/index.js stampDeliveryDownload) so this value and the CF's
   * eventual heal agree — no flip-flop:
   *   - skip `rejected` / `deleted` (terminal) deliveries
   *   - skip `isMediaHouse` bundles (duplicate their children)
   *   - count only deliveries WITH a `broadcasterOrgId`
   * `downloadedDeliveryCount` is recomputed the same way (subset whose
   * `downloadStatus === 'complete'`) so numerator never exceeds the fresh
   * denominator. Best-effort — a failure just leaves the CF to heal later.
   */
  recomputeSourceDeliveryCount = async (
    sourceOrgId: string,
    jobId: string,
  ): Promise<void> => {
    try {
      const delSnap = await getDocs(
        collection(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId, 'deliveries'),
      );
      let deliveryCount = 0;
      let downloadedDeliveryCount = 0;
      for (const d of delSnap.docs) {
        const dd = d.data() || {};
        if (dd.status === 'rejected' || dd.status === 'deleted') continue;
        if (dd.isMediaHouse) continue;
        if (!dd.broadcasterOrgId) continue;
        deliveryCount++;
        if (dd.downloadStatus === 'complete') downloadedDeliveryCount++;
      }
      await updateDoc(
        doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId),
        { deliveryCount, downloadedDeliveryCount },
      );
    } catch (err) {
      console.warn('[recomputeSourceDeliveryCount] failed (best effort):', err);
    }
  };

  /**
   * Publish station-field updates to every broadcaster that received
   * this campaign. Fires when the source clicks the "Publish Updates"
   * CTA (visible when `hasPendingUpdates: true` on a delivered
   * campaign). Distinct from Deliver — no new delivery record, no
   * notification, no snapshot duplication. Patches the existing
   * delivery record snapshots + broadcaster job copies + broadcaster
   * audiometa in place, then clears the flag.
   *
   * Behaviour per delivered station whose id also exists on the source:
   *   1. For every delivery record whose stations[] contains that id,
   *      patch the entry in place with the current source fields
   *      (STATION_TRACKED_FIELDS + linkedTrackId).
   *   2. For every affected broadcaster, patch the same station entry
   *      in their /jobs/{jobId} copy (matched by id).
   *   3. If linkedTrackId changed: push the new track's audiometa to
   *      the broadcaster's audiometa collection (marked
   *      isDeliveryTrack) and cleanup the old track's audiometa via
   *      cleanupOrphanTrackCopies (isDeliveryTrack marker safety).
   *   4. Once all writes succeed, clear `hasPendingUpdates: false` on
   *      the source doc and mirror via syncCampaignCopies so studio /
   *      agency copies also see the flag flip.
   *
   * Skips stations that don't have a `deliveryStatus === 'delivered'`
   * marker — pending stations belong to the Deliver flow, not here.
   *
   * Idempotent — a second call after the first cleared the flag is a
   * no-op (no diffs found, nothing to write).
   */
  cascadeUpdateDeliveredStations = async (
    sourceOrgId: string,
    jobId: string,
  ): Promise<{
    deliveryRecordsUpdated: number;
    broadcasterCopiesUpdated: number;
    broadcastersAffected: string[];
    audiometaAdded: number;
    audiometaCleaned: number;
    stationsPatched: number;
    errors: string[];
  }> => {
    const result = {
      deliveryRecordsUpdated: 0,
      broadcasterCopiesUpdated: 0,
      broadcastersAffected: [] as string[],
      audiometaAdded: 0,
      audiometaCleaned: 0,
      stationsPatched: 0,
      errors: [] as string[],
    };

    // Same field set as computeCampaignDiff — station-level fields
    // that the user can meaningfully change and expect to see reflected
    // downstream. `linkedTrackId` is handled separately (triggers
    // audiometa side-effects).
    const PATCH_FIELDS = [
      'flightingCode', 'materialDeadline', 'flightingDate', 'language',
      'duration', 'contact', 'contactEmail', 'description', 'station',
      'mediaOwner', 'title', 'isLiveRead',
    ] as const;

    // 1. Read source job — the authoritative field values.
    const sourceJobRef = doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId);
    let sourceData: any = null;
    try {
      const snap = await getDoc(sourceJobRef);
      if (!snap.exists()) {
        result.errors.push(`Source job /organizations/${sourceOrgId}/jobs/${jobId} does not exist`);
        return result;
      }
      sourceData = snap.data() || {};
    } catch (err) {
      result.errors.push(`Source read failed: ${(err as any)?.message || err}`);
      return result;
    }
    const sourceStations: any[] = Array.isArray(sourceData.stations) ? sourceData.stations : [];
    const sourceById = new Map<string, any>();
    for (const s of sourceStations) {
      if (s?.id) sourceById.set(s.id, s);
    }
    if (sourceById.size === 0) {
      // Nothing to patch — clear the flag and exit so the CTA resets.
      try {
        await updateDoc(sourceJobRef, { hasPendingUpdates: false });
      } catch { /* best effort */ }
      return result;
    }

    // 2. Read every delivery record. For each, walk its stations
    //    snapshot; patch each station whose id matches a current source
    //    station AND whose field values differ. Track OLD trackIds (for
    //    cleanup) and NEW trackIds (for push) as we go, keyed by the
    //    broadcaster org they need to land in.
    let deliveriesSnap: any;
    try {
      deliveriesSnap = await getDocs(collection(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId, 'deliveries'));
    } catch (err) {
      result.errors.push(`Deliveries read failed: ${(err as any)?.message || err}`);
      return result;
    }

    const affectedBroadcasterOrgIds = new Set<string>();
    // trackId -> Set<broadcasterOrgId> — new tracks to push
    const newTracksPerBroadcaster = new Map<string, Set<string>>();
    // trackId -> Set<broadcasterOrgId> — old tracks to consider cleanup
    const oldTracksPerBroadcaster = new Map<string, Set<string>>();

    for (const dSnap of deliveriesSnap.docs) {
      const dData = dSnap.data();
      if (dData.status === 'deleted' || dData.status === 'rejected') continue;
      const bOrgId: string = dData.broadcasterOrgId || '';
      const dStations: any[] = Array.isArray(dData.stations) ? dData.stations : [];
      let touched = false;
      const updatedDeliveryStations = dStations.map((deliveryStation: any) => {
        if (deliveryStation?.deleted) return deliveryStation;
        const id = deliveryStation?.id;
        if (!id) return deliveryStation;
        const sourceStation = sourceById.get(id);
        if (!sourceStation) return deliveryStation; // station gone at source — handled by cascadeDeleteStations
        // Compute the patched entry — only overwrite fields whose
        // source value differs. Preserves any per-delivery metadata
        // (like the `deleted`/`deletedAt` flags on other stations).
        let patched = deliveryStation;
        let anyChange = false;
        for (const f of PATCH_FIELDS) {
          const sv = (sourceStation as any)[f];
          const dv = (deliveryStation as any)[f];
          // eslint-disable-next-line eqeqeq
          if (sv != dv) {
            if (patched === deliveryStation) patched = { ...deliveryStation };
            (patched as any)[f] = sv !== undefined ? sv : null;
            anyChange = true;
          }
        }
        // Link change — separate handling because it triggers audiometa
        // side-effects. Treat null / undefined / '' as equivalent.
        const oldLink = deliveryStation.linkedTrackId || null;
        const newLink = sourceStation.linkedTrackId || null;
        if (oldLink !== newLink) {
          if (patched === deliveryStation) patched = { ...deliveryStation };
          patched.linkedTrackId = newLink;
          anyChange = true;
          if (bOrgId) {
            if (oldLink) {
              if (!oldTracksPerBroadcaster.has(oldLink)) oldTracksPerBroadcaster.set(oldLink, new Set());
              oldTracksPerBroadcaster.get(oldLink)!.add(bOrgId);
            }
            if (newLink) {
              if (!newTracksPerBroadcaster.has(newLink)) newTracksPerBroadcaster.set(newLink, new Set());
              newTracksPerBroadcaster.get(newLink)!.add(bOrgId);
            }
          }
        }
        if (anyChange) {
          touched = true;
          result.stationsPatched++;
          if (bOrgId) affectedBroadcasterOrgIds.add(bOrgId);
        }
        return patched;
      });

      if (!touched) continue;
      try {
        await updateDoc(dSnap.ref, { stations: updatedDeliveryStations });
        result.deliveryRecordsUpdated++;
      } catch (err) {
        result.errors.push(`delivery ${dSnap.id} patch failed: ${(err as any)?.message || err}`);
      }

      // Public /deliveryCodes lookup carries the same stations
      // snapshot — keep it in sync so external links show the current
      // field values.
      if (dData.deliveryCode) {
        try {
          await updateDoc(doc(this.db, 'deliveryCodes', dData.deliveryCode), {
            stations: updatedDeliveryStations,
          });
        } catch { /* best effort */ }
      }
    }

    // 3. Patch every affected broadcaster's job copy — station-by-station.
    for (const bOrgId of Array.from(affectedBroadcasterOrgIds)) {
      try {
        const bJobRef = doc(this.db, getOrgCollectionPath(bOrgId, 'jobs'), jobId);
        const bJobSnap = await getDoc(bJobRef);
        if (!bJobSnap.exists()) continue;
        const bData = bJobSnap.data() as any;
        const bStations: any[] = Array.isArray(bData.stations) ? bData.stations : [];
        let touched = false;
        const patchedStations = bStations.map((broadcasterStation: any) => {
          const id = broadcasterStation?.id;
          if (!id) return broadcasterStation;
          const sourceStation = sourceById.get(id);
          if (!sourceStation) return broadcasterStation;
          let patched = broadcasterStation;
          for (const f of PATCH_FIELDS) {
            const sv = (sourceStation as any)[f];
            const dv = (broadcasterStation as any)[f];
            // eslint-disable-next-line eqeqeq
            if (sv != dv) {
              if (patched === broadcasterStation) patched = { ...broadcasterStation };
              (patched as any)[f] = sv !== undefined ? sv : null;
              touched = true;
            }
          }
          const oldLink = broadcasterStation.linkedTrackId || null;
          const newLink = sourceStation.linkedTrackId || null;
          if (oldLink !== newLink) {
            if (patched === broadcasterStation) patched = { ...broadcasterStation };
            patched.linkedTrackId = newLink;
            touched = true;
          }
          return patched;
        });
        if (!touched) continue;
        await updateDoc(bJobRef, {
          stations: patchedStations,
          linkedCount: patchedStations.filter((s: any) => s.linkedTrackId).length,
        });
        result.broadcasterCopiesUpdated++;
        result.broadcastersAffected.push(bOrgId);
      } catch (err) {
        result.errors.push(`broadcaster ${bOrgId} job copy patch failed: ${(err as any)?.message || err}`);
      }
    }

    // 4. Push NEW-track audiometa to affected broadcasters. Read the
    //    source's audiometa doc for each new trackId and write to the
    //    broadcaster's audiometa with isDeliveryTrack marker. Mirrors
    //    what copyDeliveryToBroadcaster does at delivery time so the
    //    broadcaster's library ends up in the same shape as if the
    //    updated track had been in the original delivery.
    const now = Date.now();
    for (const [trackId, bOrgIds] of Array.from(newTracksPerBroadcaster.entries())) {
      try {
        const srcRef = doc(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), trackId);
        const srcSnap = await getDoc(srcRef);
        if (!srcSnap.exists()) continue;
        const src = srcSnap.data() as any;
        for (const bOrgId of Array.from(bOrgIds)) {
          try {
            await setDoc(doc(this.db, getOrgCollectionPath(bOrgId, AUDIOMETAPATH), trackId), {
              trackName: src.trackName || '',
              tags: src.tags || [],
              language: src.language || [],
              client: src.client || sourceData.client || null,
              agency: src.agency || null,
              studio: src.studio || null,
              mp3Files: src.mp3Files || [],
              wavFiles: src.wavFiles || [],
              defaultMp3Id: src.defaultMp3Id || null,
              approval_status: src.approval_status || '',
              qc_status: src.qc_status || '',
              is_broadcast_master: src.is_broadcast_master || false,
              broadcast_master_file: src.broadcast_master_file || null,
              jobId,
              jobCampaign: sourceData.campaign || '',
              jobTitle: sourceData.campaign || '',
              isDeliveryTrack: true,
              sourceOrgId,
              sourceTrackId: trackId,
              sourceOrgName: sourceData.sourceOrgName || '',
              sourceJobId: jobId,
              createdAt: now,
              deliveredAt: now,
              publishedUpdateAt: now,
            });
            result.audiometaAdded++;
          } catch (err) {
            result.errors.push(`audiometa push for ${trackId} → ${bOrgId} failed: ${(err as any)?.message || err}`);
          }
        }
      } catch (err) {
        result.errors.push(`audiometa read for ${trackId} failed: ${(err as any)?.message || err}`);
      }
    }

    // 5. Cleanup OLD trackIds on the broadcasters that no longer
    //    reference them. Uses the same helper we use elsewhere — safety
    //    filter checks isDeliveryTrack marker so the broadcaster's own
    //    uploads (no marker) are never touched. The "still referenced
    //    by any job in that org?" check catches the common case where
    //    the broadcaster has the same track linked to another campaign
    //    and shouldn't lose it.
    for (const [trackId, bOrgIds] of Array.from(oldTracksPerBroadcaster.entries())) {
      for (const bOrgId of Array.from(bOrgIds)) {
        try {
          const cleaned = await this.cleanupOrphanTrackCopies(sourceOrgId, trackId, [bOrgId], {
            allowedMarkers: ['isDeliveryTrack'],
          });
          result.audiometaCleaned += cleaned.deleted.length;
        } catch (err) {
          result.errors.push(`old audiometa cleanup ${trackId} in ${bOrgId} failed: ${(err as any)?.message || err}`);
        }
      }
    }

    // 6. Clear the pending-updates flag on source and mirror to studio /
    //    agency copies via syncCampaignCopies. Even if the patch loop
    //    above found nothing to touch (idempotent no-op), clearing here
    //    resets the CTA so the button doesn't stay stuck on Publish
    //    Updates.
    try {
      await updateDoc(sourceJobRef, { hasPendingUpdates: false });
      try {
        const freshSnap = await getDoc(sourceJobRef);
        if (freshSnap.exists()) {
          await this.syncCampaignCopies(sourceOrgId, jobId, freshSnap.data() as any);
        }
      } catch (err) {
        console.warn('[cascadeUpdateDeliveredStations] flag-clear sync failed (best effort):', err);
      }
    } catch (err) {
      result.errors.push(`hasPendingUpdates clear failed: ${(err as any)?.message || err}`);
    }

    return result;
  };

  /**
   * Copy a single delivery into a broadcaster's org (audiometa + jobs).
   *
   * Historically this ran inside `acceptDelivery` — the broadcaster had
   * to click Accept before their library / campaigns got populated.
   * The new "auto-copy at delivery" flow calls this directly from
   * `shareJobWithBroadcasters` immediately after the delivery record
   * lands, so the broadcaster's org is up to date the moment the studio
   * hits Deliver. The bell notification becomes informational only;
   * Reject is what triggers cleanup (see `rejectDelivery`).
   *
   * The function is idempotent:
   *   - Audiometa docs use setDoc keyed on the SOURCE trackId, so a
   *     second call overwrites with the same content.
   *   - Broadcaster's job doc uses runTransaction merge — a follow-up
   *     partial delivery (e.g. adds 2 more stations to a job the
   *     broadcaster already received 5 for) appends the new stations
   *     without wiping the existing ones. Races between parallel
   *     shareJobWithBroadcasters + operator-Accept converge cleanly.
   *
   * Rapid Upload (`deliveryData.isRapidUpload`) is NOT auto-copied by
   * this helper — those come from unverified external partners and
   * still require explicit broadcaster acceptance via acceptDelivery's
   * rapid-upload branch.
   *
   * Filters out `deleted: true` stations (Phase 2 cascade audit trail)
   * so the broadcaster's copy only contains live rows.
   */
  copyDeliveryToBroadcaster = async (
    broadcasterOrgId: string,
    sourceOrgId: string,
    jobId: string,
    deliveryId: string,
    deliveryCode: string,
    deliveryData: any,
  ): Promise<void> => {
    if (!broadcasterOrgId) return; // link-only recipient, nothing to copy
    if (deliveryData?.isRapidUpload) return; // still explicit-accept

    const now = Date.now();
    const userId = this.auth.currentUser?.uid || '';

    // 1. Copy each unique linkedTrackId's audiometa into the
    //    broadcaster's org. Uses SOURCE trackId as the doc id so the
    //    broadcaster's copy can be identity-mapped back to the source.
    const stations = (deliveryData.stations || []).filter((s: any) => !s?.deleted);
    const trackIdsCopied = new Set<string>();
    for (const station of stations) {
      if (!station.linkedTrackId || trackIdsCopied.has(station.linkedTrackId)) continue;
      try {
        const sourceTrackRef = doc(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), station.linkedTrackId);
        const sourceTrackDoc = await getDoc(sourceTrackRef);
        if (!sourceTrackDoc.exists()) continue;
        const sourceTrack = sourceTrackDoc.data();
        const broadcasterTrackRef = doc(
          this.db,
          getOrgCollectionPath(broadcasterOrgId, AUDIOMETAPATH),
          station.linkedTrackId,
        );
        await setDoc(broadcasterTrackRef, {
          trackName: sourceTrack.trackName || '',
          tags: sourceTrack.tags || [],
          language: sourceTrack.language || [],
          client: sourceTrack.client || deliveryData.client || null,
          agency: sourceTrack.agency || null,
          studio: sourceTrack.studio || null,
          mp3Files: sourceTrack.mp3Files || [],
          wavFiles: sourceTrack.wavFiles || [],
          defaultMp3Id: sourceTrack.defaultMp3Id || null,
          approval_status: sourceTrack.approval_status || '',
          qc_status: sourceTrack.qc_status || '',
          is_broadcast_master: sourceTrack.is_broadcast_master || false,
          broadcast_master_file: sourceTrack.broadcast_master_file || null,
          jobId,
          jobCampaign: deliveryData.campaign || '',
          jobTitle: deliveryData.campaign || '',
          isDeliveryTrack: true,
          sourceOrgId,
          sourceTrackId: station.linkedTrackId,
          sourceOrgName: deliveryData.sourceOrgName || '',
          deliveryCode,
          deliveryId,
          sourceJobId: jobId,
          createdAt: now,
          deliveredAt: now,
        });
        trackIdsCopied.add(station.linkedTrackId);
      } catch (err) {
        console.error(`[copyDeliveryToBroadcaster] track ${station.linkedTrackId} copy failed:`, err);
      }
    }

    // 2. Create / merge the broadcaster's job doc — INSIDE A TRANSACTION.
    //    Prevents lost writes when two parallel deliveries for the same
    //    jobId land concurrently (e.g. multi-region campaigns delivered
    //    in a single share call): both reads see the same base state,
    //    both merge, last write wins → stations vanish. runTransaction
    //    detects the write conflict and auto-retries the loser.
    const broadcasterJobRef = doc(
      this.db,
      getOrgCollectionPath(broadcasterOrgId, 'jobs'),
      jobId,
    );
    const newStations = stations.map((s: any) => ({ ...s, deliveryStatus: 'delivered' }));

    await runTransaction(this.db, async (tx) => {
      const existingSnap = await tx.get(broadcasterJobRef);
      const existingData = existingSnap.exists() ? existingSnap.data() : null;
      const existingStations: any[] = (existingData && Array.isArray(existingData.stations))
        ? existingData.stations
        : [];

      // Dedupe by station.id — newer snapshot wins per id. Legacy stations
      // without ids fall through as append-only (matches pre-Phase-1 behaviour).
      const mergedById = new Map<string, any>();
      const appended: any[] = [];
      for (const s of existingStations) {
        if (s?.id) mergedById.set(s.id, s);
        else appended.push(s);
      }
      for (const s of newStations) {
        if (s?.id) mergedById.set(s.id, s);
        else appended.push(s);
      }
      const mergedStations = [...Array.from(mergedById.values()), ...appended];

      const isFreshCopy = !existingSnap.exists();
      const prevDeliveryCodes: string[] = existingData && Array.isArray(existingData.deliveryCodes)
        ? existingData.deliveryCodes
        : (existingData && existingData.deliveryCode ? [existingData.deliveryCode] : []);

      // MERGE not overwrite — this helper is called from BOTH the
      // delivery-time flow (creating the copy) AND the acceptDelivery
      // "backup copy" path (which runs on every Dismiss / Accept). A
      // full overwrite would wipe fields written by other systems
      // between calls — notably `deliveryCount` and
      // `downloadedDeliveryCount` which the stampDeliveryDownload CF
      // writes to reflect download progress. Merge preserves those
      // while still updating everything in this payload.
      tx.set(broadcasterJobRef, {
        campaign: deliveryData.campaign || '',
        client: deliveryData.client || '',
        period: deliveryData.period || '',
        stations: mergedStations,
        stationCount: mergedStations.length,
        linkedCount: mergedStations.filter((s: any) => s.linkedTrackId).length,
        isDeliveredJob: true,
        sourceOrgId,
        sourceOrgName: deliveryData.sourceOrgName || '',
        sourceJobId: jobId,
        deliveryCode,
        deliveryId,
        deliveryCodes: Array.from(new Set([...prevDeliveryCodes, deliveryCode])),
        // Preserve first-delivery timestamps on subsequent partial deliveries.
        // deliveredAt = when the FIRST delivery for this job landed.
        // acceptedAt is now "auto-copied at delivery time" — still stamp it
        // on first copy for continuity with old records.
        uploadedAt: isFreshCopy ? now : (existingData?.uploadedAt || now),
        deliveredAt: isFreshCopy ? now : (existingData?.deliveredAt || now),
        acceptedAt: isFreshCopy ? now : (existingData?.acceptedAt || now),
        acceptedBy: isFreshCopy ? userId : (existingData?.acceptedBy || userId),
        lastAcceptedAt: now,
        lastAcceptedBy: userId,
        // status stays 'accepted' for backwards compat — downstream UI
        // reads this field to distinguish delivered vs draft jobs.
        status: 'accepted',
      }, { merge: true });
    });
  };

  /**
   * Share a job with broadcasters.
   * Groups stations by broadcaster org, creates delivery records,
   * generates delivery codes, and (for in-app broadcasters) auto-copies
   * tracks + job to their org.
   */
  shareJobWithBroadcasters = async (
    orgId: string,
    jobId: string,
    job: any,
    /** OPTIONAL subset filter. When omitted, every station on the job
     *  is eligible for delivery (legacy behaviour — unchanged). When
     *  provided, only stations whose `id` appears in this set are
     *  grouped, delivered, and flipped to `deliveryStatus: 'delivered'`
     *  on the source job. Enables the partial re-deliver flow: after
     *  editing a delivered campaign to add new stations, JobDetail
     *  passes the ids of the pending-only subset so already-delivered
     *  stations are neither re-shipped nor re-notified. */
    stationIdsToDeliver?: Set<string>,
  ): Promise<{
    deliveries: {
      broadcasterOrgId: string | null;
      broadcasterOrgName: string;
      deliveryCode: string;
      deliveryId: string;
      stations: any[];
      tracksCopied: number;
    }[];
    errors: string[];
  }> => {
    const errors: string[] = [];
    const deliveries: any[] = [];

    // Group stations by delivery target — deliver to both media owner AND broadcaster if both have linked orgs
    const broadcasterGroups = new Map<string, { orgId: string | null; orgName: string; stations: any[]; isMediaHouse?: boolean }>();

    // Filter stations by the optional id set. Legacy path (no filter)
    // = every station goes through, exactly as before.
    const eligibleStations = stationIdsToDeliver
      ? (job.stations as any[]).filter((s: any) => s?.id && stationIdsToDeliver.has(s.id))
      : (job.stations as any[]);

    for (const station of eligibleStations) {
      // Add to media owner group if they have a linked org
      if (station.mediaOwnerOrgId) {
        const moKey = `mo_${station.mediaOwnerOrgId}`;
        if (!broadcasterGroups.has(moKey)) {
          broadcasterGroups.set(moKey, {
            orgId: station.mediaOwnerOrgId,
            orgName: station.mediaOwnerOrgName || station.mediaOwner || '',
            stations: [],
            isMediaHouse: true,
          });
        }
        broadcasterGroups.get(moKey)!.stations.push(station);
      }

      // Add to broadcaster/station group (linked org or by name)
      const stationKey = station.stationOrgId || station.station;
      if (stationKey) {
        if (!broadcasterGroups.has(stationKey)) {
          broadcasterGroups.set(stationKey, {
            orgId: station.stationOrgId || null,
            orgName: station.stationOrgName || station.station || '',
            stations: [],
          });
        }
        broadcasterGroups.get(stationKey)!.stations.push(station);
      } else if (!station.mediaOwnerOrgId) {
        // No media owner org and no station — group by media owner name as fallback
        const fallbackKey = station.mediaOwner || 'unknown';
        if (!broadcasterGroups.has(fallbackKey)) {
          broadcasterGroups.set(fallbackKey, {
            orgId: null,
            orgName: station.mediaOwner || 'Unknown',
            stations: [],
          });
        }
        broadcasterGroups.get(fallbackKey)!.stations.push(station);
      }
    }

    // Get source org name for snapshots
    let sourceOrgName = '';
    try {
      const orgDoc = await getDoc(doc(this.db, 'organizations', orgId));
      sourceOrgName = orgDoc.data()?.name || '';
    } catch { /* ignore */ }

    // Process each broadcaster group
    const groupEntries = Array.from(broadcasterGroups.values());
    for (const group of groupEntries) {
      try {
        // Generate delivery code (always, even for in-app deliveries)
        const { generateClaimCode } = await import('../../types/license');
        const deliveryCode = generateClaimCode();

        // Create delivery record in source org's job
        const deliveriesRef = collection(
          this.db,
          getOrgCollectionPath(orgId, 'jobs'),
          jobId,
          'deliveries'
        );
        const newDeliveryRef = doc(deliveriesRef);

        // Collect audio file snapshots for each station's linked track
        const stationsWithAudio = [];
        for (const station of group.stations) {
          let audioSnapshot: any = {};
          if (station.linkedTrackId) {
            try {
              const trackDoc = await getDoc(
                doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), station.linkedTrackId)
              );
              if (trackDoc.exists()) {
                const td = trackDoc.data();
                audioSnapshot = {
                  trackName: td.trackName || '',
                  mp3Files: td.mp3Files || [],
                  wavFiles: td.wavFiles || [],
                  defaultMp3Id: td.defaultMp3Id || null,
                  language: td.language || [],
                  tags: td.tags || [],
                  approval_status: td.approval_status || '',
                  qc_status: td.qc_status || '',
                  is_broadcast_master: td.is_broadcast_master || false,
                  broadcast_master_file: td.broadcast_master_file || null,
                };
              }
            } catch { /* ignore */ }
          }
          stationsWithAudio.push({ ...station, ...audioSnapshot });
        }

        // Count unique linked tracks
        const uniqueTrackIds = new Set<string>();
        for (const station of group.stations) {
          if (station.linkedTrackId) uniqueTrackIds.add(station.linkedTrackId);
        }

        const deliveryData = {
          id: newDeliveryRef.id,
          broadcasterOrgId: group.orgId,
          broadcasterOrgName: group.orgName,
          isMediaHouse: group.isMediaHouse || false,
          deliveryCode,
          stations: stationsWithAudio,
          trackCount: uniqueTrackIds.size,
          status: 'delivered', // delivered | acknowledged
          deliveredAt: Date.now(),
          deliveredBy: this.auth.currentUser?.uid || '',
          deliveredByName: this.auth.currentUser?.displayName || this.auth.currentUser?.email || '',
          acknowledgedAt: null,
          acknowledgedBy: null,
          // Job-level header snapshot. These four fields MUST be on
          // the deliveryData object because it's passed to
          // copyDeliveryToBroadcaster below — that helper reads them
          // into the broadcaster's job copy. Without them the copy
          // ends up with client/campaign/period/sourceOrgName all set
          // to '' (see Phase 2 auto-copy bug: pre-fix broadcaster
          // copies had blank campaign headers). The public lookup
          // (setDoc lookupRef below) sets these same values too so
          // the delivery record and the lookup stay in sync.
          campaign: job.campaign || '',
          client: job.client || '',
          period: job.period || '',
          sourceOrgName,
        };

        await setDoc(newDeliveryRef, deliveryData);

        // Create public lookup for external delivery link
        const lookupRef = doc(this.db, 'deliveryCodes', deliveryCode);
        await setDoc(lookupRef, {
          deliveryCode,
          orgId,
          jobId,
          deliveryId: newDeliveryRef.id,
          broadcasterOrgId: group.orgId,
          broadcasterOrgName: group.orgName,
          status: 'delivered',
          createdAt: Date.now(),
          // Job snapshots
          campaign: job.campaign || '',
          client: job.client || '',
          period: job.period || '',
          sourceOrgName,
          // Station + audio snapshots
          stations: stationsWithAudio,
        });

        // If broadcaster has an org, send notification AND auto-copy the
        // delivery into their org's library + campaigns. Under the new
        // auto-copy model the notification is purely informational —
        // Reject is what triggers cleanup, not Accept/Dismiss.
        if (group.orgId) {
          const notifRef = doc(collection(
            this.db,
            getOrgCollectionPath(group.orgId!, 'deliveryNotifications')
          ));
          await setDoc(notifRef, {
            id: notifRef.id,
            sourceOrgId: orgId,
            sourceOrgName,
            jobId,
            deliveryId: newDeliveryRef.id,
            deliveryCode,
            campaign: job.campaign || '',
            client: typeof job.client === 'object' ? job.client?.label : job.client || '',
            period: job.period || '',
            stationCount: group.stations.length,
            trackCount: uniqueTrackIds.size,
            deliveredAt: Date.now(),
            deliveredByName: this.auth.currentUser?.displayName || this.auth.currentUser?.email || '',
            status: 'pending', // pending | accepted | rejected
          });

          // Auto-copy audiometa + jobs into broadcaster's org. Best-
          // effort: a copy failure gets logged and pushed into `errors`
          // but doesn't abort the surrounding delivery — the delivery
          // record + notification are already written, so the studio
          // can retry via a manual accept from the broadcaster side if
          // needed. Silent failure would be worse than a surfaced one.
          try {
            await this.copyDeliveryToBroadcaster(
              group.orgId!,
              orgId,
              jobId,
              newDeliveryRef.id,
              deliveryCode,
              deliveryData,
            );
          } catch (err: any) {
            const msg = `Auto-copy to ${group.orgName} failed: ${err?.message || err}`;
            errors.push(msg);
            console.error('[shareJobWithBroadcasters]', msg);
          }
        }

        deliveries.push({
          broadcasterOrgId: group.orgId,
          broadcasterOrgName: group.orgName,
          deliveryCode,
          deliveryId: newDeliveryRef.id,
          stations: group.stations,
          trackCount: uniqueTrackIds.size,
          // Marker used by the deliveryCount aggregate below to
          // exclude MH-type deliveries from the "how many recipients
          // must download" count. See rationale at the deliveryCount
          // write further down.
          isMediaHouse: !!group.isMediaHouse,
        });
      } catch (err: any) {
        errors.push(`Failed to deliver to ${group.orgName}: ${err.message}`);
        console.error(`Delivery error for ${group.orgName}:`, err);
      }
    }

    // Update source job status + flip the just-delivered stations'
    // deliveryStatus. Two important properties:
    //   * We compute the delivered station id set from what ACTUALLY
    //     ended up in a delivery record — not just what was passed in.
    //     Failed deliveries (network error, etc.) won't get flipped.
    //   * We re-read the source job so we edit the current stations
    //     array — the caller may have picked up updates between when
    //     the share started and now.
    try {
      const deliveredStationIds = new Set<string>();
      for (const d of deliveries) {
        for (const s of (d.stations || [])) {
          if (s?.id) deliveredStationIds.add(s.id);
        }
      }
      const jobRef = doc(this.db, getOrgCollectionPath(orgId, 'jobs'), jobId);
      const jobSnap = await getDoc(jobRef);
      const currentStations = (jobSnap.exists() && Array.isArray(jobSnap.data().stations))
        ? jobSnap.data().stations
        : (job.stations || []);
      // Legacy safety net: if a source station has no id (pre-id
      // introduction) we can't match it, so we leave its status
      // untouched. New stations always have ids via the persist path.
      const updatedStations = currentStations.map((s: any) => {
        if (s?.id && deliveredStationIds.has(s.id)) {
          return { ...s, deliveryStatus: 'delivered' };
        }
        return s;
      });
      // Update deliveryCount aggregate — the denominator for the
      // "3/8 downloaded" pill on the Jobs list + Home dashboard. Read
      // then write in-place; the stampDeliveryDownload Cloud Function
      // maintains downloadedDeliveryCount on the other side.
      //
      // STATION-ONLY counting: when a station has a linked media
      // house, TWO delivery records are created (the station's own +
      // the MH bundle). Both grant access to the same file, so
      // operationally there's only ONE download obligation per
      // station. Excluding MH deliveries from the count keeps the
      // "X/Y downloaded" badge honest — a job with 1 station under
      // 1 MH shows 1/1 not 1/2. The CF's final propagation pass
      // recomputes both fields from live delivery state on every
      // download, so legacy jobs written before this filter existed
      // heal automatically on their first download.
      const existingDeliveryCount = (jobSnap.exists() && Number(jobSnap.data().deliveryCount)) || 0;
      const newDeliveries = deliveries.filter((d: any) => !d.isMediaHouse).length;
      await updateDoc(jobRef, {
        status: 'delivered',
        stations: updatedStations,
        // Preserve the FIRST-delivered timestamp for legacy reporting;
        // add a "last delivered" one so we always know the newest
        // partial-delivery time.
        deliveredAt: (jobSnap.exists() && jobSnap.data().deliveredAt) ? jobSnap.data().deliveredAt : Date.now(),
        lastDeliveredAt: Date.now(),
        deliveredBy: this.auth.currentUser?.uid || '',
        deliveryCount: existingDeliveryCount + newDeliveries,
      });

      // Propagate the fresh status + stations to studio / agency campaign
      // copies so their Jobs page badges stay in sync. Without this,
      // copies keep whatever status they had at copy-creation time
      // ('draft' or 'active') even though the source is now 'delivered'.
      // Best-effort — a failure here doesn't fail the delivery itself,
      // but the copies would drift and need a separate heal.
      try {
        const freshSnap = await getDoc(jobRef);
        if (freshSnap.exists()) {
          await this.syncCampaignCopies(orgId, jobId, freshSnap.data() as any);
        }
      } catch (err) {
        console.warn('[shareJobWithBroadcasters] campaign-copy status sync failed (best effort)', err);
      }
    } catch (err) {
      // Keep the error visible so operators notice if the flip
      // silently fails — a swallowed failure here would break the
      // pending/delivered gate on subsequent partial re-deliveries.
      console.error('[shareJobWithBroadcasters] source status flip failed', err);
    }

    // Build + upload delivery report PDF, then write the studio's own
    // deliveryConfirmations notification doc. Triggers the Cloud Function
    // which attaches the PDF to a confirmation email to the studio's
    // email-opted-in members. Best-effort — failure here doesn't fail the
    // delivery itself (deliveries already exist on the broadcaster side).
    if (deliveries.length > 0) {
      try {
        const clientLabel = typeof job.client === 'object'
          ? (job.client?.label || job.client?.value || '')
          : (job.client || '');
        const reportDeliveries = deliveries.map((d: any) => ({
          deliveryId: d.deliveryId,
          deliveryCode: d.deliveryCode,
          broadcasterOrgName: d.broadcasterOrgName,
          broadcasterOrgId: d.broadcasterOrgId,
          // At delivery time the broadcaster hasn't yet accepted/rejected, so
          // "delivered" is the accurate status to show on the confirmation
          // report. Using "pending" here confused clients into thinking the
          // delivery itself was pending.
          status: 'delivered',
          stations: d.stations || [],
          stationCount: (d.stations || []).length,
          trackCount: d.trackCount || 0,
          createdAt: Date.now(),
        }));
        const ctx = {
          job: { ...job, id: jobId },
          deliveries: reportDeliveries,
          sourceOrgName,
        };
        const pdfBlob = buildDeliveryReportPdfBlob(ctx as any);
        const filename = deliveryReportFilename(ctx as any);
        const reportStoragePath = `delivery-reports/${orgId}/${jobId}/${Date.now()}_${filename}`;
        await uploadBytes(
          storageRef(this.storage, reportStoragePath),
          pdfBlob,
          { contentType: 'application/pdf' },
        );

        const broadcasterNames = deliveries.map((d: any) => d.broadcasterOrgName).filter(Boolean);
        const totalStations = reportDeliveries.reduce((s, d) => s + d.stationCount, 0);
        const totalTracks = reportDeliveries.reduce((s, d) => s + d.trackCount, 0);

        const confirmRef = doc(collection(
          this.db,
          getOrgCollectionPath(orgId, 'deliveryConfirmations'),
        ));
        await setDoc(confirmRef, {
          id: confirmRef.id,
          jobId,
          campaign: job.campaign || '',
          client: clientLabel,
          period: job.period || '',
          broadcasterCount: deliveries.length,
          broadcasterNames,
          stationCount: totalStations,
          trackCount: totalTracks,
          deliveredAt: Date.now(),
          deliveredBy: this.auth.currentUser?.uid || '',
          deliveredByName: this.auth.currentUser?.displayName || this.auth.currentUser?.email || '',
          reportStoragePath,
          reportFilename: filename,
          dismissed: false,
        });
      } catch (err) {
        console.error('Failed to generate / upload delivery confirmation report:', err);
        errors.push('Delivery report generation failed — confirmation email may not include the attachment.');
      }
    }

    return { deliveries, errors };
  };

  /**
   * Validate a delivery code for the external delivery page
   */
  validateDeliveryCode = async (
    deliveryCode: string
  ): Promise<{ valid: boolean; data?: any; error?: string }> => {
    try {
      const lookupRef = doc(this.db, 'deliveryCodes', deliveryCode);
      const lookupDoc = await getDoc(lookupRef);

      if (!lookupDoc.exists()) {
        return { valid: false, error: 'Invalid delivery link' };
      }

      const data = lookupDoc.data();
      return { valid: true, data };
    } catch (error) {
      console.error('Error validating delivery code:', error);
      return { valid: false, error: 'Failed to validate delivery link' };
    }
  };

  /**
   * Subscribe to delivery notifications for a broadcaster org
   */
  subscribeDeliveryNotificationsForOrg = (
    orgId: string,
    callback: (notifications: any[]) => void
  ) => {
    const notifsRef = collection(
      this.db,
      getOrgCollectionPath(orgId, 'deliveryNotifications')
    );
    const q = query(notifsRef, orderBy('deliveredAt', 'desc'));

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(notifications);
    });
  };

  /**
   * Subscribe to delivery rejection notifications for a studio org
   */
  subscribeDeliveryRejectionsForOrg = (
    orgId: string,
    callback: (rejections: any[]) => void
  ) => {
    const refsCol = collection(
      this.db,
      getOrgCollectionPath(orgId, 'deliveryRejections')
    );
    const q = query(refsCol, orderBy('rejectedAt', 'desc'));

    return onSnapshot(q, (snapshot) => {
      const rejections = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(rejections);
    });
  };

  /**
   * Dismiss a delivery rejection notification
   */
  dismissDeliveryRejection = async (orgId: string, notificationId: string): Promise<boolean> => {
    try {
      const notifRef = doc(this.db, getOrgCollectionPath(orgId, 'deliveryRejections'), notificationId);
      await deleteDoc(notifRef);
      return true;
    } catch (error) {
      console.error('Error dismissing delivery rejection:', error);
      return false;
    }
  };

  /**
   * Accept a delivery — copy tracks and job to broadcaster's org
   */
  acceptDelivery = async (
    broadcasterOrgId: string,
    notificationId: string,
    sourceOrgId: string,
    jobId: string,
    deliveryId: string,
    deliveryCode: string,
  ): Promise<boolean> => {
    try {
      const now = Date.now();
      const userId = this.auth.currentUser?.uid || '';
      const userName = this.auth.currentUser?.displayName || this.auth.currentUser?.email || '';

      // 1. Get the delivery record to access station/track data
      const lookupRef = doc(this.db, 'deliveryCodes', deliveryCode);
      const lookupDoc = await getDoc(lookupRef);
      const deliveryData = lookupDoc.exists() ? lookupDoc.data() : null;

      if (!deliveryData) {
        console.error('Delivery lookup not found for code:', deliveryCode);
        return false;
      }

      // Fast-path: delivery already accepted (e.g. user clicked
      // Accept twice, or a stale notification lingered after another
      // client already processed it). Silently doing the full flow
      // again is safe (merge is idempotent per station id) but
      // wasteful, and clean up the notification so the modal
      // reflects reality.
      if (deliveryData.status === 'accepted') {
        try {
          await deleteDoc(doc(this.db, getOrgCollectionPath(broadcasterOrgId, 'deliveryNotifications'), notificationId));
        } catch { /* best effort */ }
        console.warn('[acceptDelivery] delivery already accepted — cleared stale notification', deliveryCode);
        return true;
      }

      // Fast-path: entire delivery was cascade-deleted post-share
      // (Phase 2). Nothing to accept — clear the notification and
      // log so operators can see it happened.
      if (deliveryData.status === 'deleted') {
        try {
          await deleteDoc(doc(this.db, getOrgCollectionPath(broadcasterOrgId, 'deliveryNotifications'), notificationId));
        } catch { /* best effort */ }
        console.warn('[acceptDelivery] delivery was deleted at source — cleared notification', deliveryCode);
        return true;
      }

      // RAPID UPLOAD branch: broadcaster is the "source" org (they received from an external partner)
      if (deliveryData.isRapidUpload) {
        const rapidStations = deliveryData.stations || [];

        // Get broadcaster org name for catalogue entries
        let broadcasterOrgName = '';
        try {
          const bOrgDoc = await getDoc(doc(this.db, 'organizations', broadcasterOrgId));
          broadcasterOrgName = bOrgDoc.data()?.name || '';
        } catch { /* ignore */ }

        for (const station of rapidStations) {
          try {
            // Use the pre-generated trackId (matches storage folder name).
            // Falls back to a new ID for legacy uploads that don't have it.
            const newTrackId = station.trackId || doc(collection(this.db, getOrgCollectionPath(broadcasterOrgId, AUDIOMETAPATH))).id;
            const broadcasterTrackRef = doc(this.db, getOrgCollectionPath(broadcasterOrgId, AUDIOMETAPATH), newTrackId);
            const mp3Files = (station.mp3Files || []).map((f: any) => ({ ...f }));
            const wavFiles = (station.wavFiles || []).map((f: any) => ({ ...f }));
            const defaultMp3Id = mp3Files[0]?.storageRef || null;
            const trackName = station.trackName || station.flightingCode || 'Rapid Upload';

            const trackData = {
              audioMetaRef: newTrackId,
              trackName,
              tags: [],
              language: [],
              client: null,
              agency: null,
              mp3Files,
              wavFiles,
              defaultMp3Id,
              approval_status: '',
              qc_status: '',
              is_broadcast_master: false,
              broadcast_master_file: null,
              jobCampaign: deliveryData.campaign || '',
              jobTitle: deliveryData.campaign || '',
              fileVersion: 1,
              qcVersion: 1,
              isRapidUploadTrack: true,
              rapidUploadFrom: deliveryData.sourceOrgName || '',
              deliveryCode,
              createdAt: now,
              deliveredAt: now,
            };

            // 1. Save to broadcaster's audiometa
            await setDoc(broadcasterTrackRef, trackData);

            // 2. Publish to master catalogue (broadcaster is effectively the source here)
            try {
              await this.publishToMasterCatalogue(broadcasterOrgId, broadcasterOrgName, newTrackId, trackData);
            } catch { /* best effort */ }

            // [DISABLED 2026-07-09] — public catalogue is now driven by the
            // per-track "Make Public" toggle in the library (agency + master
            // orgs only). Broadcaster acceptance no longer auto-publishes.
            // Kept commented for future revival — do not delete.
            // 3. Copy to public catalogue (accepted by broadcaster = available to platform)
            // try {
            //   const masterRef = doc(this.db, MASTER_CATALOGUEPATH, newTrackId);
            //   const masterSnap = await getDoc(masterRef);
            //   if (masterSnap.exists()) {
            //     const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, newTrackId);
            //     await setDoc(pubRef, {
            //       ...masterSnap.data(),
            //       acceptedByBroadcaster: true,
            //       acceptedAt: now,
            //       acceptedByOrgId: broadcasterOrgId,
            //       acceptedByOrgName: broadcasterOrgName,
            //     });
            //   }
            // } catch { /* best effort */ }
          } catch (err) {
            console.error('Error copying rapid upload track:', err);
          }
        }

        // Delete notification + mark lookup accepted
        try {
          await deleteDoc(doc(this.db, getOrgCollectionPath(broadcasterOrgId, 'deliveryNotifications'), notificationId));
        } catch { /* ignore */ }
        try {
          await updateDoc(lookupRef, { status: 'accepted', acceptedAt: now, acceptedBy: userId, acceptedByName: userName });
        } catch { /* ignore */ }
        return true;
      }

      // Auto-copy at delivery moved the audiometa + jobs work to
      // shareJobWithBroadcasters (see `copyDeliveryToBroadcaster`).
      // Accept is now purely an acknowledgment: clear the notification,
      // mark the source delivery accepted, ping the studio. If a race
      // means the copy hasn't happened yet (extremely unlikely — the
      // studio's client awaits the copy before setDoc'ing the
      // notification), calling copyDeliveryToBroadcaster here is
      // idempotent and would fill any gap.
      try {
        await this.copyDeliveryToBroadcaster(
          broadcasterOrgId,
          sourceOrgId,
          jobId,
          deliveryId,
          deliveryCode,
          deliveryData,
        );
      } catch (err) {
        console.error('[acceptDelivery] backup copy failed:', err);
      }

      // [DISABLED 2026-07-09] — public catalogue is now driven by the
      // per-track "Make Public" toggle in the library (agency + master
      // orgs only). Broadcaster acceptance no longer auto-publishes.
      // Kept commented for future revival — do not delete.
      // 4. Copy tracks from masterCatalogue to publicCatalogue (broadcaster accepted)
      // let broadcasterOrgNameForCatalogue = '';
      // try {
      //   const bOrgDoc = await getDoc(doc(this.db, 'organizations', broadcasterOrgId));
      //   broadcasterOrgNameForCatalogue = bOrgDoc.data()?.name || '';
      // } catch { /* ignore */ }

      // for (const tid of Array.from(trackIdsCopied)) {
      //   try {
      //     const masterRef = doc(this.db, MASTER_CATALOGUEPATH, tid);
      //     const masterSnap = await getDoc(masterRef);
      //     if (masterSnap.exists()) {
      //       const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, tid);
      //       await setDoc(pubRef, {
      //         ...masterSnap.data(),
      //         acceptedByBroadcaster: true,
      //         acceptedAt: now,
      //         acceptedByOrgId: broadcasterOrgId,
      //         acceptedByOrgName: broadcasterOrgNameForCatalogue,
      //       });
      //     }
      //   } catch { /* best effort */ }
      // }

      // 5. Delete the notification (renumbered from 4)
      const notifRef = doc(
        this.db,
        getOrgCollectionPath(broadcasterOrgId, 'deliveryNotifications'),
        notificationId
      );
      await deleteDoc(notifRef);

      // 5. Update source org's delivery record
      try {
        const deliveryRef = doc(
          this.db,
          getOrgCollectionPath(sourceOrgId, 'jobs'),
          jobId,
          'deliveries',
          deliveryId
        );
        await updateDoc(deliveryRef, {
          status: 'accepted',
          acceptedAt: now,
          acceptedBy: userId,
          acceptedByName: userName,
        });
      } catch { /* may not have permission */ }

      // 6. Update public lookup status
      try {
        await updateDoc(lookupRef, { status: 'accepted' });
      } catch { /* ignore */ }

      // 7. Notify studio of acceptance — written to the deliveryRejections
      // collection (shared with rejections) with `type: 'accepted'` so the
      // email trigger and the in-app modal can branch on it.
      try {
        let broadcasterOrgName = '';
        try {
          const orgDoc = await getDoc(doc(this.db, 'organizations', broadcasterOrgId));
          broadcasterOrgName = orgDoc.data()?.name || '';
        } catch { /* ignore */ }

        // Look up campaign so the acceptance email + in-app modal can show it.
        let campaign = '';
        try {
          const jobDoc = await getDoc(doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId));
          campaign = jobDoc.data()?.campaign || '';
        } catch { /* ignore */ }

        const studioNotifRef = doc(collection(
          this.db,
          getOrgCollectionPath(sourceOrgId, 'deliveryRejections')
        ));
        await setDoc(studioNotifRef, {
          id: studioNotifRef.id,
          type: 'accepted',
          jobId,
          deliveryId,
          deliveryCode,
          broadcasterOrgId,
          broadcasterOrgName,
          campaign,
          acceptedBy: userId,
          acceptedByName: userName,
          rejectedAt: now, // using same field for sorting consistency
          status: 'pending',
        });
      } catch { /* ignore — best effort */ }

      return true;
    } catch (error) {
      console.error('Error accepting delivery:', error);
      return false;
    }
  };

  /**
   * Reject a delivery — dismiss notification and notify studio
   */
  rejectDelivery = async (
    broadcasterOrgId: string,
    notificationId: string,
    sourceOrgId: string,
    jobId: string,
    deliveryId: string,
    deliveryCode: string,
    reason: string,
  ): Promise<boolean> => {
    try {
      const now = Date.now();
      const userId = this.auth.currentUser?.uid || '';
      const userName = this.auth.currentUser?.displayName || this.auth.currentUser?.email || '';

      // 1. Delete the notification
      const notifRef = doc(
        this.db,
        getOrgCollectionPath(broadcasterOrgId, 'deliveryNotifications'),
        notificationId
      );
      await deleteDoc(notifRef);

      // 2. Read source delivery record + capture station snapshot for
      //    the cleanup work below, then mark it rejected.
      let rejectedStationIds: string[] = [];
      let deliveryStationSnapshot: any[] = [];
      try {
        const deliveryRef = doc(
          this.db,
          getOrgCollectionPath(sourceOrgId, 'jobs'),
          jobId,
          'deliveries',
          deliveryId
        );
        // Read the delivery FIRST so we know which stations to revert.
        const deliverySnap = await getDoc(deliveryRef);
        if (deliverySnap.exists()) {
          const deliveryStations: any[] = deliverySnap.data()?.stations || [];
          deliveryStationSnapshot = deliveryStations.filter((s: any) => !s?.deleted);
          rejectedStationIds = deliveryStationSnapshot
            .map((s: any) => (s && typeof s.id === 'string') ? s.id : '')
            .filter(Boolean);
        }
        await updateDoc(deliveryRef, {
          status: 'rejected',
          rejectedAt: now,
          rejectedBy: userId,
          rejectedByName: userName,
          rejectionReason: reason,
        });
      } catch { /* may not have permission */ }

      // 2a. NEW (auto-copy model) — REJECT triggers cleanup of the
      //     broadcaster's copy. The delivery was auto-copied at
      //     shareJobWithBroadcasters time, so we now need to undo it:
      //       - Remove the rejected stations from the broadcaster's
      //         /jobs/{jobId} copy (delete the doc if it ends up empty).
      //       - For each linkedTrackId that no other job in this
      //         broadcaster's org still references, delete the
      //         audiometa doc. The existing deleteOrgTrackData CF
      //         cascades to Cloud Storage automatically.
      //     Legacy stations without ids can't be identified for
      //     removal — they stay in the broadcaster's copy (safe
      //     default; matches pre-auto-copy behaviour for legacy data).
      //     Best-effort: a cleanup failure logs a warning but does
      //     NOT abort the reject — the studio still gets the rejection
      //     notification and the source delivery still shows rejected.
      const removedTrackIds = new Set<string>();
      if (rejectedStationIds.length > 0) {
        try {
          const broadcasterJobRef = doc(
            this.db,
            getOrgCollectionPath(broadcasterOrgId, 'jobs'),
            jobId,
          );
          await runTransaction(this.db, async (tx) => {
            const snap = await tx.get(broadcasterJobRef);
            if (!snap.exists()) return; // never copied — nothing to remove
            const data = snap.data();
            const stations: any[] = Array.isArray(data.stations) ? data.stations : [];
            const idsToRemove = new Set(rejectedStationIds);
            const survivors: any[] = [];
            const removed: any[] = [];
            for (const s of stations) {
              if (s?.id && idsToRemove.has(s.id)) removed.push(s);
              else survivors.push(s);
            }
            if (removed.length === 0) return; // no matches
            for (const s of removed) {
              if (s.linkedTrackId) removedTrackIds.add(s.linkedTrackId);
            }
            if (survivors.length === 0) {
              tx.delete(broadcasterJobRef);
            } else {
              tx.set(broadcasterJobRef, {
                ...data,
                stations: survivors,
                stationCount: survivors.length,
                linkedCount: survivors.filter((s: any) => s.linkedTrackId).length,
              });
            }
          });

          // Audiometa dedup: for each removed trackId, scan every
          // /jobs in the broadcaster's org for surviving references.
          // If none, delete the audiometa doc.
          if (removedTrackIds.size > 0) {
            const jobsSnap = await getDocs(
              collection(this.db, getOrgCollectionPath(broadcasterOrgId, 'jobs')),
            );
            const stillReferenced = new Set<string>();
            jobsSnap.forEach((d: any) => {
              const jobData = d.data() || {};
              const jobStations: any[] = Array.isArray(jobData.stations) ? jobData.stations : [];
              for (const s of jobStations) {
                if (s?.linkedTrackId && removedTrackIds.has(s.linkedTrackId)) {
                  stillReferenced.add(s.linkedTrackId);
                }
              }
            });
            for (const trackId of Array.from(removedTrackIds)) {
              if (stillReferenced.has(trackId)) continue;
              try {
                await deleteDoc(doc(
                  this.db,
                  getOrgCollectionPath(broadcasterOrgId, AUDIOMETAPATH),
                  trackId,
                ));
              } catch (err) {
                console.warn(`[rejectDelivery] audiometa delete failed for ${trackId}:`, err);
              }
            }
          }
        } catch (err) {
          console.error('[rejectDelivery] broadcaster cleanup failed:', err);
        }
      }
      void deliveryStationSnapshot; // reserved for future audit surfaces

      // 2b. Revert source station status for the rejected stations —
      //     but only if NO OTHER active (non-rejected) delivery still
      //     covers the same station id. A station delivered to both
      //     the media house AND the broadcaster is in two delivery
      //     records; if just one gets rejected, the station is still
      //     "delivered" via the other. Only flip back to 'pending'
      //     when the rejection strips its last active delivery.
      if (rejectedStationIds.length > 0) {
        try {
          const jobRef = doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId);
          // Which station ids still have coverage from ANOTHER active
          // delivery? Read the deliveries subcollection excluding this
          // rejection.
          const deliveriesSnap = await getDocs(
            collection(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId, 'deliveries')
          );
          const stillCovered = new Set<string>();
          deliveriesSnap.forEach((d: any) => {
            if (d.id === deliveryId) return;
            const data = d.data() || {};
            if (data.status === 'rejected') return;
            for (const s of (data.stations || [])) {
              if (s?.id) stillCovered.add(s.id);
            }
          });
          const toRevert = new Set(rejectedStationIds.filter((id) => !stillCovered.has(id)));
          if (toRevert.size > 0) {
            const jobSnap = await getDoc(jobRef);
            if (jobSnap.exists()) {
              const stations: any[] = jobSnap.data()?.stations || [];
              const updated = stations.map((s: any) => {
                if (s?.id && toRevert.has(s.id)) {
                  return { ...s, deliveryStatus: 'pending' };
                }
                return s;
              });
              await updateDoc(jobRef, { stations: updated });
            }
          }
        } catch { /* best effort — rejection still succeeds */ }
      }

      // 3. Update public lookup status
      try {
        const lookupRef = doc(this.db, 'deliveryCodes', deliveryCode);
        await updateDoc(lookupRef, { status: 'rejected' });
      } catch { /* ignore */ }

      // 4. Notify studio of rejection
      try {
        // Get broadcaster org name
        let broadcasterOrgName = '';
        try {
          const orgDoc = await getDoc(doc(this.db, 'organizations', broadcasterOrgId));
          broadcasterOrgName = orgDoc.data()?.name || '';
        } catch { /* ignore */ }

        // Look up campaign name so the email + in-app modal can show it.
        let campaign = '';
        try {
          const jobDoc = await getDoc(doc(this.db, getOrgCollectionPath(sourceOrgId, 'jobs'), jobId));
          campaign = jobDoc.data()?.campaign || '';
        } catch { /* ignore */ }

        const studioNotifRef = doc(collection(
          this.db,
          getOrgCollectionPath(sourceOrgId, 'deliveryRejections')
        ));
        await setDoc(studioNotifRef, {
          id: studioNotifRef.id,
          jobId,
          deliveryId,
          deliveryCode,
          broadcasterOrgId,
          broadcasterOrgName,
          campaign,
          rejectedBy: userId,
          rejectedByName: userName,
          rejectionReason: reason,
          rejectedAt: now,
          status: 'pending', // pending | dismissed
        });
      } catch { /* ignore — best effort */ }

      return true;
    } catch (error) {
      console.error('Error rejecting delivery:', error);
      return false;
    }
  };

  /**
   * Get delivery records for a job (source org view)
   */
  getJobDeliveries = async (
    orgId: string,
    jobId: string,
  ): Promise<any[]> => {
    try {
      const deliveriesRef = collection(
        this.db,
        getOrgCollectionPath(orgId, 'jobs'),
        jobId,
        'deliveries'
      );
      const snapshot = await getDocs(deliveriesRef);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error getting job deliveries:', error);
      return [];
    }
  };

  /**
   * Claim a delivery — copy tracks and job from a delivery link into
   * the current user's org. Used when a broadcaster visits a delivery link
   * and their org wasn't the original target (e.g. new org, different ID).
   */
  claimDelivery = async (
    claimerOrgId: string,
    deliveryData: any,
  ): Promise<boolean> => {
    try {
      const { orgId: sourceOrgId, jobId, deliveryId, deliveryCode, stations } = deliveryData;

      // Copy each linked track to claimer's audiometa
      const trackIdsCopied = new Set<string>();
      for (const station of stations || []) {
        if (station.linkedTrackId && !trackIdsCopied.has(station.linkedTrackId)) {
          try {
            const sourceTrackRef = doc(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), station.linkedTrackId);
            const sourceTrackDoc = await getDoc(sourceTrackRef);
            if (!sourceTrackDoc.exists()) continue;

            const sourceTrack = sourceTrackDoc.data();
            const claimerTrackRef = doc(
              this.db,
              getOrgCollectionPath(claimerOrgId, AUDIOMETAPATH),
              station.linkedTrackId
            );

            await setDoc(claimerTrackRef, {
              trackName: sourceTrack.trackName || station.trackName || '',
              tags: sourceTrack.tags || [],
              language: sourceTrack.language || [],
              client: sourceTrack.client || deliveryData.client || null,
              agency: sourceTrack.agency || null,
              mp3Files: sourceTrack.mp3Files || station.mp3Files || [],
              wavFiles: sourceTrack.wavFiles || station.wavFiles || [],
              defaultMp3Id: sourceTrack.defaultMp3Id || station.defaultMp3Id || null,
              approval_status: sourceTrack.approval_status || '',
              qc_status: sourceTrack.qc_status || '',
              is_broadcast_master: sourceTrack.is_broadcast_master || false,
              broadcast_master_file: sourceTrack.broadcast_master_file || null,
              // Job back-reference (same fields auto-match writes)
              jobId: jobId,
              jobCampaign: deliveryData.campaign || '',
              jobTitle: deliveryData.campaign || '',
              // Delivery reference fields
              isDeliveryTrack: true,
              sourceOrgId,
              sourceTrackId: station.linkedTrackId,
              sourceOrgName: deliveryData.sourceOrgName || '',
              deliveryCode,
              deliveryId,
              sourceJobId: jobId,
              createdAt: Date.now(),
              deliveredAt: Date.now(),
            });
            trackIdsCopied.add(station.linkedTrackId);
          } catch (err) {
            console.error(`Error copying track ${station.linkedTrackId}:`, err);
          }
        }
      }

      // Create job in claimer's org
      const claimerJobRef = doc(
        this.db,
        getOrgCollectionPath(claimerOrgId, 'jobs'),
        jobId
      );

      const claimerStations = (stations || []).map((s: any) => ({
        ...s,
        deliveryStatus: 'delivered',
      }));

      await setDoc(claimerJobRef, {
        campaign: deliveryData.campaign || '',
        client: deliveryData.client || '',
        period: deliveryData.period || '',
        stations: claimerStations,
        stationCount: claimerStations.length,
        linkedCount: claimerStations.filter((s: any) => s.linkedTrackId).length,
        isDeliveredJob: true,
        sourceOrgId,
        sourceOrgName: deliveryData.sourceOrgName || '',
        sourceJobId: jobId,
        deliveryCode,
        deliveryId,
        uploadedAt: Date.now(),
        deliveredAt: Date.now(),
        claimedAt: Date.now(),
        claimedBy: this.auth.currentUser?.uid || '',
        status: 'accepted',
      });

      // [DISABLED 2026-07-09] — public catalogue is now driven by the
      // per-track "Make Public" toggle in the library (agency + master
      // orgs only). Broadcaster claim no longer auto-publishes.
      // Kept commented for future revival — do not delete.
      // Copy tracks from masterCatalogue to publicCatalogue (broadcaster accepted via claim)
      // for (const tid of Array.from(trackIdsCopied)) {
      //   try {
      //     const masterRef = doc(this.db, MASTER_CATALOGUEPATH, tid);
      //     const masterSnap = await getDoc(masterRef);
      //     if (masterSnap.exists()) {
      //       const pubRef = doc(this.db, PUBLIC_CATALOGUEPATH, tid);
      //       await setDoc(pubRef, {
      //         ...masterSnap.data(),
      //         acceptedByBroadcaster: true,
      //         acceptedAt: Date.now(),
      //         acceptedByOrgId: claimerOrgId,
      //       });
      //     }
      //   } catch { /* best effort */ }
      // }

      // Update the delivery lookup to record the claim
      try {
        const lookupRef = doc(this.db, 'deliveryCodes', deliveryCode);
        await updateDoc(lookupRef, {
          claimedByOrgId: claimerOrgId,
          claimedAt: Date.now(),
        });
      } catch { /* ignore */ }

      // Update source delivery record if accessible
      try {
        const deliveryRef = doc(
          this.db,
          getOrgCollectionPath(sourceOrgId, 'jobs'),
          jobId,
          'deliveries',
          deliveryId
        );
        await updateDoc(deliveryRef, {
          claimedByOrgId: claimerOrgId,
          claimedByOrgName: this.auth.currentUser?.displayName || '',
          claimedAt: Date.now(),
        });
      } catch { /* ignore */ }

      return true;
    } catch (error) {
      console.error('Error claiming delivery:', error);
      return false;
    }
  };

  // =====================================================
  // DOWNLOAD PRESETS
  // =====================================================

  getDownloadPresets = async (orgId: string): Promise<any[]> => {
    try {
      const presetsRef = collection(this.db, getOrgCollectionPath(orgId, 'downloadPresets'));
      const snapshot = await getDocs(presetsRef);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error loading download presets:', error);
      return [];
    }
  };

  saveDownloadPreset = async (
    orgId: string,
    preset: { name: string; prefix: string; suffix: string; format: string; bitrate: string; sampleRate: string; bitDepth: string; channels: string },
    presetId?: string,
  ): Promise<string> => {
    if (presetId) {
      const presetRef = doc(this.db, getOrgCollectionPath(orgId, 'downloadPresets'), presetId);
      await updateDoc(presetRef, { ...preset, updatedAt: Date.now() });
      return presetId;
    }
    const presetsRef = collection(this.db, getOrgCollectionPath(orgId, 'downloadPresets'));
    const newRef = await addDoc(presetsRef, {
      ...preset,
      createdAt: Date.now(),
      createdBy: this.auth.currentUser?.uid || '',
    });
    return newRef.id;
  };

  deleteDownloadPreset = async (orgId: string, presetId: string): Promise<boolean> => {
    try {
      const presetRef = doc(this.db, getOrgCollectionPath(orgId, 'downloadPresets'), presetId);
      await deleteDoc(presetRef);
      return true;
    } catch (error) {
      console.error('Error deleting download preset:', error);
      return false;
    }
  };
}

// Create singleton instance and expose on window for console access (dev/admin use)
const firebaseInstance = new Firebase();
if (typeof window !== 'undefined') {
  (window as any).firebaseAdmin = firebaseInstance;
}

export default Firebase;
